import { TechnicianTimeReport } from '../components/TechnicianTimeReport';
import {FieldMembershipServiceCompletion} from '../components/FieldMembershipServiceCompletion';
import { FieldTags, type FieldTagGroups } from "../components/FieldTags";
import { EquipmentInstallDateField } from '../components/EquipmentInstallDateField';
import { equipmentInstallDateLabel } from '../../../../shared/equipment-install-date';
import { EquipmentTypePicker } from '../components/EquipmentTypePicker';
import { FieldMembershipBuilder } from '../components/FieldMembershipBuilder';
import {fieldViewKey, readFieldView, writeFieldView, useFieldPanel} from "../lib/field-view-state";
import { FieldJobTime } from "../components/FieldJobTime";
import { timesheetWorkedMilliseconds, timesheetIsOnLunch } from '../../../../shared/timesheet-work-time';
import { FieldEstimateDeposit } from "../components/FieldEstimateDeposit";
import { FieldAppInstallDialog, useFieldAppInstall } from "../components/FieldAppInstall";
import { InvoicePaymentBadge } from "../components/InvoicePaymentBadge";
import { commercialLinePresentation } from "../../../../shared/commercial-line-presentation";
import { DictationButton } from "../components/DictationButton";
import type { PurchaseMaterial } from "../components/PurchaseOrderItemPicker";
import type { VendorCosts } from "../lib/vendor-costs";
import { formatPhoneNumber as fieldPhoneLabel } from "../lib/phone-format";
import { FieldMaintenancePlans, type FieldEquipmentPlans } from '../components/FieldMaintenancePlans';
import { FieldWorkflowProvider, useFieldWorkflow } from '../lib/field-workflow-capabilities';
import { estimateDescription } from "../lib/estimate-description";
import { PriceBookThumb } from "../components/PriceBookThumb";
import { EstimateDescription } from "../components/EstimateOptionItems";
import { Phone, MessageSquare, Mail, X, ArrowUp, ArrowDown, Wrench, Package, Boxes, BadgePercent, Search, ChevronDown, CheckCircle2, AlertCircle, Info, Plus, Copy, LayoutTemplate, FilePlus2 } from "lucide-react";
import { EstimatePriceInput } from "../components/EstimatePriceInput";
import { moneyInputCents } from "../lib/money-input";
import { usePhotoImage } from "../lib/use-photo-image";
import { groupFieldInvoiceLines } from "../lib/invoice-line-groups";
import { isCustomerVisibleInvoiceLine } from "../lib/invoice-line-visibility";
import { FieldInvoiceLine, type PendingInvoiceEdit } from "../components/FieldInvoiceLine";
import { invoiceNeedsSummary, serviceCallSummaryRequired } from "../lib/service-call-summary";
import { FieldInvoiceNotes } from "../components/FieldInvoiceNotes";
import { FieldPricebookPicker } from "../components/FieldPricebookPicker";
import { PurchaseOrderItemPicker } from "../components/PurchaseOrderItemPicker";
import { PurchaseOrderInvoiceItems } from "../components/PurchaseOrderInvoiceItems";
import { FieldVendorSearch } from "../components/FieldVendorSearch";
import { FieldPurchaseOrderContents } from "../components/FieldPurchaseOrderContents";
import { InstallScheduler } from "../components/InstallScheduler";
import { Dialog } from "../components/controls";
import "./technician-page.css";
import { fieldDate, fieldDayLabel, shiftFieldDay } from "../lib/field-schedule";
import { FieldLocationHistory } from "../components/FieldLocationHistory";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type FormEvent,
  type PointerEvent,
} from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, API_URL, IS_PREVIEW_ENVIRONMENT } from "../lib/api-client";
import { authClient } from "../lib/auth-client";
import { FIELD_SIGN_IN_PATH } from "../lib/field-app";
import { estimatePresentation, invoicePresentation, jobPresentation } from "../lib/commercial-status";
import { prepareFieldPhoto } from "../lib/field-photos";
import { EstimateTemplatePicker, type EstimateTemplateChoice } from "../components/EstimateTemplatePicker";
import { EstimatePackageReview, type TemplatePreview } from "../components/EstimatePackageReview";
import {
  FIELD_PULL_THRESHOLD_PX,
  useFieldPullToRefresh,
  useOnFieldRefresh,
} from "../lib/use-field-pull-to-refresh";

function fieldJobStage(status: string) {
  if (["completed", "cancelled", "canceled", "no_show"].includes(status)) return "complete";
  if (["arrived", "in_progress"].includes(status)) return "working";
  if (["dispatched", "en_route"].includes(status)) return "dispatched";
  return "scheduled";
}



type Access = {
  role: "owner" | "admin" | "dispatcher" | "technician";
  workView: string;
  dataScope: "company" | "assigned";
  permissions: string[];
  companyName?: string | null;
  companyLogoUrl?: string | null;
};
type Technician = {
  id: string;
  name: string;
  active: boolean;
  dispatchable: boolean;
};
type JobPhoto = {
  createdAt?: string | null;
  caption?: string | null;
  id: string;
  fileName: string | null;
  contentUrl: string | null;
  availability?: "available" | "not_copied";
};
type WorkItem = FieldTagGroups & {
  appointmentId: string;
  scheduledStart: string;
  scheduledEnd: string;
  appointmentStatus: string;
  customerConfirmedAt?: string | null;
  crewCount?: number;
  openCrewmateCount?: number;
  dispatchedAt?: string | null;
  arrivedAt?: string | null;
  jobId: string | null;
  jobNumber: string | number | null;
  ownerType?: 'job' | 'estimate' | 'membership';
  ownerId?: string;
  jobStatus: string | null;
  jobTypeName?: string | null;
  serviceTeamName?: string | null;
  description: string | null;
  priority: string | null;
  customerId: string;
  customerName: string;
  customerPhone?: string | null;
  customerEmail?: string | null;
  propertyId: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  postalCode: string | null;
  specialInstructions?: string | null;
};
type EquipmentEntry = { equipmentId?: string; name?: string | null; installDate?: string | null; serialNumber: string; modelNumber: string; manufacturer: string; manufactureYear: number | null; equipmentTypeId: string | null; otherEquipmentType: string | null; condition: string };
type JobEquipment = {
  lastServicedAt?: string | null;
  id: string;
  propertyId?: string;
  name: string | null;
  manufacturer: string | null;
  modelNumber: string | null;
  serialNumber: string | null;
  manufactureYear: number | null;
	  equipmentTypeId?: string | null;
	  equipmentTypeName: string | null;
	  otherEquipmentType?: string | null;
  condition: string | null;
  status: string;
  installDate: string | null;
  updatedAt?: string;
};
type ProfileEquipment = JobEquipment;
type Invoice = {
  id: string;
  invoiceNumber: number;
  summary?: string | null;
  displayNumber?: string;
  status: string;
  paidCents?: number;
  payments?: Array<{id:string;amountCents:number;method:string;reference:string|null;paidAt:string}>;
  hasInvoiceSignature?: boolean;
  summaryRequired?: boolean | number;
  fulfillment?: string | null;
  estimateDisplayNumber?: string | null;
  jobStatus?: string | null;
  taxCents?: number;
  subtotalCents?: number;
  totalCents: number;
  balanceDueCents: number;
  signerName: string | null;
  signedAt: string | null;
  terms: string | null;
  items: Array<{
    id: string;
    description: string;
    quantity: number;
    unitPriceCents: number;
    groupName?: string | null;
    itemName?: string | null;
    itemDescription?: string | null;
    generatedSource?: string | null;
    taxClass?: string | null;
    materialMarkupBps?: number | null;
    sourceItemType?: string | null;
    sourceIsChargeable?: number | null;
    customerVisible?: number | boolean | null;
    hiddenReason?: string | null;
  }>;
};
type Detail = {
  continuation?: { appointments: Array<{id:string;scheduledStart:string;scheduledEnd:string;status:string}>; hasMoreAppointments:boolean; hasOtherRecordedWork?:boolean; waitingOn:string|null; truncated?:boolean };
  canCompleteEstimateVisit?: boolean;
  job: WorkItem & { status?: string; completedAt: string | null };
  invoices: Invoice[];
  notes: Array<{ id: string; body: string; createdAt: string }>;
  timesheets: Array<{ id: string; appointmentId?: string | null; clockIn: string; clockOut: string | null; lunchBreaksJson?: string | null }>;
  closeout: {
    paymentDisposition: "paid" | "due_later";
    completedAt: string;
  } | null;
  equipment: JobEquipment[];
	  equipmentTypes: Array<{ id: string; name: string }>;
  purchaseOrders?: Array<{
    id: string;
    displayNumber: string | null;
    status: string;
    amountCents: number;
    vendorName: string;
    canEdit?: boolean; vendorMemo?: string | null; createdBy?: string | null; orderType?: string | null; createdAt?: string; requiredByDate?: string | null; sourceImported?: number;
  }>;
};
type ApiPayload<T> = { data: T };
type FieldTimeEntry = {
  id: string;
  entryType: "attendance" | "job" | "non_job";
  kind: string;
  title: string;
  jobId: string | null;
  scheduleBlockId: string | null;
  payClass: "paid" | "unpaid" | "pending";
  startedAt: string;
  endedAt: string | null;
  localDate: string;
};
type LearningLesson = {
  id: string;
  title: string;
  topic: string;
  issueSummary: string;
  keyTakeaway: string;
};
type ExtraLineItem = {
  description: string;
  quantity: number;
  unitPriceCents: number;
};

type FinishPayload = {
  laborHours: number;
  notes: string | null;
  signerName?: string;
  signatureDataUrl?: string;
  extraLineItems: ExtraLineItem[];
};

type FieldScheduleEvent = { id: string; title: string; kind: string; startsAt: string; endsAt: string; allDay?: boolean; paid?: boolean };
async function readWork(path: string) {
  const response = await apiFetch(path);
  if (!response.ok) throw new Error();
  const payload = ((await response.json()) as ApiPayload<{ items: WorkItem[]; events?: FieldScheduleEvent[]; timezone?: string }>).data;
  const items = payload.items.filter((item) =>
    ![item.jobStatus, item.appointmentStatus].some((status) => status === 'cancelled' || status === 'canceled'),
  );
  return { items, events: payload.events ?? [], timezone: payload.timezone };
}

const time = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
const dateLabel = (value: Date | string = new Date()) =>
  new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(new Date(value));
const money = (value: number) =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
  }).format(value / 100);
const statusWords = (value: string) => value.replaceAll("_", " ");
const appointmentStatusLabel = (status: string, confirmedAt?: string | null) =>
  ["pending", "scheduled", "confirmed"].includes(status)
    ? confirmedAt || status === "confirmed" ? "Confirmed" : "Scheduled"
    : statusWords(status);
const jobCode = (value: string | number) => `J-${value}`;

// UI-only lifecycle state. See docs/technician-ui-handoff.md before wiring writes.
type JobProgress = { source?: "recorded"; status: string; dispatchedAt?: number; arrivedAt?: number; startedAt?: number; finishedAt?: number; totalMs?: number; recordedMs?: number; runningSince?: number; customerName?: string; jobNumber?: string | number | null };
const jobDuration = (entry: JobProgress, now: number) => entry.totalMs ?? (
  entry.recordedMs !== undefined
    ? entry.recordedMs + (entry.runningSince !== undefined ? Math.max(0, now - entry.runningSince) : 0)
    : Math.max(0, now - (entry.startedAt ?? now))
);
const durationLabel = (milliseconds: number) => {
  const seconds = Math.floor(Math.max(0, milliseconds) / 1000);
  return [Math.floor(seconds / 3600), Math.floor(seconds / 60) % 60, seconds % 60]
    .map((value) => String(value).padStart(2, "0")).join(":");
};
function useTimerNow() {
  const [now, setNow] = useState(Date.now);
  useEffect(() => { const timer = window.setInterval(() => setNow(Date.now()), 1000); return () => window.clearInterval(timer); }, []);
  return now;
}
function recordedDuration(timesheets: Detail["timesheets"], now: number) {
  return timesheets.reduce((total, entry) => total + timesheetWorkedMilliseconds(entry, now), 0);
}

const card = "rounded-[22px] bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.06)]";
const well = "rounded-2xl bg-[#f3f3f3] px-3.5 py-3";
const iconBtn =
  "grid h-11 w-11 place-items-center rounded-full border border-[#ececec] bg-white text-black";

// Deliberately contains no account, technician, job, or customer identifiers.
// These marks make cold Field startup measurable in browser performance traces
// without adding an extra API request (and therefore no extra D1 work).
function recordFieldBootPhase(phase: string, startedAt: number) {
  const durationMs = Math.round(performance.now() - startedAt);
  performance.mark(`field_boot:${phase}`);
  console.info("field_boot_phase", { phase, durationMs });
}

export default function TechnicianPage() { const {data:session}=authClient.useSession(); return <FieldWorkflowProvider key={session?.user.id ?? "anonymous"}><TechnicianPageContent /></FieldWorkflowProvider>; }

function TechnicianPageContent() {
  const features = useFieldWorkflow();
  const navigate = useNavigate();
  const { data: session, isPending } = authClient.useSession();
  const [initialView] = useState(() => readFieldView(session?.user.id));
  const explicitTechnician = new URLSearchParams(window.location.search).get('technicianId');
  const restoreSelection = !new URLSearchParams(window.location.search).has('jobId') && !window.sessionStorage.getItem('field-checkout-job') && (explicitTechnician === null || explicitTechnician === initialView?.viewedTech);
  const [access, setAccess] = useState<Access | null>(null);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [viewedTech, setViewedTech] = useState(() => explicitTechnician ?? initialView?.viewedTech ?? "");
  const [timezone, setTimezone] = useState<string | undefined>();
  const [hasActiveJobData, setHasActiveJobData] = useState(false);
  const [openJobTimers, setOpenJobTimers] = useState<Array<{ jobId: string; jobNumber: string; title: string }>>([]);
  const [events, setEvents] = useState<FieldScheduleEvent[]>([]);
  const [homeView, setHomeView] = useState<"home" | "timesheet">(restoreSelection ? initialView?.homeView ?? "home" : "home");
  const [jobs, setJobs] = useState<WorkItem[]>([]);
  const [lessons, setLessons] = useState<LearningLesson[]>([]);
  const [selectedId, setSelectedId] = useState(restoreSelection ? initialView?.selectedId ?? "" : "");
  const returnJobId = useRef(new URLSearchParams(window.location.search).get("jobId") ?? window.sessionStorage.getItem("field-checkout-job"));
  const [timesheetDay, setTimesheetDay] = useState(initialView?.timesheetDay ?? "");
  const [scheduleDay, setScheduleDay] = useState(restoreSelection ? initialView?.scheduleDay ?? "" : "");
  const [progress, setProgress] = useState<Record<string, JobProgress>>({});
  const [timeEntries, setTimeEntries] = useState<FieldTimeEntry[]>([]);
  const [timeBusy, setTimeBusy] = useState("");
  const now = useTimerNow();
  const techKey = viewedTech || session?.user.id || "";
  const progressKey = (appointmentId: string) => `${techKey}:${appointmentId}`;
  const technicianName = (viewedTech ? technicians.find((tech) => tech.id === viewedTech)?.name : session?.user.name)?.split(" ")[0] || "there";
  const todayShifts = timeEntries.filter((entry) => entry.entryType === "attendance").map((entry) => ({
    id: entry.id,
    start: Date.parse(entry.startedAt),
    end: entry.endedAt ? Date.parse(entry.endedAt) : undefined,
  }));
  const clockedIn = todayShifts.some((shift) => shift.end === undefined);
  const timeDates = [...new Set([new Date(now).toLocaleDateString(), ...Object.entries(progress).filter(([key, entry]) => key.startsWith(`${techKey}:`) && entry.startedAt !== undefined).map(([, entry]) => new Date(entry.startedAt!).toLocaleDateString()), ...timeEntries.map((entry) => new Date(entry.startedAt).toLocaleDateString())])];
  const displayedTimeDay = timeDates.includes(timesheetDay) ? timesheetDay : new Date(now).toLocaleDateString();
  const displayedShifts = todayShifts.filter(shift => new Date(shift.start).toLocaleDateString() === displayedTimeDay);
  const todayJobs = Object.entries(progress).filter(([key, entry]) => key.startsWith(`${techKey}:`) && entry.startedAt && new Date(entry.startedAt).toLocaleDateString() === displayedTimeDay);
  const todayBlocks = timeEntries.filter((entry) => entry.entryType === "non_job" && new Date(entry.startedAt).toLocaleDateString() === displayedTimeDay).map((entry) => ({
    id: entry.id,
    eventId: entry.scheduleBlockId ?? `unscheduled:${entry.kind}`,
    title: entry.title,
    start: Date.parse(entry.startedAt),
    end: entry.endedAt ? Date.parse(entry.endedAt) : undefined,
    payClass: entry.payClass,
  }));
  const activeBlockEntry = timeEntries.find((entry) => entry.entryType === "non_job" && !entry.endedAt);
  const activeBlock = activeBlockEntry ? { id: activeBlockEntry.id, eventId: activeBlockEntry.scheduleBlockId ?? `unscheduled:${activeBlockEntry.kind}`, title: activeBlockEntry.title, start: Date.parse(activeBlockEntry.startedAt) } : undefined;
  const runningProgress = Object.entries(progress).filter(([key, entry]) => key.startsWith(`${techKey}:`) && !entry.finishedAt && (entry.runningSince !== undefined || (entry.source !== "recorded" && entry.startedAt !== undefined)));
  const activeJob = openJobTimers.length > 0 || (!hasActiveJobData && runningProgress.length > 0);

  const activityRows = [
    ...todayJobs.map(([key, entry]) => ({ key, title: entry.customerName ?? jobs.find((job) => progressKey(job.appointmentId) === key)?.customerName ?? "Job", kind: `Job ${jobCode(entry.jobNumber ?? jobs.find((job) => progressKey(job.appointmentId) === key)?.jobNumber ?? "")}`, dispatchedAt: entry.dispatchedAt, arrivedAt: entry.arrivedAt, isJob: true, start: entry.startedAt!, end: entry.finishedAt, duration: jobDuration(entry, now) })),
    ...todayBlocks.map((entry) => ({ key: `${entry.eventId}:${entry.start}`, title: entry.title, kind: entry.payClass === "pending" ? "Pay classification pending" : entry.payClass === "unpaid" ? "Unpaid" : "Non-job", dispatchedAt: undefined, arrivedAt: undefined, isJob: false, start: entry.start, end: entry.end, duration: Math.max(0, (entry.end ?? now) - entry.start) })),
  ].sort((a, b) => a.start - b.start);
  const [refreshToken, setRefreshToken] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const keepOwnAssignments = useRef(Boolean(initialView && !initialView.viewedTech));
  const loadGeneration = useRef(0);
  const workLoadInFlight = useRef(false);
  const techniciansLoaded = useRef(false);
  const techniciansState = useRef<Technician[]>([]);
  const techniciansLoadInFlight = useRef<Promise<Technician[]> | null>(null);
  const fieldBootStartedAt = useRef<number | null>(null);
  const fieldBootRendered = useRef(false);
  const fieldExtrasStarted = useRef(false);
  useEffect(() => {
    writeFieldView(session?.user.id,{viewedTech,selectedId,scheduleDay:scheduleDay || fieldDate(Date.now(),timezone),timesheetDay,homeView});
  },[session?.user.id,viewedTech,selectedId,scheduleDay,timesheetDay,homeView,timezone]);


  useEffect(() => {
    if (!isPending && !session?.user) navigate(FIELD_SIGN_IN_PATH, { replace: true });
  }, [isPending, navigate, session?.user]);

  const loadTechnicians = useCallback(async () => {
    if (techniciansLoaded.current) return techniciansState.current;
    if (!techniciansLoadInFlight.current) {
      techniciansLoadInFlight.current = apiFetch("/api/v1/workspace/dispatch/technicians")
        .then(async (response) => {
          if (!response.ok) return [];
          return ((await response.json()) as ApiPayload<{ items: Technician[] }>).data.items
            .filter((item) => item.active && item.dispatchable);
        })
        .then((items) => {
          techniciansLoaded.current = true;
          techniciansState.current = items;
          setTechnicians(items);
          return items;
        })
        .finally(() => {
          techniciansLoadInFlight.current = null;
        });
    }
    return techniciansLoadInFlight.current;
  }, []);

  const loadWork = useCallback(async (options?: { quiet?: boolean }) => {
    if (!session?.user) return;
    workLoadInFlight.current = true;
    const generation = ++loadGeneration.current;
    const isStale = () => generation !== loadGeneration.current;
    try {
      if (!options?.quiet) {
        setLoading(true);
        setError("");
        fieldBootStartedAt.current = performance.now();
      }
      const me = await apiFetch("/api/v1/me");
      if (!me.ok) throw new Error();
      const applicationUser = (
        (await me.json()) as ApiPayload<{ applicationUser: Access | null }>
      ).data.applicationUser;
      if (!applicationUser) {
        if (!isStale())
          setError(
            "Your account is not assigned to a WorkView tenant yet. Ask an owner to add you, then refresh this page.",
          );
        return;
      }
      if (!options?.quiet && fieldBootStartedAt.current !== null) {
        recordFieldBootPhase("identity_ready", fieldBootStartedAt.current);
      }
      const workParams = new URLSearchParams();
      if (viewedTech) workParams.set("technicianId", viewedTech);
      if (scheduleDay) workParams.set("date", scheduleDay);
      const workPath = `/api/v1/workspace/my-work${workParams.size ? `?${workParams}` : ""}`;
      // The schedule and time controls are the critical Field path. The roster,
      // Learning, and Company logo remain available, but must not hold the first
      // render hostage or add a concurrent D1 spike during boot.
      const work = await readWork(workPath);
      if (!options?.quiet && fieldBootStartedAt.current !== null) {
        recordFieldBootPhase("schedule_ready", fieldBootStartedAt.current);
      }
      let assigned = work.items;
      let assignedEvents = work.events;
      let previewTechnicianId = viewedTech;
      const fieldTimeQuery = previewTechnicianId
        ? `?technicianId=${encodeURIComponent(previewTechnicianId)}`
        : "";
      const fieldTimeResponse = await apiFetch(
        `/api/v1/workspace/field/time${fieldTimeQuery}`,
      );
      if (options?.quiet && !fieldTimeResponse.ok) throw new Error("Unable to refresh active time");
      let fieldTimeData = fieldTimeResponse.ok
        ? ((await fieldTimeResponse.json()) as ApiPayload<{ entries: FieldTimeEntry[]; activeJobs?: Array<{ jobId: string; jobNumber: string; title: string }> }>).data
        : null;
      if (!options?.quiet && fieldBootStartedAt.current !== null) {
        recordFieldBootPhase("time_ready", fieldBootStartedAt.current);
      }
      // The schedule and timer endpoints are separate snapshots. If they cross
      // during an update, retry them together once before telling a technician
      // to contact dispatch about a timer that may already be on their schedule.
      if (fieldTimeData?.activeJobs?.some((timer) => !assigned.some((item) => item.jobId === timer.jobId))) {
        try {
          const [refreshedWork, refreshedTime] = await Promise.all([
            readWork(workPath),
            apiFetch(`/api/v1/workspace/field/time${fieldTimeQuery}`),
          ]);
          if (refreshedTime.ok) {
            assigned = refreshedWork.items;
            assignedEvents = refreshedWork.events;
            fieldTimeData = ((await refreshedTime.json()) as ApiPayload<{ entries: FieldTimeEntry[]; activeJobs?: Array<{ jobId: string; jobNumber: string; title: string }> }>).data;
          }
        } catch {
          // Keep the first successful snapshots if the reconciliation retry fails.
        }
      }
      const persistedTimeEntries = fieldTimeData?.entries ?? [];
      // Publish the schedule before loading historical job details.
      if (isStale()) return;
      if (previewTechnicianId !== viewedTech) {
        setViewedTech(previewTechnicianId);
        return;
      }
      setAccess(applicationUser);
      setJobs(assigned);
      setEvents(assignedEvents);
      setTimezone(work.timezone);
      setTimeEntries(persistedTimeEntries);
      setOpenJobTimers(fieldTimeData?.activeJobs ?? []);
      setHasActiveJobData(Array.isArray(fieldTimeData?.activeJobs));
      setLoading(false);
      const recorded: Record<string, JobProgress> = {};
      const timedJobs = assigned.filter((item) => item.jobId && ['completed', 'arrived', 'en_route'].includes(item.appointmentStatus));
      for (let offset = 0; offset < timedJobs.length; offset += 6) {
        if (isStale()) return;
        await Promise.all(timedJobs.slice(offset, offset + 6).map(async (item) => {
          try {
            const query = `?appointmentId=${encodeURIComponent(item.appointmentId)}${previewTechnicianId ? `&technicianId=${encodeURIComponent(previewTechnicianId)}` : ''}`;
            const response = await apiFetch(`/api/v1/workspace/field/jobs/${encodeURIComponent(item.jobId!)}${query}`);
            if (!response.ok) return;
            const data = (await response.json()).data as Detail;
            data.timesheets = data.timesheets.filter(entry => entry.appointmentId === item.appointmentId);
            const starts = data.timesheets.map((entry) => Date.parse(entry.clockIn)).filter(Number.isFinite);
            if (!starts.length) return;
            const ends = data.timesheets.flatMap((entry) => entry.clockOut ? [Date.parse(entry.clockOut)] : []).filter(Number.isFinite);
            const completion = item.appointmentStatus === 'completed' ? (ends.length ? new Date(Math.max(...ends)).toISOString() : data.job.status === 'completed' ? data.job.completedAt : undefined) : undefined;
            const finishedAt = completion ? Date.parse(completion) : item.appointmentStatus === 'completed' && ends.length ? Math.max(...ends) : undefined;
            const running = Array.isArray(fieldTimeData?.activeJobs) && !fieldTimeData.activeJobs.some((timer) => timer.jobId === item.jobId) ? undefined : data.timesheets.find((entry) => !entry.clockOut);
            recorded[`${previewTechnicianId || session.user.id}:${item.appointmentId}`] = {
              source: "recorded", status: item.appointmentStatus, customerName: item.customerName, jobNumber: item.jobNumber,
              startedAt: Math.min(...starts), finishedAt,
              dispatchedAt: item.dispatchedAt ? Date.parse(item.dispatchedAt) : undefined,
              arrivedAt: item.arrivedAt ? Date.parse(item.arrivedAt) : undefined,
              recordedMs: recordedDuration(data.timesheets, Date.now()),
              runningSince: running && !timesheetIsOnLunch(running) ? Date.now() : undefined,
              ...(finishedAt !== undefined ? { totalMs: recordedDuration(data.timesheets, finishedAt) } : {}),
            };
          } catch { /* Missing time is left uncounted rather than inferred from the appointment window. */ }
        }));
      }
      if (!isStale()) {
        setProgress((current) => ({ ...current, ...recorded, ...Object.fromEntries(Object.entries(current).filter(([, entry]) => entry.source !== "recorded")) }));
        const returnedAppointment = returnJobId.current ? assigned.find((item) => item.jobId === returnJobId.current) : undefined;
        setSelectedId((current) => assigned.some((item) => item.appointmentId === current)
          ? current : returnedAppointment?.appointmentId ?? "");
        if (returnedAppointment) window.sessionStorage.removeItem("field-checkout-job");
        returnJobId.current = null;
      }
    } catch {
      if (!isStale() && !options?.quiet)
        setError(
          "We couldn’t load your assigned jobs. Pull to refresh and try again.",
        );
    } finally {
      if (!isStale()) {
        setLoading(false);
        workLoadInFlight.current = false;
      }
    }
  }, [session?.user, viewedTech, scheduleDay]);

  useEffect(() => {
    if (access && !loading && !fieldBootRendered.current && fieldBootStartedAt.current !== null) {
      fieldBootRendered.current = true;
      recordFieldBootPhase("first_render_ready", fieldBootStartedAt.current);
    }
  }, [access, loading]);

  useEffect(() => {
    if (!access || loading || fieldExtrasStarted.current) return;
    fieldExtrasStarted.current = true;
    // Run only after React has committed the usable Field screen. These panels
    // remain available but cannot delay dispatch, arrive, schedule, or time UI.
    void (async () => {
      // Keep optional reads serial so many simultaneous Field launches do not
      // recreate the D1 request fan-out after the first paint.
      const branding = access.permissions.includes("company:read")
        ? await apiFetch("/api/v1/workspace/company")
        : null;
      if (branding?.ok) {
        const company = (await branding.json()).data?.company;
        if (company?.logoAttachmentId && company.logoAvailability !== "not_copied") {
          setAccess((current) => current ? {
            ...current,
            companyLogoUrl: `${API_URL}/api/v1/workspace/attachments/${encodeURIComponent(company.logoAttachmentId)}/content`,
          } : current);
        }
      }
      const learning = access.permissions.includes("learning:read")
        ? await apiFetch("/api/v1/learning")
        : null;
      if (learning?.ok) {
        setLessons(((await learning.json()) as ApiPayload<{ items: LearningLesson[] }>).data.items);
      }
    })().catch(() => {
      // Optional panels never block or fail the critical Field workflow.
    });
    if ((access.role === "owner" || access.role === "admin") && !jobs.length && !viewedTech && !scheduleDay && !keepOwnAssignments.current) {
      void loadTechnicians().then(async (techs) => {
        // Preserve the existing owner fallback, but defer it until after the
        // critical view has rendered.
        for (const technician of techs) {
          try {
            const previewWork = await readWork(`/api/v1/workspace/my-work?technicianId=${encodeURIComponent(technician.id)}`);
            if (previewWork.items.length) {
              setViewedTech(technician.id);
              return;
            }
          } catch {
            // Try the next technician without interrupting the live Field UI.
          }
        }
      }).catch(() => {
        // The View as selector can retry later; Field remains usable.
      });
    }
  }, [access, jobs.length, loadTechnicians, loading, scheduleDay, viewedTech]);

  useEffect(() => {
    void loadWork();
  }, [loadWork]);

  // Office updates happen outside this screen. Recheck operational locks when
  // Field is resumed, and while an active activity could block the next call.
  // Do not refresh editor panels: changed appointment status reloads its card.
  useEffect(() => {
    if (!session?.user) return;
    const refresh = () => {
      if (document.visibilityState === "hidden" || workLoadInFlight.current) return;
      void loadWork({ quiet: true });
    };
    window.addEventListener("focus", refresh);
    window.addEventListener("online", refresh);
    document.addEventListener("visibilitychange", refresh);
    const timer = activeJob || activeBlockEntry
      ? window.setInterval(refresh, 30_000) : undefined;
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("online", refresh);
      document.removeEventListener("visibilitychange", refresh);
      if (timer !== undefined) window.clearInterval(timer);
    };
  }, [session?.user, loadWork, activeJob, Boolean(activeBlockEntry)]);

  const refreshField = useCallback(async () => {
    await loadWork();
    setRefreshToken((value) => value + 1);
  }, [loadWork]);

  const mutateFieldTime = useCallback(async (path: string, body?: unknown) => {
    setTimeBusy(path);
    setError("");
    try {
      const query = viewedTech ? `?technicianId=${encodeURIComponent(viewedTech)}` : "";
      const response = await apiFetch(`/api/v1/workspace/field/time${path}${query}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new Error(payload?.error?.message || "That time entry could not be saved");
      await loadWork();
      return true;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "That time entry could not be saved");
      return false;
    } finally {
      setTimeBusy("");
    }
  }, [loadWork, viewedTech]);

  const { scrollerRef, pullDistance, refreshing } = useFieldPullToRefresh({
    onRefresh: refreshField,
    disabled: isPending || !session?.user,
  });
  const pullHint = refreshing
    ? "Refreshing…"
    : pullDistance >= FIELD_PULL_THRESHOLD_PX
      ? "Release to refresh"
      : "Pull to refresh";

  const selected = useMemo(
    () => jobs.find((job) => job.appointmentId === selectedId) ?? null,
    [jobs, selectedId],
  );
  const today = fieldDate(now, timezone);
  const visibleDay = scheduleDay || today;
  const dailyJobs = jobs.filter((job) => fieldDate(job.scheduledStart, timezone) === visibleDay);
  const dailyEvents = events.filter((event) => fieldDate(event.startsAt, timezone) <= visibleDay && fieldDate(Date.parse(event.endsAt) - 1, timezone) >= visibleDay);
  const scheduleRows = [...dailyJobs.map((job) => ({ at: job.scheduledStart, job, event: null })), ...dailyEvents.map((event) => ({ at: event.startsAt, job: null, event }))].sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
  const canPreview = access?.role === "owner" || access?.role === "admin";

  if (isPending || (session?.user && loading && !access)) {
    return (
      <main data-field-app
        ref={scrollerRef}
        className="flex min-h-[100dvh] items-center justify-center bg-white text-sm text-[#6b6b6b]"
      >
        Loading technician workspace…
      </main>
    );
  }

  return (
    <main data-field-app
      ref={scrollerRef}
      aria-busy={refreshing}
      className="min-h-[100dvh] overscroll-y-contain bg-white pb-[calc(6rem+env(safe-area-inset-bottom))] text-black"
    >
      <div
        className="flex flex-col items-center justify-end overflow-hidden"
        style={{ height: refreshing ? 56 : pullDistance }}
        aria-hidden={pullDistance <= 0 && !refreshing}
      >
        <div className="flex h-14 flex-col items-center justify-center">
          <span
            className={refreshing ? "animate-spin text-[#6b6b6b]" : "text-[#6b6b6b]"}
            style={
              refreshing
                ? undefined
                : { transform: `rotate(${Math.round(pullDistance * 2.5)}deg)` }
            }
          >
            <RefreshIcon />
          </span>
          <span className="mt-0.5 text-[11px] font-semibold text-[#8a8a8a]">
            {pullHint}
          </span>
        </div>
      </div>
      <p className="sr-only" aria-live="polite">
        {refreshing || pullDistance > 0 ? pullHint : ""}
      </p>
      <div className="w-full px-4 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <header className="relative py-2">
          <div className="grid grid-cols-[64px_minmax(0,1fr)_44px] items-center">
            {access?.companyLogoUrl ? <img src={access.companyLogoUrl} alt="Company logo" className="h-16 w-16 object-contain" /> : <span title="Company logo needs connection" className="grid h-16 w-16 place-items-center bg-white text-lg font-semibold">{access?.companyName?.split(' ').map((word) => word[0]).slice(0, 2).join('') || 'WV'}</span>}
            <div className="px-2">
              <h1 className="text-[21px] font-semibold leading-tight">{new Date(now).getHours() < 12 ? 'Good morning' : new Date(now).getHours() < 17 ? 'Good afternoon' : 'Good evening'}, {technicianName}</h1>
            </div>
            <FieldAccountMenu
              signedIn={Boolean(session?.user)}
              onSignOut={() =>
                void authClient.signOut().then(() => navigate(FIELD_SIGN_IN_PATH))
              }
              onSignIn={() => navigate(FIELD_SIGN_IN_PATH)}
              viewAs={
                canPreview
                  ? {
                      technicians,
                      viewedTech,
                      onOpen: loadTechnicians,
                      onSelect: (technicianId) => {
                        keepOwnAssignments.current = !technicianId;
                        setSelectedId("");
                        setScheduleDay("");
                        setTimeEntries([]);
                        setOpenJobTimers([]);
                        setHasActiveJobData(false);
                        setJobs([]);
                        setEvents([]);
                        setViewedTech(technicianId);
                      },
                    }
                  : undefined
              }
            />
          </div>
        </header>

        {!selected && homeView === "timesheet" && <div className="mb-3">
          <div className="mt-2 grid grid-cols-3 gap-2" aria-label="Unscheduled activities">
            <button type="button" aria-pressed={clockedIn} disabled={Boolean(timeBusy)} onClick={() => void mutateFieldTime(clockedIn ? "/attendance/stop" : "/attendance/start")} className={`min-h-11 rounded-full border px-2 text-xs font-semibold disabled:opacity-50 ${clockedIn ? 'border-[var(--accent)] bg-[var(--accent)] text-white' : 'border-[#e2e2e2] bg-white'}`}>{clockedIn ? "Clock out" : "Clock in"}</button>
            {['Lunch', 'Commute'].map((title) => {
              const eventId = `unscheduled:${title.toLowerCase()}`;
              const running = activeBlock?.eventId === eventId;
              const canPauseJob = title === 'Lunch' && features.has('job_lunch');
              return <button key={title} type="button" aria-pressed={running} disabled={Boolean(timeBusy) || !clockedIn || (!running && Boolean(activeBlock || (activeJob && !canPauseJob)))} onClick={() => void mutateFieldTime(running ? `/activities/${encodeURIComponent(activeBlock!.id)}/stop` : "/activities/start", running ? undefined : { kind: title.toLowerCase() })} className={`min-h-11 rounded-full border px-2 text-xs font-semibold disabled:opacity-50 ${running ? 'border-[var(--accent)] bg-[var(--accent)] text-white' : 'border-[#e2e2e2] bg-white'}`}>{running ? 'End' : 'Start'} {title}</button>;
            })}
          </div>
          {activeBlock && <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm"><p>Running activity: {activeBlock.title}</p><button type="button" disabled={Boolean(timeBusy)} onClick={() => void mutateFieldTime(`/activities/${encodeURIComponent(activeBlock.id)}/stop`)} className="mt-2 min-h-11 rounded-xl border bg-white px-3">Finish activity</button></div>}
          {openJobTimers.map((timer) => {
            const appointment = jobs.find((item) => item.jobId === timer.jobId);
            return <div key={timer.jobId} className="mt-3 rounded-xl border p-3 text-sm"><p>Running job: {jobCode(timer.jobNumber)} · {timer.title}</p>{appointment ? <button type="button" onClick={() => { setHomeView("home"); setSelectedId(appointment.appointmentId); }} className="mt-2 min-h-11 rounded-xl border px-3">Open running job</button> : <p className="mt-2">Contact dispatch to review this open timer.</p>}</div>;
          })}
        </div>}
        {!selected && homeView === "timesheet" && <TechnicianTimeReport key={`${techKey}:${timezone}`} timezone={timezone || 'UTC'} technicianId={viewedTech || undefined} revision={timeEntries} />}
        {!selected && homeView === "timesheet" && <section className={`${card} mb-3`} aria-label="Daily timesheet">
          <h2 className="text-sm font-semibold">Activity details · {displayedTimeDay}</h2>
          {timeDates.length > 1 && <select aria-label="Timesheet date" value={displayedTimeDay} onChange={(event) => setTimesheetDay(event.target.value)} className="mt-2 min-h-12 rounded-full border px-4 text-base font-semibold">{timeDates.map((day) => <option key={day}>{day}</option>)}</select>}
          {jobs.some((job) => job.appointmentStatus === 'completed' && !progress[progressKey(job.appointmentId)]) && <p className="mt-2 text-xs text-[#8a6a00]">Completed jobs with no recorded time: {jobs.filter((job) => job.appointmentStatus === 'completed' && !progress[progressKey(job.appointmentId)]).map((job) => jobCode(job.jobNumber ?? '')).join(', ')}. Timer data needs connection.</p>}
          <div className="mt-3" aria-label="Clock-in history"><h3 className="text-sm font-semibold">Clock-in history</h3>{displayedShifts.length ? displayedShifts.map((shift) => <p key={shift.start} className="mt-2 text-xs text-[#6b6b6b]">{time(new Date(shift.start).toISOString())} – {shift.end ? time(new Date(shift.end).toISOString()) : "Clocked in"}</p>) : <p className="mt-2 text-xs text-[#8a8a8a]">No clock-in recorded for this day.</p>}</div>
          {activityRows.length ? <div className="mt-2 divide-y divide-[#ececec]">{activityRows.map((entry) => <div key={entry.key} className="flex justify-between gap-3 py-2 text-sm"><div><p className="font-medium">{entry.title}</p><p className="mt-0.5 text-xs text-[#8a8a8a]">{entry.kind}{!entry.isJob && <> · {time(new Date(entry.start).toISOString())} – {entry.end === undefined ? 'Running' : time(new Date(entry.end).toISOString())}</>}</p>{entry.isJob && <div className="mt-1 space-y-1 text-xs text-[#6b6b6b]"><p>Dispatch: {entry.dispatchedAt !== undefined ? time(new Date(entry.dispatchedAt).toISOString()) : 'Not recorded'}</p><p>Arrived: {entry.arrivedAt !== undefined ? time(new Date(entry.arrivedAt).toISOString()) : entry.end === undefined && progress[entry.key]?.status === 'en_route' ? 'Not yet' : 'Not recorded'}</p>{entry.end !== undefined && <p>Completed: {time(new Date(entry.end).toISOString())}</p>}</div>}</div><span aria-label={`Time for ${entry.title}`} className="text-right tabular-nums"><span className="block text-[10px] text-[#8a8a8a]">{entry.end === undefined ? "Running total" : "Total"}</span>{durationLabel(entry.duration)}</span></div>)}</div> : null}
          {events.some((event) => event.kind === 'pto') && <div className="mt-3 border-t border-[#ececec] pt-3" aria-label="PTO for payroll">
            <h3 className="text-sm font-semibold">PTO for payroll</h3>
            {events.filter((event) => event.kind === 'pto').map((event) => <div key={event.id} className="mt-2 text-xs"><p className="font-medium">{event.title || 'PTO'}</p><p className="mt-1 text-[#6b6b6b]">{dateLabel(event.startsAt)}{dateLabel(new Date(Date.parse(event.endsAt) - 1)) !== dateLabel(event.startsAt) ? ` – ${dateLabel(new Date(Date.parse(event.endsAt) - 1))}` : ""} · {event.allDay ? 'All day' : `${time(event.startsAt)} – ${time(event.endsAt)}`} · {event.paid === false ? 'Unpaid leave' : event.paid === true ? 'Paid leave' : 'Pay classification pending'}</p></div>)}
            <p className="mt-2 text-xs text-[#8a8a8a]">No clock-in required. Leave is shown separately from timed work; payroll hours still need to be connected.</p>
          </div>}
        </section>}
        {error && (
          <div
            role="alert"
            className="mb-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800"
          >
            {error}
          </div>
        )}

        {selected && <button type="button" onClick={() => setSelectedId("")} className="mb-3 min-h-11 rounded-xl px-2 text-sm font-semibold">← Back to schedule</button>}
        {!selected && homeView === "home" && (
          <section className="mb-4" aria-label="Daily schedule">
            <h2 className="field-schedule-heading mb-3">
              <span className="text-xs font-semibold uppercase tracking-wide text-[#6b6b6b]">{visibleDay === today ? "Today" : "Schedule"}</span>{" "}
              <time dateTime={visibleDay} title={fieldDayLabel(visibleDay)}>{new Date(`${visibleDay}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric", year: "numeric" })}</time>
            </h2>
            <div className="field-calendar-controls mb-3">
              <button type="button" aria-label="Previous day" onClick={() => setScheduleDay(shiftFieldDay(visibleDay, -1))} className="min-h-11 rounded-xl border px-3">‹</button>
              <label className="field-calendar-picker min-h-11 rounded-xl border px-2 text-sm">
                <span aria-hidden="true">{new Date(`${visibleDay}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
                <input type="date" aria-label="Choose schedule date" value={visibleDay} onChange={(event) => setScheduleDay(event.target.value)} className="field-calendar-date" />
              </label>
              <button type="button" aria-label="Next day" onClick={() => setScheduleDay(shiftFieldDay(visibleDay, 1))} className="min-h-11 rounded-xl border px-3">›</button>
              <button type="button" onClick={() => setScheduleDay("")} className="min-h-11 rounded-xl border px-2 text-sm">Today</button>
            </div>
            {!scheduleRows.length && <p className={`${card} text-sm`}>{loading ? "Loading schedule…" : visibleDay === today ? "No appointments today" : "No appointments on this day"}</p>}
            <div className="space-y-3">
              {scheduleRows.map(({ job, event }) => {
                if (event) {
                  const onCall = ['after_hours', 'on_call'].includes(event.kind) || (['other', 'working'].includes(event.kind) && /\b(?:on[\s_-]*call|after[\s_-]*hours)\b/i.test(event.title));
                  const running = !onCall && activeBlock?.eventId === event.id;
                  return <div key={`event:${event.id}`} className="grid min-h-20 grid-cols-[5rem_minmax(0,1fr)] gap-3 rounded-[18px] border border-dashed border-[#d4d4d4] bg-[#eeeeef] p-4">
                    <span className="text-xs font-semibold">{event.allDay ? "All day" : `${time(event.startsAt)} – ${time(event.endsAt)}`}</span>
                    <div><p className="text-sm font-semibold">{event.title || statusWords(event.kind)}</p><p className="mt-1 text-xs capitalize text-[#6b6b6b]">{onCall ? 'On call · After hours' : `Blocked time · ${statusWords(event.kind)}`}</p>
                      {onCall ? null : event.kind === 'pto' ? <p className="mt-2 text-xs text-[#6b6b6b]">PTO · no clock-in required</p> : <button type="button" disabled={Boolean(timeBusy) || !clockedIn || (!running && Boolean(activeJob || activeBlock))} onClick={() => void mutateFieldTime(running ? `/activities/${encodeURIComponent(activeBlock!.id)}/stop` : "/activities/start", running ? undefined : { kind: "scheduled", scheduleBlockId: event.id })} className="mt-2 min-h-11 rounded-full bg-white px-3 text-xs font-semibold disabled:opacity-50">{running ? `Stop ${event.title || statusWords(event.kind)}` : `Start ${event.title || statusWords(event.kind)}`}</button>}
                      {running && <span className="ml-2 text-xs font-semibold tabular-nums">{durationLabel(now - activeBlock.start)}</span>}
                    </div>
                  </div>;
                }
                if (!job) return null;
                const state = progress[progressKey(job.appointmentId)]?.status ?? job.appointmentStatus;
                return (
                  <button
                    key={job.appointmentId}
                    type="button"
                    onClick={() => setSelectedId(job.appointmentId)}
                    data-field-stage={fieldJobStage(state)}
                    className="field-status-card grid min-h-[104px] w-full grid-cols-[5rem_minmax(0,1fr)_auto] items-center gap-3 rounded-[18px] bg-white p-4 text-left shadow-[0_3px_12px_rgba(0,0,0,0.10)] focus-visible:outline-2 focus-visible:outline-offset-2"

                  >
                    <span className="self-stretch border-r border-[#ececec] pr-3 text-xs font-semibold"><span className="block">{time(job.scheduledStart)}</span><span className="mt-1 block font-normal text-[#8a8a8a]">{time(job.scheduledEnd)}</span></span>
                    <span className="min-w-0"><span className="block text-sm font-semibold">{job.customerName}</span><span className="mt-1 block text-xs font-medium text-[#6b6b6b]">{[job.jobTypeName, job.serviceTeamName].filter(Boolean).join(" · ") || "Job type · needs connection"}</span><span className="mt-1 block text-xs text-[#6b6b6b]">{estimateDescription(job.description)}</span><span className="mt-1 block text-xs text-[#6b6b6b]">{job.addressLine1}</span><span className="field-status-chip mt-2 inline-block rounded-full px-2 py-1 text-[11px] font-semibold capitalize">{appointmentStatusLabel(state, job.customerConfirmedAt)}</span><FieldTags {...job} /></span>
                    <span aria-hidden="true" className="text-xl text-[#8a8a8a]">›</span>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {selected && activeBlock && <div className={`${card} mb-3 text-sm`}><p>Running activity: {activeBlock.title}</p><button type="button" disabled={Boolean(timeBusy)} onClick={() => void mutateFieldTime(`/activities/${encodeURIComponent(activeBlock.id)}/stop`)} className="mt-2 min-h-11 rounded-xl border px-3 font-semibold">Finish {activeBlock.title}</button></div>}
        {selected && openJobTimers.filter((timer) => timer.jobId !== selected.jobId).map((timer) => {
          const appointment = jobs.find((item) => item.jobId === timer.jobId);
          return <div key={timer.jobId} className={`${card} mb-3 text-sm`}><p>Running job: {jobCode(timer.jobNumber)} · {timer.title}</p>{appointment ? <button type="button" onClick={() => setSelectedId(appointment.appointmentId)} className="mt-2 min-h-11 rounded-xl border px-3 font-semibold">Open running job</button> : <p className="mt-2">Contact dispatch to review this open timer.</p>}</div>;
        })}
        {selected && (selected.jobId ? (
          <CurrentJob
            key={`${viewedTech}:${selected.appointmentId}`}
            viewStorageKey={session?.user.id ? `${fieldViewKey(session.user.id)}:panels:${viewedTech}:${selected.appointmentId}` : undefined}
            progress={progress[progressKey(selected.appointmentId)]}
            clockedIn={clockedIn}
            jobTimerRunning={hasActiveJobData ? openJobTimers.some((timer) => timer.jobId === selected.jobId) : undefined}
            anotherJobRunning={Boolean(activeBlock) || openJobTimers.some((timer) => timer.jobId !== selected.jobId) || (!hasActiveJobData && runningProgress.some(([key]) => {
              const appointment = jobs.find((item) => progressKey(item.appointmentId) === key);
              return appointment && appointment.jobId !== selected.jobId;
            }))}
            activityDescription={activeBlock?.title || openJobTimers.filter((timer) => timer.jobId !== selected.jobId).map((timer) => `${jobCode(timer.jobNumber)} · ${timer.title}`).join(", ") || runningProgress.filter(([key]) => key !== progressKey(selected.appointmentId)).map(([, entry]) => entry.customerName || "Job timer").join(", ")}
            onActivityChanged={() => void loadWork()}

            onProgressChange={(entry) => setProgress((current) => ({ ...current, [progressKey(selected.appointmentId)]: { ...entry, customerName: selected.customerName, jobNumber: selected.jobNumber } }))}
            onCompleted={() => { setSelectedId(""); setHomeView("home"); }}
            job={{ ...selected, jobId: selected.jobId, jobStatus: selected.jobStatus ?? 'scheduled' }}
            actingTechnicianId={viewedTech}
            canUseInventory={Boolean(access?.permissions.includes("inventory:use")) && !viewedTech}
            refreshToken={refreshToken}
          />
        ) : (
          <OwnerAppointment key={selected.appointmentId} item={selected} actingTechnicianId={viewedTech} refreshToken={refreshToken} />
        )
        )}

        {!selected && homeView === "home" && lessons.length > 0 && (
          <details className="mt-5 rounded-2xl border border-[#ececec] bg-white">
            <summary className="flex min-h-14 cursor-pointer items-center justify-between gap-3 px-4 text-sm font-semibold">Technician training <span className="text-xs font-normal text-[#6b6b6b]">{lessons.length} lessons · Expand</span></summary>
            <div className="space-y-2 p-3 pt-0">
              {lessons.map((lesson) => <details key={lesson.id} className="rounded-xl bg-[#f7f7f7] p-3">
                <summary className="cursor-pointer text-sm font-semibold"><span className="block text-[11px] font-medium text-[#6b6b6b]">{lesson.topic}</span>{lesson.title}</summary>
                <p className="mt-2 text-sm text-[#6b6b6b]">{lesson.issueSummary}</p>
                <p className="mt-2 rounded-lg bg-[#fff7df] p-3 text-sm"><strong>In the field: </strong>{lesson.keyTakeaway}</p>
              </details>)}
            </div>
          </details>
        )}
        {!selected && <nav aria-label="Field navigation" className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] left-1/2 z-20 flex -translate-x-1/2 gap-1 rounded-full border border-white bg-white/95 p-1.5 shadow-lg">
          {([['home', 'Home'], ['timesheet', 'Timesheet']] as const).map(([id, label]) => <button key={id} type="button" aria-pressed={homeView === id} onClick={() => setHomeView(id)} className={`min-h-12 min-w-24 rounded-full px-4 text-sm font-semibold ${homeView === id ? 'bg-[#ededf0]' : ''}`}>{label}</button>)}
        </nav>}

      </div>
    </main>
  );
}

function OwnerAppointment({ item, actingTechnicianId, refreshToken }: { item: WorkItem; actingTechnicianId: string; refreshToken: number }) {
  const [status, setStatus] = useState(item.appointmentStatus);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function update(action: 'arrive' | 'complete') {
    setBusy(true); setError('');
    try {
      const response = await apiFetch(`/api/v1/workspace/field/appointments/${encodeURIComponent(item.appointmentId)}/status`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action }),
      });
      if (!response.ok) throw new Error('Unable to update this appointment.');
      const payload = await response.json();
      setStatus(payload.data.item.status);
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Unable to update appointment'); }
    finally { setBusy(false); }
  }
  return <><FieldLocationHistory appointmentId={item.appointmentId} actingTechnicianId={actingTechnicianId} refreshToken={refreshToken} /><section className={card}>
    <p className="text-sm capitalize">{item.ownerType} appointment · {status}</p>
    <h2 className="mt-2 text-lg font-semibold">{item.customerName}</h2>
    <FieldTags {...item} />
    <p>{item.description}</p><p>{item.addressLine1}, {item.city}</p>
    {error && <p role="alert">{error}</p>}
    {!['completed', 'cancelled', 'no_show'].includes(status) && <div className="mt-4 flex gap-3">
      <button disabled={busy} onClick={() => void update('arrive')}>Arrived</button>
      <button disabled={busy} onClick={() => void update('complete')}>Complete appointment</button>
    </div>}
  </section>{item.ownerType === 'estimate' && item.ownerId && <FieldEstimateWorkspace appointment={item} estimateId={item.ownerId} actingTechnicianId={actingTechnicianId} refreshToken={refreshToken} />}</>;
}

function CurrentJob({
  viewStorageKey,
  job,
  actingTechnicianId,
  canUseInventory,
  refreshToken,
  progress,
  onProgressChange,
  onCompleted,
  anotherJobRunning,
  activityDescription,
  onActivityChanged,
  clockedIn,
  jobTimerRunning,
}: {
  viewStorageKey?: string;
  progress?: JobProgress;
  onProgressChange: (progress: JobProgress) => void;
  onCompleted: () => void;
  anotherJobRunning: boolean;
  activityDescription: string;
  onActivityChanged: () => void;
  clockedIn: boolean;
  jobTimerRunning?: boolean;
  job: WorkItem & { jobId: string; jobStatus: string };
  actingTechnicianId: string;
  canUseInventory: boolean;
  refreshToken: number;
}) {
  const [detail, setDetail] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");
  const [showPayment, setShowPayment] = useState(false);
  const [confirmFutureDispatch, setConfirmFutureDispatch] = useState(false);
  const [completionOpen, setCompletionOpen] = useState(false);
  const [requestAppointmentOpen, setRequestAppointmentOpen] = useState(false);
  const [appointmentReason, setAppointmentReason] = useState('');
  const [returnVisitHours, setReturnVisitHours] = useState('');
  const [returnVisitHelper, setReturnVisitHelper] = useState('');
  const [returnVisitParts, setReturnVisitParts] = useState('');
  const [returnVisitSupplier, setReturnVisitSupplier] = useState('');
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [reference, setReference] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addMode, setAddMode] = useState<'manual' | 'agent'>('manual');
  const [requestedTool, setRequestedTool] = useState('');
  const [maintenanceBuilderRequest,setMaintenanceBuilderRequest]=useState(0);
  const [poDraft, setPoDraft] = useState<Record<string, string> | null>(null);
  const estimateRef = useRef<HTMLDivElement>(null);
  const invoiceRef = useRef<HTMLDivElement>(null);
  const [invoiceActionsTarget, setInvoiceActionsTarget] = useState<HTMLDivElement | null>(null);
  const purchaseRef = useRef<HTMLDivElement>(null);
  const photosRef = useRef<HTMLDivElement>(null);
  const closeoutRef = useRef<HTMLDivElement>(null);
  const completionAction = useRef<(() => Promise<void>) | null>(null);
  const now = useTimerNow();
  const actingQuery = `?appointmentId=${encodeURIComponent(job.appointmentId)}${actingTechnicianId ? `&technicianId=${encodeURIComponent(actingTechnicianId)}` : ""}`;

  const [addEquipmentRequest, setAddEquipmentRequest] = useState(0);
  const [equipmentPlans, setEquipmentPlans] = useState<{jobId:string; data:FieldEquipmentPlans|null}>({jobId:'',data:null});
  const receiveEquipmentPlans = useCallback((data:FieldEquipmentPlans|null)=>setEquipmentPlans({jobId:job.jobId,data}),[job.jobId]);
  const equipmentContext = `${job.appointmentId}:${actingTechnicianId}`;
  const [profileEquipment, setProfileEquipment] = useState<{context: string; items: ProfileEquipment[] | null; failed: boolean}>({context: '', items: null, failed: false});
  const locationEquipment = profileEquipment.context === equipmentContext ? profileEquipment.items : null;
  const equipmentFailed = profileEquipment.context === equipmentContext && profileEquipment.failed;
  const equipmentOnFile = [...new Map([...(locationEquipment ?? []), ...(detail?.equipment ?? [])].map(item=>[item.id,item])).values()];
  useEffect(() => {
    if (!detail) return;
    const controller = new AbortController();
    setProfileEquipment({context: equipmentContext, items: null, failed: false});
    void (async () => {
      try {
        const query = actingTechnicianId ? `?technicianId=${encodeURIComponent(actingTechnicianId)}` : '';
        const response = await apiFetch(`/api/v1/workspace/field/appointments/${encodeURIComponent(job.appointmentId)}/location${query}`, {signal: controller.signal});
        const payload = await response.json();
        if (!response.ok || !Array.isArray(payload.data?.equipment)) throw new Error('Unable to check location equipment');
        if (!controller.signal.aborted) setProfileEquipment({context: equipmentContext, items: payload.data.equipment, failed: false});
      } catch {
        if (!controller.signal.aborted) setProfileEquipment({context: equipmentContext, items: null, failed: true});
      }
    })();
    return () => controller.abort();
  }, [equipmentContext, detail?.equipment, refreshToken]);

  const load = async (options?: { quiet?: boolean; signal?: AbortSignal }) => {
    try {
      if (!options?.quiet) setLoading(true);
      const response = await apiFetch(
        `/api/v1/workspace/field/jobs/${encodeURIComponent(job.jobId)}${actingQuery}`,
        { signal: options?.signal },
      );
      const payload = await response.json().catch(() => null);
      if (!response.ok)
        throw new Error(
          payload?.error?.message || "Unable to load job details",
        );
      const loaded = payload.data as Detail;
      loaded.timesheets = loaded.timesheets.filter(entry => entry.appointmentId === job.appointmentId);
      setDetail({
        ...loaded,
        equipment: loaded.equipment ?? [],
      });
      // Import recorded job time when opening an existing job; never invent a historical start.
      if (!progress?.startedAt && loaded.timesheets.length) {
        const starts = loaded.timesheets.map((entry) => Date.parse(entry.clockIn)).filter(Number.isFinite);
        const ends = loaded.timesheets.flatMap(entry => entry.clockOut ? [Date.parse(entry.clockOut)] : []).filter(Number.isFinite);
        const completion = job.appointmentStatus === 'completed' ? (ends.length ? new Date(Math.max(...ends)).toISOString() : loaded.job.status === 'completed' ? loaded.job.completedAt : undefined) : undefined;
        const finishedAt = completion ? Date.parse(completion) : undefined;
        if (starts.length && (finishedAt === undefined || Number.isFinite(finishedAt))) {
          onProgressChange({
            source: "recorded", status: finishedAt !== undefined ? "completed" : job.appointmentStatus,
            startedAt: Math.min(...starts),
            dispatchedAt: job.dispatchedAt ? Date.parse(job.dispatchedAt) : undefined,
            arrivedAt: job.arrivedAt ? Date.parse(job.arrivedAt) : undefined,
            recordedMs: recordedDuration(loaded.timesheets, Date.now()),
            runningSince: jobTimerRunning !== false && loaded.timesheets.some((entry) => !entry.clockOut && !timesheetIsOnLunch(entry)) ? Date.now() : undefined,
            finishedAt,
            ...(finishedAt !== undefined ? { totalMs: recordedDuration(loaded.timesheets, finishedAt) } : {}),
          });
        }
      }
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Unable to load job details",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [job.jobId, job.appointmentId, job.appointmentStatus, actingTechnicianId]);

  useOnFieldRefresh(refreshToken, () => {
    void load({ quiet: true });
  });

  const post = async (path: string, body?: unknown) => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 30_000);
    setBusy(path);
    setError("");
    try {
      const response = await apiFetch(
        `/api/v1/workspace/field/jobs/${encodeURIComponent(job.jobId)}${path}${actingQuery}`,
        {
          method: "POST",
          signal: controller.signal,
          headers: { "Content-Type": "application/json" },
          body: body === undefined ? undefined : JSON.stringify(body),
        },
      );
      const payload = await response.json().catch((cause) => {
        if (controller.signal.aborted) throw cause;
        return null;
      });
      if (!response.ok)
        throw new Error(
          payload?.error?.message || "That action could not be saved",
        );
      // Progress is already saved. Its caller updates the chips immediately;
      // a slow details refresh must not turn that successful write into a timeout.
      if (!["/timer/start", "/arrive", "/complete", "/complete-work", "/complete-estimate"].includes(path)) {
        await load({ quiet: true, signal: controller.signal });
      }
      onActivityChanged();
      return true;
    } catch (cause) {
      setError(
        controller.signal.aborted
          ? "The request timed out. Refresh the schedule to check whether it saved before trying again."
          : cause instanceof Error
          ? cause.message
          : "That action could not be saved",
      );
      return false;
    } finally {
      window.clearTimeout(timeout);
      setBusy("");
    }
  };

  const addInvoiceLine = async (
    description: string,
    quantity: number,
    unitPriceCents: number,
    priceBookItemId?: string,
  ) => {
    const path = "/invoice/line-items";
    setBusy(path);
    setError("");
    try {
      const response = await apiFetch(
        `/api/v1/workspace/field/jobs/${encodeURIComponent(job.jobId)}${path}${actingQuery}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ description, quantity, unitPriceCents, ...(priceBookItemId ? { priceBookItemId } : {}) }),
        },
      );
      const payload = await response.json().catch(() => null);
      if (!response.ok)
        throw new Error(
          payload?.error?.message || "That invoice line could not be saved",
        );
      // The add-line endpoint already returns the recalculated invoice. Using
      // it avoids reloading the entire field job after every pricebook item.
      const updatedInvoice = payload?.data?.invoice as Invoice | undefined;
      if (updatedInvoice) {
        setDetail((current) =>
          current
            ? {
                ...current,
                invoices: current.invoices.some((invoice) => invoice.id === updatedInvoice.id)
                  ? current.invoices.map((invoice) =>
                      invoice.id === updatedInvoice.id ? updatedInvoice : invoice,
                    )
                  : [updatedInvoice, ...current.invoices],
              }
            : current,
        );
      }
      return { ok: true, message: "" };
    } catch (cause) {
      const message =
        cause instanceof Error
          ? cause.message
          : "That invoice line could not be saved";
      setError(message);
      return { ok: false, message };
    } finally {
      setBusy("");
    }
  };

  const scanEquipment = async (equipment: EquipmentEntry) => {
    const path = "/equipment/scan";
    setBusy(path);
    setError("");
    try {
      const response = await apiFetch(
        `/api/v1/workspace/field/jobs/${encodeURIComponent(job.jobId)}${path}${actingQuery}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(equipment),
        },
      );
      const payload = await response.json().catch(() => null);
      if (!response.ok)
        throw new Error(
          payload?.error?.message || "That equipment scan could not be saved",
        );
      await load();
      return { ok: true, message: "" };
    } catch (cause) {
      const message =
        cause instanceof Error
          ? cause.message
          : "That equipment scan could not be saved";
      setError(message);
      return { ok: false, message };
    } finally {
      setBusy("");
    }
  };

  const [photoCount, setPhotoCount] = useState<number | null | "error">(null);
  useEffect(() => { setPhotoCount(null); }, [job.jobId]);
  const invoice = detail?.invoices[0];
  const appointmentStatus = progress?.status ?? job.appointmentStatus;
  const completed = Boolean(detail?.job.status === 'completed' || detail?.job.completedAt || appointmentStatus === "completed");
  const continuation = detail?.continuation;
  const finishVisitOnly = Boolean(continuation?.hasOtherRecordedWork || continuation?.waitingOn || job.openCrewmateCount);
  // Writes on a field job are only available after this technician has arrived.
  // This mirrors the API guard for photos, notes, equipment, parts, and billing.
  const canEditJobDetails = !completed && ["arrived", "in_progress"].includes(appointmentStatus);
  const elapsedMs = progress?.startedAt !== undefined ? jobDuration(progress, now) : recordedDuration(detail?.timesheets ?? [], now);
  const waitingToLeave = ["pending", "scheduled", "confirmed"].includes(appointmentStatus);
  const phone = detail?.job.customerPhone?.trim() || job.customerPhone?.trim();
  const email = detail?.job.customerEmail?.trim() || job.customerEmail?.trim();
  const completeJob = () => {
    const finishedAt = Date.now();
    const recordedStarts = (detail?.timesheets ?? []).map((entry) => Date.parse(entry.clockIn)).filter(Number.isFinite);
    const startedAt = progress?.startedAt ?? (recordedStarts.length ? Math.min(...recordedStarts) : finishedAt);
    const totalMs = progress?.startedAt !== undefined ? jobDuration(progress, finishedAt) : recordedDuration(detail?.timesheets ?? [], finishedAt);
    onProgressChange({ ...progress, source: undefined, status: "completed", startedAt, finishedAt, totalMs });
    onCompleted();
  };
  const completeOwnWork = async (requestAnotherAppointment = false) => {
    const ok = await post('/complete-work', requestAnotherAppointment ? {
      requestAnotherAppointment:true, reason:appointmentReason,
      ...(returnVisitHours ? {estimatedHours:Number(returnVisitHours)} : {}),
      ...(returnVisitHelper ? {helperNeeded:returnVisitHelper} : {}),
      ...(returnVisitParts ? {partsOrdered:returnVisitParts} : {}),
      ...(returnVisitParts === 'yes' && returnVisitSupplier.trim() ? {partsSupplier:returnVisitSupplier.trim()} : {}),
    } : {});
    if (!ok) return;
    const finishedAt = Date.now();
    const startedAt = progress?.startedAt ?? finishedAt;
    onProgressChange({ ...progress, status: 'completed', startedAt, finishedAt, totalMs: jobDuration(progress ?? { status: 'arrived', startedAt }, finishedAt) });
    onCompleted();
  };
  const openCloseout = () => {
    const panel = invoiceRef.current?.querySelector<HTMLDetailsElement>("details");
    if (panel) panel.open = true;
    closeoutRef.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
    closeoutRef.current?.focus({ preventScroll: true });
  };
  const instructions = estimateDescription(job.specialInstructions || detail?.job.specialInstructions);
  const serviceNotes = estimateDescription(job.description);
  const dispatchToJob = async () => {
    setConfirmFutureDispatch(false);
    if (!waitingToLeave || completed || !clockedIn || anotherJobRunning || busy) return;
    if (await post('/timer/start')) {
      const timestamp = Date.now();
      onProgressChange({ source: 'recorded', status: 'en_route', startedAt: timestamp, runningSince: timestamp, dispatchedAt: timestamp });
    }
  };

  return (
    <article id="selected-job-card" aria-label={`${job.customerName} job card`} data-field-stage={fieldJobStage(completed ? "completed" : appointmentStatus)} className="min-w-0 overflow-hidden rounded-[22px] border border-[#e2e2e2] bg-white shadow-[0_4px_18px_rgba(0,0,0,0.12)] [&>section]:rounded-none [&>section]:border-b [&>section]:border-[#ececec] [&>section]:shadow-none">
      <section className="bg-[#f7f7f8] p-4 text-[#202124]">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-semibold leading-tight">{job.customerName}</h2>
            <FieldTags {...(detail?.job ?? job)} />
            <div aria-label="Customer contact" className="mt-1 flex min-w-0 flex-col text-sm font-medium leading-6">
              {phone ? <div className="flex min-w-0 items-center gap-1">
                <a aria-label={`Call ${job.customerName} at ${fieldPhoneLabel(phone)}`} className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-sky-900 no-underline hover:bg-sky-100" href={`tel:${phone.replace(/[^\d+]/g, "")}`}><Phone size={18} aria-hidden="true" /></a>
                <a aria-label={`Text ${job.customerName}`} title={`Text ${job.customerName}`} className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-sky-900 no-underline hover:bg-sky-100" href={`sms:${phone.replace(/[^\d+]/g, '')}`}><MessageSquare size={18} aria-hidden="true" /></a>
                <span className="min-w-0 break-words pl-1 tabular-nums">{fieldPhoneLabel(phone)}</span>
              </div> : <span className="py-0.5 font-normal">No phone on file</span>}
              {email ? <a className="inline-flex min-h-11 min-w-0 items-center gap-2 no-underline" href={`mailto:${email}`}><Mail size={18} className="shrink-0" aria-hidden="true" /><span className="min-w-0 break-all">{email}</span></a> : <span className="py-0.5 font-normal">No email on file</span>}
            </div>
          </div>
          <button type="button" aria-label="Add to job" aria-expanded={addOpen} disabled={!canEditJobDetails} title={canEditJobDetails ? undefined : "Arrive at the job before adding job details"} onClick={() => setAddOpen((value) => !value)} className="h-11 w-11 shrink-0 rounded-full bg-white/60 text-2xl disabled:cursor-not-allowed disabled:opacity-45">+</button>
        </div>
        <p aria-label="Scheduled time" className="mt-3 text-sm font-semibold text-slate-700"><span className="mb-1 block font-normal text-slate-500">Scheduled</span> {new Date(job.scheduledStart).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} · {time(job.scheduledStart)} – {time(job.scheduledEnd)}</p>
        <p className="mt-2 flex flex-wrap items-baseline gap-x-2 text-base font-semibold text-slate-900"><span aria-label="Job number">{job.jobNumber}</span><span aria-hidden="true" className="text-slate-400">·</span><span aria-label="Job type">{detail?.job.jobTypeName || job.jobTypeName || 'Not specified'}</span></p>
        <div className="mt-2">
          <p aria-label="Service location" className="flex items-start gap-2 text-sm font-medium"><span className="mt-0.5 shrink-0"><PinIcon /></span><span>{[job.addressLine1, job.addressLine2, job.city, job.state, job.postalCode].filter(Boolean).join(', ')}</span></p>
          <div aria-label="Maps" className="flex flex-wrap gap-x-4 pl-5">
            <a href={`https://maps.apple.com/?daddr=${encodeURIComponent([job.addressLine1, job.addressLine2, job.city, job.state, job.postalCode].filter(Boolean).join(', '))}`} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center text-xs font-semibold text-sky-800 underline underline-offset-4">Apple Maps</a>
            <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent([job.addressLine1, job.addressLine2, job.city, job.state, job.postalCode].filter(Boolean).join(', '))}`} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center text-xs font-semibold text-sky-800 underline underline-offset-4">Google Maps</a>
          </div>
        </div>
        {serviceNotes && <div aria-label="Service notes" className="mt-3 rounded-xl bg-white/75 p-3 text-black"><h3 className="text-xs font-semibold text-slate-500">Service notes</h3><p className="mt-1 whitespace-pre-wrap break-words text-sm">{serviceNotes}</p></div>}
      </section>
      {confirmFutureDispatch && <Dialog title="Dispatch to a future job?" onClose={() => setConfirmFutureDispatch(false)} width="max-w-[398px]">
        <p className="text-sm">This appointment is scheduled for {dateLabel(job.scheduledStart)} at {time(job.scheduledStart)}. Dispatching now starts your travel time early.</p>
        <div className="mt-4 flex gap-2"><button type="button" onClick={() => setConfirmFutureDispatch(false)} className="min-h-11 flex-1 rounded-xl border px-3 text-sm font-semibold">Cancel</button><button type="button" disabled={Boolean(busy) || !waitingToLeave || completed || !clockedIn || anotherJobRunning} onClick={() => void dispatchToJob()} className="min-h-11 flex-1 rounded-xl bg-[#6b46d9] px-3 text-sm font-semibold text-white disabled:opacity-50">Dispatch anyway</button></div>
      </Dialog>}
      {detail && (
          <>

      {addOpen && canEditJobDetails && <section className={card} aria-label="Add to job"><div className="flex gap-2">{(['manual', 'agent'] as const).map((mode) => <button key={mode} type="button" aria-pressed={addMode === mode} onClick={() => setAddMode(mode)} className={`min-h-11 rounded-full px-4 text-sm font-semibold ${addMode === mode ? 'bg-black text-white' : 'bg-[#f3f3f3]'}`}>{mode === 'manual' ? 'Add manually' : 'Voice agent'}</button>)}</div>
        {addMode === 'agent' ? <p className="mt-3 text-sm text-[#6b6b6b]">Ask the agent to add a purchase order, invoice, or estimate. Voice connection is pending.</p> : <div className="mt-3 flex flex-wrap gap-2">{['Purchase order', 'Invoice', 'Estimate', 'Maintenance Plans', 'Take picture'].map((label) => <button key={label} type="button" onClick={() => { setRequestedTool(label); if(label==='Maintenance Plans'){setAddOpen(false);setMaintenanceBuilderRequest(value=>value+1);return;} const target = label === 'Estimate' ? estimateRef : label === 'Invoice' ? invoiceRef : label === 'Take picture' ? photosRef : purchaseRef; const panel = target.current?.querySelector('details'); if (panel) panel.open = true; let ancestor = target.current?.parentElement; while (ancestor) { if (ancestor instanceof HTMLDetailsElement) ancestor.open = true; ancestor = ancestor.parentElement; } if (label === 'Take picture') target.current?.querySelector<HTMLInputElement>('input[capture]')?.click(); target.current?.scrollIntoView({ block: 'start' }); }} className="min-h-11 rounded-xl border px-3 text-sm">{label === 'Take picture' || label === 'Maintenance Plans' ? label : `Add ${label.toLowerCase()}`}</button>)}</div>}
        {requestedTool && <p role="status" className="mt-2 text-xs text-[#6b6b6b]">{requestedTool} tools opened below.</p>}
      </section>}
      <section className={card} aria-label="Job time tracking">
        <h3 className="mb-2 text-xs font-semibold text-slate-500">Job progress</h3>
        <div className="grid grid-cols-3 gap-2">
          <button type="button" aria-label="Dispatch to Job" data-step="dispatched" data-active={!completed && ["dispatched", "en_route"].includes(appointmentStatus)} disabled={!waitingToLeave || completed || !clockedIn || anotherJobRunning || Boolean(busy)} onClick={() => { if (Date.parse(job.scheduledStart) > Date.now()) setConfirmFutureDispatch(true); else void dispatchToJob(); }} className="field-progress-chip min-h-14 rounded-2xl bg-[#f2f3f8] px-2 py-2 text-xs disabled:cursor-default enabled:font-semibold"><span className="block text-[#68738b]">{busy === '/timer/start' ? 'Dispatching…' : 'Dispatch'}</span><span aria-label="Dispatch time" className="mt-1 block font-semibold">{progress?.dispatchedAt !== undefined ? time(new Date(progress.dispatchedAt).toISOString()) : job.dispatchedAt ? time(job.dispatchedAt) : busy === '/timer/start' ? '—' : waitingToLeave ? 'Start' : 'Not recorded'}</span></button>
          <button type="button" aria-label="Arrive" data-step="working" data-active={!completed && ["arrived", "in_progress"].includes(appointmentStatus)} disabled={!['en_route', 'dispatched'].includes(appointmentStatus) || completed || Boolean(busy)} onClick={() => void post('/arrive').then((ok) => { if (ok) onProgressChange({ ...progress, source: 'recorded', status: 'arrived', arrivedAt: Date.now() }); })} className="field-progress-chip min-h-14 rounded-2xl bg-[#f2f3f8] px-2 py-2 text-xs disabled:cursor-default enabled:font-semibold"><span className="block text-[#68738b]">Arrive</span><span aria-label="Arrived time" className="mt-1 block font-semibold">{progress?.arrivedAt !== undefined ? time(new Date(progress.arrivedAt).toISOString()) : job.arrivedAt ? time(job.arrivedAt) : waitingToLeave || ['en_route', 'dispatched'].includes(appointmentStatus) ? 'Not yet' : 'Not recorded'}</span></button>
          <button type="button" aria-label="Complete" data-step="complete" data-active={completed} disabled={Boolean(busy) || !canEditJobDetails || completed} onClick={() => setCompletionOpen(true)} className="field-progress-chip min-h-14 rounded-2xl bg-[#f2f3f8] px-2 py-2 text-xs disabled:cursor-default enabled:font-semibold"><span className="block text-[#68738b]">{completed ? 'Completed' : 'Complete'}</span><span aria-label="Completed time" className="mt-1 block font-semibold">{progress?.finishedAt !== undefined ? time(new Date(progress.finishedAt).toISOString()) : completed ? 'Not recorded' : 'Not yet'}</span></button>
        </div>

        {!clockedIn && waitingToLeave && <p className="mt-2 text-xs text-[#8a6a00]">Clock in for the day before dispatching.</p>}
        {anotherJobRunning && waitingToLeave && <p className="mt-2 text-xs text-[#8a6a00]">Finish your active activity before dispatching to a job: {activityDescription}. Open Timesheet or return to that job to finish it.</p>}
      </section>

      {completionOpen && <Dialog title="Complete visit" onClose={() => { if (!busy) setCompletionOpen(false); }}>
        {!continuation ? <p role="alert" className="text-sm">Unable to check remaining appointments. Refresh the job before finishing.</p> : <>
          <p className="text-base font-semibold">{continuation.hasMoreAppointments ? 'More visits scheduled' : 'No more visits scheduled'}</p>
          {continuation.hasMoreAppointments && <ul className="mt-3 space-y-2 text-sm">{continuation.appointments.map(visit => <li key={visit.id}>{dateLabel(visit.scheduledStart)} · {time(visit.scheduledStart)}–{time(visit.scheduledEnd)}</li>)}</ul>}
          {continuation.waitingOn && <p className="mt-2 text-sm">The job is waiting on {continuation.waitingOn === 'tech' ? 'another appointment' : continuation.waitingOn}. The job will stay open.</p>}
          {Boolean(job.openCrewmateCount) && <p className="mt-2 text-sm">Other crew members are still working. You can finish your own work and keep the job open.</p>}
          <div className="mt-4 space-y-3">
            {(finishVisitOnly || continuation.hasMoreAppointments) && <button type="button" disabled={Boolean(busy)} onClick={() => void completeOwnWork()} className="min-h-12 w-full rounded-xl bg-[#6b46d9] px-4 text-sm font-semibold text-white disabled:opacity-50">Complete this visit</button>}
            {!finishVisitOnly && <button type="button" aria-label="Complete job" disabled={Boolean(busy)} onClick={() => { setCompletionOpen(false); openCloseout(); if (!invoice) { setError('Wait for the invoice to load before completing the job.'); return; } void completionAction.current?.(); }} className="min-h-12 w-full rounded-xl bg-[#6b46d9] px-4 py-3 text-left text-white disabled:opacity-50"><span className="block text-sm font-semibold">Complete job</span><span className="mt-1 block text-xs">{continuation.hasMoreAppointments ? 'All work is done. Remaining unused appointments will be cancelled.' : 'All work is done. No more appointments needed.'}</span></button>}
            {!continuation.hasMoreAppointments && !continuation.waitingOn && <button type="button" disabled={Boolean(busy)} onClick={() => { setCompletionOpen(false); setRequestAppointmentOpen(true); }} className="min-h-12 w-full rounded-xl border px-4 text-sm font-semibold disabled:opacity-50">Request an additional appointment</button>}
          </div>
        </>}
        {error && <p role="alert" className="mt-3 text-sm text-red-700">{error}</p>}
      </Dialog>}

      {requestAppointmentOpen && <Dialog title="Request an additional appointment" onClose={() => { if (!busy) setRequestAppointmentOpen(false); }}>
        <p className="text-sm">Finish your work on today’s visit and send the office a request to schedule more work. The job stays open.</p>
        <label className="mt-3 block text-sm font-semibold">Work remaining<textarea aria-label="Work remaining" placeholder="What work needs to be done on the return visit?" value={appointmentReason} maxLength={2000} onChange={event => setAppointmentReason(event.target.value)} className="mt-1 block min-h-24 w-full rounded-xl border p-3 placeholder:text-slate-400" /></label>
        <p className="mt-3 text-xs text-slate-500">Add scheduling details if known.</p>
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          <label className="block text-sm font-semibold">Time needed (hours)<input type="number" inputMode="decimal" min="0.25" max="1000" step="0.25" placeholder="e.g. 2" value={returnVisitHours} onChange={event => setReturnVisitHours(event.target.value)} className="mt-1 min-h-11 w-full rounded-xl border p-3 placeholder:text-slate-400" /></label>
          <label className="block text-sm font-semibold">Helper needed?<select value={returnVisitHelper} onChange={event => setReturnVisitHelper(event.target.value)} className="mt-1 min-h-11 w-full rounded-xl border p-3"><option value="">Choose if known</option><option value="yes">Yes, helper needed</option><option value="no">No helper needed</option><option value="unsure">Not sure yet</option></select></label>
          <label className="block text-sm font-semibold">Parts ordered?<select value={returnVisitParts} onChange={event => setReturnVisitParts(event.target.value)} className="mt-1 min-h-11 w-full rounded-xl border p-3"><option value="">Choose if known</option><option value="yes">Yes</option><option value="no">Not yet</option><option value="not_needed">No parts needed</option></select></label>
          {returnVisitParts === 'yes' && <label className="block text-sm font-semibold">Parts supplier / order details<input type="text" maxLength={500} placeholder="Supplier, order number, expected arrival" value={returnVisitSupplier} onChange={event => setReturnVisitSupplier(event.target.value)} className="mt-1 min-h-11 w-full rounded-xl border p-3 placeholder:text-slate-400" /></label>}
        </div>
        <button type="button" disabled={Boolean(busy) || !appointmentReason.trim() || Boolean(returnVisitHours && (!Number.isFinite(Number(returnVisitHours)) || Number(returnVisitHours) < 0.25 || Number(returnVisitHours) > 1000))} onClick={() => void completeOwnWork(true)} className="mt-3 min-h-11 rounded-xl bg-[#6b46d9] px-4 font-semibold text-white disabled:opacity-50">Request appointment</button>
      </Dialog>}

          </>
      )}

      {instructions && <section className={card}>
        {instructions ? (
          <div className={`${well} mt-4`}>
            <p className="text-[11px] font-semibold text-[#8a8a8a]">
              Special instructions
            </p>
            <p className="mt-1 whitespace-pre-wrap break-words text-sm">{instructions}</p>
          </div>
        ) : null}
      </section>}

      {error && (
        <div
          role="alert"
          className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800"
        >
          {error}
        </div>
      )}
      {loading && !detail && (
        <div className={`${card} text-sm text-[#8a8a8a]`}>Loading job…</div>
      )}

      <FieldLocationHistory storageKey={viewStorageKey ? `${viewStorageKey}:history` : undefined} currentJobId={job.jobId} appointmentId={job.appointmentId} actingTechnicianId={actingTechnicianId} refreshToken={refreshToken} />
      {detail && <>
          <JobTool title="Equipment" openRequest={addEquipmentRequest} needsAction={equipmentOnFile.length===0 && locationEquipment!==null} onAdd={()=>setAddEquipmentRequest(value=>value+1)} storageKey={viewStorageKey ? `${viewStorageKey}:Equipment` : undefined} disabled={!canEditJobDetails} summaryDetail={<FieldTaskStatus complete={equipmentOnFile.length > 0 ? true : locationEquipment === null ? undefined : false} text={equipmentOnFile.length > 0 ? `Completed · ${equipmentOnFile.length} on file` : equipmentFailed ? "Unable to check location equipment" : locationEquipment === null ? "Checking equipment…" : "Incomplete · Add equipment"} />}><EquipmentScan
            jobId={job.jobId}
            appointmentId={job.appointmentId}
            actingTechnicianId={actingTechnicianId}
            equipment={equipmentOnFile}
            addRequest={addEquipmentRequest}
            planConnections={equipmentPlans.jobId===job.jobId ? equipmentPlans.data : null}
            linkedEquipmentIds={detail.equipment.map(item=>item.id)}
			equipmentTypes={detail?.equipmentTypes ?? []}
            disabled={Boolean(busy) || !canEditJobDetails}
            onScan={scanEquipment}
            onSaved={load}
          />

          {equipmentFailed && <p className="mx-4 mb-3 text-sm text-amber-900">Location equipment could not be checked. Refresh to try again.</p>}
          </JobTool>
          <div ref={photosRef}><JobTool title="Job photos" needsAction={photoCount===0} storageKey={viewStorageKey ? `${viewStorageKey}:Job photos` : undefined} disabled={!canEditJobDetails && !completed} summaryDetail={<FieldTaskStatus complete={typeof photoCount !== "number" ? undefined : photoCount > 0} text={photoCount === null ? "Checking photos…" : photoCount === "error" ? "Unable to check photos" : photoCount > 0 ? `Completed · ${photoCount} photo${photoCount === 1 ? "" : "s"}` : "Incomplete · Photos required"} />}><JobPhotos
            jobId={job.jobId}
            actingTechnicianId={actingTechnicianId}
            refreshToken={refreshToken}
            onCountChange={setPhotoCount}
          />
          </JobTool></div>
      </>}

      <FieldMaintenancePlans openRequest={maintenanceBuilderRequest} onEquipmentPlans={receiveEquipmentPlans} storageKey={viewStorageKey ? `${viewStorageKey}:maintenance` : undefined} key={job.jobId} jobId={job.jobId} actingQuery={actingQuery} disabled={!canEditJobDetails} refreshToken={detail} renderSignature={(onSave, busy) => <SignaturePad customerName={job.customerName} disabled={busy || !canEditJobDetails} onSave={onSave} />}>
        {(builderOpen,planVersion)=><><FieldMembershipBuilder refreshToken={planVersion} onSigned={()=>void load()} renderSignature={(onSave,busy)=><SignaturePad customerName={job.customerName} disabled={busy||!canEditJobDetails} onSave={onSave}/>} embedded visible={builderOpen} openRequest={maintenanceBuilderRequest} key={`builder:${job.jobId}:${actingTechnicianId}`} jobId={job.jobId} actingQuery={actingQuery} disabled={!canEditJobDetails} equipmentVersion={detail?.equipment} storageKey={viewStorageKey ? `${viewStorageKey}:maintenance-draft:${job.jobId}:${actingTechnicianId}` : undefined} renderEquipmentEntry={onComplete=><EquipmentScan compact jobId={job.jobId} appointmentId={job.appointmentId} actingTechnicianId={actingTechnicianId} equipment={equipmentOnFile} equipmentTypes={detail?.equipmentTypes??[]} disabled={Boolean(busy)||!canEditJobDetails} onScan={scanEquipment} onSaved={load} onComplete={onComplete}/>} /><FieldMembershipServiceCompletion onSaved={()=>void load()} jobId={job.jobId} actingQuery={actingQuery} disabled={!canEditJobDetails} refreshToken={detail}/></>}
      </FieldMaintenancePlans>
      {detail && <div>


      {(
          <>
        <div ref={estimateRef}><JobTool title="Estimate" storageKey={viewStorageKey ? `${viewStorageKey}:Estimate` : undefined} showDoneButton={false}><FieldEstimatePanel job={{...job,customerEmail:email}} actingTechnicianId={actingTechnicianId} onCurrentJobInvoiceChanged={load} refreshToken={refreshToken} /></JobTool></div>
          <div ref={invoiceRef}><JobTool title="Invoices & payment" needsAction={!fieldInvoiceReady(invoice)} storageKey={viewStorageKey ? `${viewStorageKey}:Invoices & payment` : undefined} summaryDetail={<FieldInvoiceStatus invoice={invoice} />}>
            <FieldJobTime jobId={job.jobId} appointmentId={job.appointmentId} actingTechnicianId={actingTechnicianId} crewCount={job.crewCount ?? 1} now={now} ownElapsedMs={elapsedMs} running={Boolean(jobTimerRunning) && !completed} completed={completed} />
            {<div aria-label="Job invoices" className="flex flex-wrap gap-2 p-3">{detail?.invoices.filter(item=>item.id!==invoice?.id).map(item => <div key={item.id} className={`min-h-11 rounded-xl border px-3 py-2 text-left text-sm ${invoice?.id===item.id ? 'border-black' : 'border-gray-200'}`}><span className="block font-semibold">Invoice #{item.displayNumber || item.invoiceNumber}</span><FieldInvoiceStatus invoice={item} /></div>)}</div>}
            <fieldset className="min-w-0">{<InvoiceDraft
            onOpenMaintenancePlan={()=>setMaintenanceBuilderRequest(value=>value+1)}
            key={job.jobId}
            customerEmail={email}
            invoice={invoice}
            disabled={Boolean(busy) || !canEditJobDetails}
            jobId={job.jobId}
            actingQuery={actingQuery}
            onSaved={load}
            jobTypeName={job.jobTypeName}
            jobNumber={job.jobNumber}
            onAddLine={addInvoiceLine}
            actionsTarget={invoiceActionsTarget}
          />}
          </fieldset>
      {detail && invoice && (
          <div ref={closeoutRef} tabIndex={-1} aria-label="Job closeout" className="scroll-mt-4 focus-visible:outline-2 [&>section]:shadow-none">
          <CloseoutCard
            invoiceActionsRef={setInvoiceActionsTarget}
            completionAction={completionAction}
            customerName={job.customerName}
            invoice={invoice}
            elapsedMs={elapsedMs}
			canComplete={canEditJobDetails && !finishVisitOnly}
            busy={busy}
            completed={completed}
            showPayment={showPayment}
            amount={amount}
            method={method}
            reference={reference}
            setShowPayment={setShowPayment}
            setAmount={setAmount}
            setMethod={setMethod}
            setReference={setReference}
            onSignature={(signerName, signatureDataUrl) =>
              invoice
                ? post("/signature", {
                    invoiceId: invoice.id,
                    signerName,
                    signatureDataUrl,
                  })
                : Promise.resolve(false)
            }
            onComplete={async (paymentDisposition, closeout) => {
              if (typeof photoCount !== "number" || photoCount === 0) {
                setError(photoCount === 0 ? "Add at least one job photo before completing this job." : "Wait for job photos to load, or refresh the job to check them.");
                const panel = photosRef.current?.querySelector("details");
                if (panel) panel.open = true;
                photosRef.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
                return false;
              }
              const ok = await post("/complete", { invoiceId: invoice.id, paymentDisposition, ...closeout });
              if (ok) completeJob();
              return ok;
            }}
            onPayment={async () => {
              if (!invoice) return;
              const amountCents = moneyInputCents(amount);
              if (!Number.isFinite(amountCents) || amountCents < 1) {
                setError("Enter a valid payment amount.");
                return;
              }
              if (
                await post("/payments", {
                  invoiceId: invoice.id,
                  amountCents,
                  method,
                  reference: reference || null,
                })
              ) {
                setShowPayment(false);
                setReference("");
              }
            }}
            onCardPayment={async (amountCents) => {
              if (!invoice) return;
              if (!Number.isFinite(amountCents) || amountCents < 1 || amountCents > invoice.balanceDueCents) { setError("Enter an amount from $0.01 through the remaining balance."); return; }
              if (busy) return;
              setBusy("card-payment");
              setError("");
              try {
                const response = await apiFetch(`/api/v1/workspace/field/jobs/${encodeURIComponent(job.jobId)}/invoices/${encodeURIComponent(invoice.id)}/checkout${actingQuery}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ amountCents }) });
                const payload = await response.json() as { data?: { checkout?: { url?: string | null } }; error?: { message?: string } };
                const url = payload.data?.checkout?.url;
                if (!response.ok || !url) throw new Error(payload.error?.message || "Unable to open card payment.");
                window.sessionStorage.setItem("field-checkout-job", job.jobId);
                window.location.assign(url);
              } catch (cause) { setError(cause instanceof Error ? cause.message : "Unable to open card payment."); }
              finally { setBusy(""); }
            }}
          />
        </div>
      )}
          </JobTool></div>
          <div ref={purchaseRef}><JobTool title="Purchase orders" showDoneButton={false} storageKey={viewStorageKey ? `${viewStorageKey}:Purchase orders` : undefined} disabled={!canEditJobDetails}>{canUseInventory && <TruckMaterialUse jobId={job.jobId} />}{Array.isArray(detail?.purchaseOrders) ? (
            <FieldPurchaseOrders
              invoiceId={invoice?.id}
              jobId={job.jobId}
              actingQuery={actingQuery}
              purchaseOrders={detail.purchaseOrders}
              busy={busy}
              onCreated={() => void load()}
            />
          ) : <section className={card}><h3 className="text-sm font-semibold">Purchase order</h3><form className="mt-3 space-y-2" onSubmit={(event) => { event.preventDefault(); setPoDraft(Object.fromEntries([...new FormData(event.currentTarget).entries()].map(([key, value]) => [key, String(value)]))); }}><label className="block text-sm">Supplier<input name="supplier" required className="mt-1 w-full rounded-xl border p-3" /></label><label className="block text-sm">Parts / materials<textarea name="items" required className="mt-1 w-full rounded-xl border p-3" /></label><label className="block text-sm">Quantity<input name="quantity" type="number" min="1" required className="mt-1 w-full rounded-xl border p-3" /></label><button className="min-h-11 rounded-full border px-4 text-sm font-semibold">Prepare purchase order</button>{poDraft && <p role="status" className="text-xs text-[#6b6b6b]">Draft: {poDraft.quantity} × {poDraft.items} from {poDraft.supplier}. Purchase-order submission needs connection.</p>}</form></section>}</JobTool></div>
          <section className={card} aria-label="Notes for the office">
            <h3 className="flex items-center gap-2 text-sm font-semibold"><NotesIcon /> Notes for the office</h3>
            {detail?.notes.map((item) => (
              <div
                key={item.id}
                className="mt-2 rounded-2xl border border-[#ececec] px-3 py-3 text-sm"
              >
                <p className="whitespace-pre-wrap break-words">{estimateDescription(item.body)}</p>
                <p className="mt-1 text-xs text-[#b0b0b0]">
                  {new Date(item.createdAt).toLocaleString()}
                </p>
              </div>
            ))}
            <JobTool title="Add note for the office" showDoneButton={false} storageKey={viewStorageKey ? `${viewStorageKey}:Notes` : undefined} disabled={!canEditJobDetails}>
            <textarea
              spellCheck autoCorrect="on" autoCapitalize="sentences"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={4}
              aria-label="Note for the office"
              placeholder="Add a note for the office"
              className="mt-3 min-h-24 w-full rounded-2xl border border-[#e2e2e2] p-3 text-base"
            />
            <button
              type="button"
              disabled={Boolean(busy) || !canEditJobDetails || !note.trim()}
              onClick={async event => {
                const panel=event.currentTarget.closest<HTMLDetailsElement>('details[data-job-tool]');
                if (await post("/notes", { body: note })) {
                  setNote("");
                  if(panel) {panel.open=false;panel.querySelector('summary')?.focus();}
                }
              }}
              className="mt-2 min-h-12 w-full rounded-full bg-[#ececec] text-sm font-semibold disabled:opacity-60"
            >
              Save note
            </button>
            </JobTool>
          </section>

          </>
      )}</div>}




    </article>
  );
}

function FieldTaskStatus({ complete, text }: { complete?: boolean; text: string }) {
  const Icon = complete === true ? CheckCircle2 : complete === false ? AlertCircle : Info;
  return <span className={`inline-flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-xs font-semibold ${complete === true ? "bg-emerald-50 text-emerald-800" : complete === false ? "bg-amber-50 text-amber-900" : "bg-slate-100 text-slate-600"}`}><Icon size={15} className="shrink-0" aria-hidden="true" />{text}</span>;
}

function fieldInvoiceReady(invoice?:Invoice) {
  return Boolean(invoice && !invoiceNeedsSummary(invoice) && invoice.status!=='void' && (invoicePresentation(invoice).isPaid || invoice.status!=='draft' || invoice.signedAt || invoice.hasInvoiceSignature || invoice.estimateDisplayNumber));
}
function FieldInvoiceStatus({ invoice }: { invoice?: Invoice }) {
  if (!invoice) return <FieldTaskStatus complete={false} text="Incomplete · No invoice" />;
  const ready = fieldInvoiceReady(invoice);
  return <span className="flex flex-wrap gap-1.5">
    <FieldTaskStatus complete={ready} text={ready ? "Completed" : invoiceNeedsSummary(invoice) ? "Incomplete · Invoice summary required" : "Incomplete"} />
    <InvoicePaymentBadge invoice={invoice} className="inline-flex text-xs" unpaidLabel="Balance due" />
  </span>;
}

export function JobTool({ title, children, disabled = false, summaryDetail, showDoneButton = false, storageKey, onAdd, openRequest = 0, needsAction = false }: { openRequest?:number; needsAction?:boolean; onAdd?:()=>void; storageKey?: string; title: string; children: ReactNode; disabled?: boolean;summaryDetail?:ReactNode; showDoneButton?: boolean }) {
  const panel = useRef<HTMLDetailsElement>(null);
  const [open, setOpen] = useFieldPanel(storageKey);
  useEffect(()=>{if(openRequest>0){setOpen(true);if(panel.current){panel.current.open=true;panel.current.scrollIntoView?.({block:'start',behavior:'smooth'});}}},[openRequest]);
  const close = () => {
    if (panel.current) { panel.current.open = false; setOpen(false); panel.current.querySelector('summary')?.focus(); }
  };
  return <details ref={panel} open={open} onToggle={event => setOpen(event.currentTarget.open)} className="m-3 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-job-tool>
    <summary onClick={(event) => { if (panel.current?.open) { event.preventDefault(); close(); } }} className="grid min-h-16 cursor-pointer list-none grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 px-4 py-3 text-base font-semibold">
      {title}<span className="flex shrink-0 items-center gap-1 text-xs text-sky-800">{onAdd&&<button type="button" aria-label={`Add ${title.toLowerCase()}`} disabled={disabled} onClick={event=>{event.preventDefault();event.stopPropagation();if(panel.current)panel.current.open=true;setOpen(true);onAdd();}} className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-800 hover:bg-slate-200 active:bg-slate-300 disabled:opacity-40"><Plus size={20} aria-hidden="true"/></button>}{open ? showDoneButton ? 'Done' : 'Hide' : ''}{open || (!onAdd && !needsAction) ? <ChevronDown size={18} aria-hidden="true" className={open ? "rotate-180" : ""} /> : !onAdd ? <Plus size={20} aria-label={`${title} requires attention`} /> : null}</span>
      {summaryDetail && <span className="col-span-2">{summaryDetail}</span>}
    </summary>
    <fieldset disabled={disabled} className="m-3 min-w-0 overflow-hidden rounded-2xl border border-[#dbe9ef] bg-[#f7fafc] shadow-sm">
      <legend className="sr-only">{title}</legend>
      {children}
    </fieldset>
    {showDoneButton && <div className="border-t border-[#dbe9ef] p-3"><button type="button" className="min-h-11 w-full rounded-full border border-[var(--accent)] text-sm font-semibold text-[var(--accent)]" onClick={close}>Done</button></div>}
  </details>;
}

type FieldLine = { clientKey?: string; itemName?: string | null; itemDescription?: string | null; costCents?: number | null; imageUrl?: string | null; imageContentUrl?: string | null; id?: string; priceBookItemId?: string | null; description: string; quantity: number; unitPriceCents: number };
function FieldEstimateItem({ line, card, descriptionLabel, onDescriptionChange, onRemove, onMoveUp, onMoveDown, children }: { line: FieldLine; card: ReactNode; descriptionLabel: string; onDescriptionChange: (description: string) => void; onRemove: () => void; onMoveUp?: () => void; onMoveDown?: () => void; children: ReactNode }) {
  const [editing, setEditing] = useState(!line.description);
  return <div className="relative rounded-xl border bg-white p-3">
    <button type="button" aria-label="Remove line" title="Remove item" onClick={onRemove} className="absolute right-1 top-1 flex h-11 w-11 items-center justify-center rounded-full text-red-600"><X size={20} aria-hidden="true" /></button>
    <div className="pr-10">{editing ? <div className="py-2">
      {line.itemName && <p className="text-sm font-semibold">{commercialLinePresentation(line.itemName,line.description,line.itemDescription).name}</p>}
      <DictationButton label="estimate item description" value={line.description} maxLength={500} onChange={onDescriptionChange} />
      <EstimateDescription aria-label={descriptionLabel} spellCheck autoCorrect="on" autoCapitalize="sentences" value={line.description} onChange={event => onDescriptionChange(event.target.value)} className="mt-1 w-full rounded border p-2 text-sm" />
    </div> : card}</div>
    {editing && <div className="space-y-2">{children}</div>}
    <div className="flex flex-wrap items-center gap-2"><button type="button" aria-label={`Move ${descriptionLabel} up`} disabled={!onMoveUp} onClick={onMoveUp} className="inline-flex min-h-11 items-center gap-1 rounded border px-2 text-xs disabled:opacity-40"><ArrowUp size={16} />Up</button><button type="button" aria-label={`Move ${descriptionLabel} down`} disabled={!onMoveDown} onClick={onMoveDown} className="inline-flex min-h-11 items-center gap-1 rounded border px-2 text-xs disabled:opacity-40"><ArrowDown size={16} />Down</button></div>
    <button type="button" aria-expanded={editing} className="min-h-11 text-xs font-semibold underline" onClick={() => setEditing(value => !value)}>{editing ? 'Save' : 'Edit item'}</button>
  </div>;
}
function FieldEstimateTotals({ subtotal, tax, total }: { subtotal: number; tax?: number; total?: number }) {
  return <dl aria-label="Estimate totals" className="mt-3 space-y-1 border-t pt-3 text-sm">
    <div className="flex justify-between"><dt>Subtotal</dt><dd>{money(subtotal)}</dd></div>
    <div className="flex justify-between"><dt>Sales tax</dt><dd>{tax === undefined ? 'Calculated on save' : money(tax)}</dd></div>
    <div className="flex justify-between font-semibold"><dt>Total</dt><dd>{total === undefined ? 'Calculated on save' : money(total)}</dd></div>
  </dl>;
}

type FieldOption = { taxCents?: number; subtotalCents?: number; id?: string; label: string; description?: string | null; isRecommended?: boolean; totalCents: number; status?: string; lines: FieldLine[] };
type FieldApproval = { id: string; optionId: string; method: 'onsite' | 'phone' | 'remote'; customerName?: string | null; approvedAmountCents: number; jobId?: string | null; invoiceId?: string | null; fulfillment?: string | null; fulfillmentId?:string;canCollectDeposit?:boolean|number;invoiceTotalCents?:number;invoiceBalanceDueCents?:number };
type FieldEstimate = { id: string; revision: number; title: string; summary?: string | null; terms?: string | null; selectionMode: 'single' | 'multi'; status?: string; options: FieldOption[]; approvals: FieldApproval[] };
type OptionDraft = { id?: string; label: string; description: string; isRecommended: boolean; lines: FieldLine[] };
type FieldPricebookItem = { taxClass?: string; materialMarkupBps?: number | null; costCents?: number | null; imageUrl?: string | null; imageContentUrl?: string | null; id: string; name: string; description: string | null; priceCents: number; unitType: string; isDeduction?: boolean; modifierAmountType?: "fixed" | "percentage" | null; modifierBasisPoints?: number | null };

const blankOption = (label = 'Recommended work'): OptionDraft => ({ label, description: '', isRecommended: false, lines: [] });
function fieldEstimatePrice(document: FieldEstimate) {
  if (document.approvals.length) return money(document.approvals.reduce((sum, approval) => sum + approval.approvedAmountCents, 0));
  const totals = document.options.map(option => option.totalCents);
  if (!totals.length) return money(0);
  if (document.selectionMode === 'single') {
    const min = Math.min(...totals), max = Math.max(...totals);
    return min === max ? money(min) : `${money(min)} – ${money(max)}`;
  }
  return money(totals.reduce((sum, total) => sum + total, 0));
}

export function FieldEstimateDelivery({ estimate, dirty, actingQuery = "", customerEmail, jobTypes = [], onPresent, onActionSelected }: {
  estimate: FieldEstimate | null; dirty: boolean; actingQuery?: string; customerEmail?: string | null;
  jobTypes?: Array<{id:string;name:string}>; onPresent?: () => void; onActionSelected?: () => void;
}) {
  const features = useFieldWorkflow();
  const [chooserOpen, setChooserOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [recipient, setRecipient] = useState('');
  const [jobTypeId, setJobTypeId] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sent, setSent] = useState('');
  const sendLock = useRef(false);
  const [optionId, setOptionId] = useState(estimate?.options[0]?.id ?? '');
  const selectedOptionId = estimate?.options.find(option => option.id === optionId)?.id ?? estimate?.options[0]?.id ?? '';
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const pdfUrls = useRef<string[]>([]);
  useEffect(() => () => { pdfUrls.current.forEach(url => URL.revokeObjectURL(url)); }, []);
  useEffect(() => { setChooserOpen(false); setEmailOpen(false); setSent(''); setPdfError(''); }, [estimate?.id, dirty]);
  async function openPdf() {
    if (!estimate || dirty || pdfBusy) return;
    const tab = window.open('', '_blank');
    if (!tab) { setPdfError('Allow new tabs to print the estimate PDF.'); return; }
    tab.opener = null;
    setPdfBusy(true); setPdfError('');
    try {
      const optionQuery = estimate.options.length > 1 ? `${actingQuery ? '&' : '?'}optionId=${encodeURIComponent(selectedOptionId)}` : '';
      const response = await apiFetch(`/api/v1/workspace/field/estimates/${encodeURIComponent(estimate.id)}/pdf${actingQuery}${optionQuery}`);
      if (!response.ok || !response.headers.get('content-type')?.includes('application/pdf')) throw new Error('Estimate PDF could not be opened.');
      const url = URL.createObjectURL(await response.blob());
      pdfUrls.current.push(url);
      tab.onload = () => { tab.print(); };
      tab.location.href = url;
      setSent('Estimate PDF opened for printing.');
      setChooserOpen(false); onActionSelected?.();
    } catch (cause) {
      tab.close();
      setPdfError(cause instanceof Error ? cause.message : 'Estimate PDF could not be opened.');
    } finally { setPdfBusy(false); }
  }
  async function sendEmail() {
    if (!estimate || dirty || sendLock.current) return;
    sendLock.current = true; setSending(true); setSendError(''); setSent('');
    try {
      if (!jobTypeId) throw new Error('Choose the job type for approved work.');
      const response = await apiFetch(`/api/v1/workspace/field/estimates/${encodeURIComponent(estimate.id)}/send${actingQuery}`, {method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({recipient:recipient.trim(),jobTypeId})});
      const body = await response.json();
      if (!response.ok || body.data?.deliveryError) throw new Error(body.error?.message || 'The estimate could not be sent. Please try again.');
      setSent(`Estimate emailed to ${body.data.recipient}.`); setEmailOpen(false);
    } catch (cause) { setSendError(cause instanceof Error ? cause.message : 'The estimate could not be sent. Please try again.'); }
    finally { sendLock.current = false; setSending(false); }
  }
  if (!estimate || dirty) return null;
  const actionClass = 'min-h-12 rounded-xl border border-sky-200 bg-sky-50 px-4 text-left text-sm font-semibold text-sky-900 hover:bg-sky-100 disabled:opacity-50';
  return <section className={`${card} mb-3`} aria-label="Estimate delivery">
    <p className="mb-2 text-xs text-slate-600">Saved · {estimate.title}</p>
    <button type="button" disabled={sending || pdfBusy} onClick={() => {setChooserOpen(true);setPdfError('');setSent('');}} className="min-h-11 w-full rounded-full bg-black px-4 text-sm font-semibold text-white disabled:opacity-50">Present</button>
    {sent && <p role="status" className="mt-2 text-sm text-emerald-700">{sent}</p>}
    {chooserOpen && <Dialog title="Present or share estimate" width="max-w-[398px]" onClose={() => {if(!pdfBusy)setChooserOpen(false);}}>
      <p className="mb-3 text-sm text-slate-600">{estimate.title} · Choose how to show the saved estimate.</p>
      {features.has('estimate_pdf') && estimate.options.length > 1 && <label className="mb-3 block text-sm font-semibold">PDF option<select value={selectedOptionId} onChange={event => setOptionId(event.target.value)} className="mt-1 min-h-11 w-full rounded-xl border p-2">{estimate.options.map(option => <option key={option.id} value={option.id}>{estimate.options[0].id === option.id && /^recommended(?: work)?$/i.test(option.label.trim()) ? estimate.title : option.label}</option>)}</select></label>}
      <div className="grid grid-cols-2 gap-2">
        {onPresent && <button type="button" disabled={pdfBusy} onClick={() => {setChooserOpen(false);onPresent();}} className={`${actionClass} col-span-2`}>Present to customer</button>}
        {features.has('estimate_pdf') && <button type="button" disabled={pdfBusy} onClick={() => void openPdf()} className={actionClass}>Print</button>}
        <button type="button" disabled aria-describedby="estimate-text-unavailable" className={actionClass}>Text</button>
        <button type="button" disabled={pdfBusy || !features.has('estimate_email')} onClick={() => {setEmailOpen(true);setChooserOpen(false);setRecipient(customerEmail?.trim() ?? '');setSendError('');setSent('');onActionSelected?.();}} className={actionClass}>Email</button>
      </div>
      <p id="estimate-text-unavailable" className="mt-3 text-xs text-slate-500">Text delivery is not available yet.</p>
      {!features.has('estimate_email') && <p className="mt-2 text-xs text-slate-500">Email requires the updated API.</p>}
      {pdfBusy && <p role="status" className="mt-2 text-sm">Preparing PDF…</p>}
      {pdfError && <p role="alert" className="mt-2 text-sm text-rose-700">{pdfError}</p>}
    </Dialog>}
    {emailOpen && <Dialog title="Email estimate" width="max-w-[398px]" onClose={() => {if(!sending)setEmailOpen(false);}}>
      <p className="text-sm font-semibold">{estimate.title}</p>
      <p className="mt-1 text-xs text-slate-500">Saved revision {estimate.revision}</p>
      <form className="mt-4 space-y-3" onSubmit={event => {event.preventDefault();void sendEmail();}}>
        <label className="block text-sm">Customer email<input disabled={sending} required type="email" value={recipient} onChange={event => setRecipient(event.target.value)} className="mt-1 min-h-11 w-full rounded-xl border p-3" /></label>
        <label className="block text-sm">Approved work job type<select aria-label="Approved work job type" required disabled={sending} value={jobTypeId} onChange={event => setJobTypeId(event.target.value)} className="mt-1 min-h-11 w-full rounded-xl border p-3"><option value="">Choose job type</option>{jobTypes.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}</select></label>
        <label className="flex gap-2 text-sm"><input type="checkbox" checked disabled readOnly />Attach estimate PDF</label>
        <p className="text-xs text-slate-500">Email includes a secure link to approve the estimate online. The office is notified when the customer accepts.</p>
        {sendError && <p role="alert" className="text-sm text-rose-700">{sendError}</p>}
        <button type="submit" disabled={sending || !features.has('estimate_email') || !jobTypeId} className="min-h-11 w-full rounded-full bg-black text-sm font-semibold text-white disabled:opacity-50">{sending ? 'Sending…' : 'Send estimate'}</button>
      </form>
    </Dialog>}
  </section>;
}

function FieldEstimatePanel({ job, actingTechnicianId, onCurrentJobInvoiceChanged, refreshToken }: { job: WorkItem & { jobId: string }; actingTechnicianId: string; onCurrentJobInvoiceChanged: () => Promise<void>; refreshToken: number }) { return <FieldEstimateWorkspace job={job} actingTechnicianId={actingTechnicianId} onCurrentJobInvoiceChanged={onCurrentJobInvoiceChanged} refreshToken={refreshToken} />; }

export function FieldEstimateWorkspace({ job, appointment, estimateId: initialEstimateId, locationAppointmentId, actingTechnicianId = '', onCurrentJobInvoiceChanged, refreshToken = 0 }: { job?: WorkItem & { jobId: string }; appointment?: WorkItem; estimateId?: string; locationAppointmentId?: string; actingTechnicianId?: string; onCurrentJobInvoiceChanged?: () => Promise<void>; refreshToken?: number }) {
  const features=useFieldWorkflow();
  const sourceJobId = job?.jobId;
  const fieldQueryParams = new URLSearchParams({ ...(actingTechnicianId ? { technicianId: actingTechnicianId } : {}), ...(locationAppointmentId ? { locationAppointmentId } : {}) }).toString();
  const fieldQuery = fieldQueryParams ? `?${fieldQueryParams}` : '';
  const fieldQuerySuffix = fieldQuery;
  const [estimate, setEstimate] = useState<FieldEstimate | null>(null);
  const [catalogCosts, setCatalogCosts] = useState<Record<string, number | null>>({});
  const [catalogImages, setCatalogImages] = useState<Record<string, string | null>>({});
  const [detailsOpen, setDetailsOpen] = useState(Boolean(initialEstimateId));
  const [deletingEstimate, setDeletingEstimate] = useState<FieldEstimate | null>(null);
  const [catalogItems, setCatalogItems] = useState<Record<string, FieldPricebookItem>>({});
  useEffect(() => {
    if (!sourceJobId) return;
    const controller = new AbortController();
    void apiFetch(`/api/v1/workspace/field/jobs/${encodeURIComponent(sourceJobId)}/pricebook?browse=true${fieldQuery ? '&' + fieldQuery.slice(1) : ''}`, { signal: controller.signal }).then(async response => {
      if (!response.ok) return;
      const body = await response.json();
      if (!controller.signal.aborted) {
        const items = body.data.items ?? [];
        setCatalogCosts(Object.fromEntries(items.map((item: { id: string; costCents: number | null }) => [item.id, item.costCents])));
        setCatalogImages(Object.fromEntries(items.map((item: { id: string; imageContentUrl?: string | null; imageUrl?: string | null }) => [item.id, item.imageContentUrl || item.imageUrl || null])));
      }
      if (!controller.signal.aborted) setCatalogItems(Object.fromEntries((body.data.items ?? []).map((item: FieldPricebookItem) => [item.id, item])));
    }).catch(() => {});
    return () => controller.abort();
  }, [sourceJobId, fieldQuery]);
  const lineCard = (line: FieldLine) => <span className="flex min-w-0 items-center gap-3 py-2">
    <PriceBookThumb src={line.imageContentUrl || line.imageUrl || catalogItems[line.priceBookItemId ?? ""]?.imageContentUrl || catalogItems[line.priceBookItemId ?? ""]?.imageUrl || null} name={line.itemName || line.description || "Item"} fit="contain" className="h-16 w-16 shrink-0 rounded-lg" />
    <span className="min-w-0 flex-1"><span className="block text-sm font-semibold">{commercialLinePresentation(line.itemName,line.description,line.itemDescription).name || line.description || 'New item'}</span>{(line.itemName || line.itemDescription) && commercialLinePresentation(line.itemName,line.description,line.itemDescription).description && <span className="mt-1 block whitespace-pre-wrap text-sm text-[#555]">{estimateDescription(commercialLinePresentation(line.itemName,line.description,line.itemDescription).description)}</span>}<span className="mt-1 block text-xs text-[#6b6b6b]">Qty {line.quantity}</span></span>
    <span className="shrink-0 text-sm font-semibold">{money(line.quantity * line.unitPriceCents)}</span>
  </span>;
  const [estimateDocuments, setEstimateDocuments] = useState<FieldEstimate[]>([]);
  const [presenting, setPresenting] = useState(false);
  const [building, setBuilding] = useState(false);
  const [draftDirty, setDraftDirty] = useState(false);
  const [title, setTitle] = useState(''); const [summary, setSummary] = useState(''); const [terms, setTerms] = useState('');
  const [mode, setMode] = useState<'single' | 'multi'>('multi');
  const [options, setOptions] = useState<OptionDraft[]>([blankOption()]);
  const [customerName, setCustomerName] = useState(job?.customerName ?? appointment?.customerName ?? '');
  const [selected, setSelected] = useState<string[]>([]); const [jobTypes, setJobTypes] = useState<Array<{ id: string; name: string }>>([]);
  const [jobTypeId, setJobTypeId] = useState(''); const [futureJobLabel, setFutureJobLabel] = useState('');
  const [saleSetupOpen, setSaleSetupOpen] = useState(false);
  const [approvalKey, setApprovalKey] = useState(() => crypto.randomUUID());
  const [deposit, setDeposit] = useState<{ fulfillmentId: string; invoiceId: string; totalCents:number; balanceDueCents:number } | null>(null);

  const [collapsedOptions,setCollapsedOptions] = useState<number[]>([]);
  const [duplicatePickerOpen, setDuplicatePickerOpen] = useState(false);
  const [pricebookTarget, setPricebookTarget] = useState<number | null>(null);
  const [estimatePickerKind,setEstimatePickerKind] = useState<"service" | "equipment" | "material" | "deductions" | "rebates" | "discounts" | undefined>();
  const [replacementLine, setReplacementLine] = useState<number | null>(null);
  const [templatePicker, setTemplatePicker] = useState(false);
  const [templatePreview, setTemplatePreview] = useState<{ template: EstimateTemplateChoice; preview: TemplatePreview } | null>(null);
  const [confirmation, setConfirmation] = useState<{ message: string; proceed: () => void } | null>(null);
  const [busy, setBusy] = useState(false); const [error, setError] = useState(''); const [notice, setNotice] = useState('');
  const canvas = useRef<HTMLCanvasElement>(null); const drawing = useRef(false);
  const hydrate = (document: FieldEstimate) => { setCollapsedOptions([]); setEstimate(document); setTitle(document.title); setSummary(estimateDescription(document.summary)); setTerms(document.terms ?? ''); setMode(document.selectionMode); setOptions(document.options.map((o) => ({ id: o.id, label: o.label, description: o.description ?? '', isRecommended: o.isRecommended ?? false, lines: o.lines.map(line=>({...line,clientKey:line.id ?? crypto.randomUUID(),description:estimateDescription(line.description)})) }))); setDraftDirty(false); };
  const load = async (preferredId = estimate?.id) => {
    try {
      if (initialEstimateId) { const r = await apiFetch(`/api/v1/workspace/field/estimates/${encodeURIComponent(initialEstimateId)}${fieldQuerySuffix}`); const b = await r.json(); if (!r.ok) throw new Error(b.error?.message || 'Unable to load estimate'); setEstimateDocuments([b.data.document]); hydrate(b.data.document); return; }
      if (!sourceJobId) return;
      const r = await apiFetch(`/api/v1/workspace/field/jobs/${encodeURIComponent(sourceJobId)}/estimates${fieldQuerySuffix}`); const b = await r.json(); if (!r.ok) throw new Error(b.error?.message || 'Unable to load estimates'); const documents = (b.data.documents ?? []) as FieldEstimate[]; setEstimateDocuments(documents); const draft = documents.find((d) => d.status === 'draft'); const pending = documents.find((d) => d.approvals.some((a) => !a.jobId)); const active = documents.find(d=>d.id === (preferredId ?? initialEstimateId)) ?? pending ?? draft ?? documents[0]; if (active) hydrate(active);
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Unable to load estimate'); }
  };
  useEffect(() => {
    void load();
    void apiFetch('/api/v1/workspace/field/job-types')
      .then(async (response) => response.ok ? response.json().catch(() => null) : null)
      .then((payload) => setJobTypes(payload?.data?.items ?? []))
      .catch(() => undefined);
  }, [initialEstimateId, sourceJobId, actingTechnicianId]);
  useOnFieldRefresh(refreshToken, () => {
    if (draftDirty) return;
    void load();
  });
  const updateOption = (index: number, change: Partial<OptionDraft>) => { setDraftDirty(true); setOptions((current) => current.map((option, i) => i === index ? { ...option, ...change } : option)); };
  const updateLine = (optionIndex: number, lineIndex: number, change: Partial<FieldLine>) => { setDraftDirty(true); setOptions((current) => current.map((option, i) => i !== optionIndex ? option : { ...option, lines: option.lines.map((line, j) => j === lineIndex ? { ...line, ...change } : line) })); };
  // Structured reads include transport-only fields such as optionId.  Do not
  // send those back into the strict draft schema.
  const content = () => ({ title, summary: summary.trim() || null, terms: terms || null, selectionMode: mode, options: options.map((option, optionIndex) => ({
    // Existing line IDs are only valid within their existing option. Preserve
    // that parent option ID on draft edits, while new options remain ID-less.
    ...(option.id ? { id: option.id } : {}),
    label: options.length === 1 || (optionIndex === 0 && /^recommended(?: work)?$/i.test(option.label.trim())) ? title.trim() : option.label,
    description: option.description || null,
    isRecommended: option.isRecommended,
    lines: option.lines.map(({ id, priceBookItemId, description, quantity, unitPriceCents, costCents }) => ({ ...(id ? { id } : {}), ...(priceBookItemId ? { priceBookItemId } : {}), description, quantity, unitPriceCents, ...(features.has("estimate_costs") && costCents !== undefined ? {costCents} : {}) })),
  })) });
  const validate = () => title.trim() && options.length && options.every((option) => (options.length === 1 || option.label.trim()) && option.lines.length && option.lines.every((line) => line.description.trim() && line.quantity > 0 && Number.isInteger(line.unitPriceCents)) && option.lines.reduce((sum, line) => sum + Math.round(line.quantity * line.unitPriceCents), 0) >= 0);
  const saveDraft = async (reviewAfterSave = false) => {
    if (!validate()) return setError('Add a title, option names, and valid pricebook items.'); if (!sourceJobId && !estimate) return;
    setBusy(true); setError(''); try {
      const path = estimate ? `/api/v1/workspace/field/estimates/${encodeURIComponent(estimate.id)}${fieldQuery}` : `/api/v1/workspace/field/jobs/${encodeURIComponent(sourceJobId!)}/estimates${fieldQuery}`;
      const body = estimate ? { ...content(), revision: estimate.revision } : content();
      const r = await apiFetch(path, { method: estimate ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }); const b = await r.json(); if (!r.ok) throw new Error(b.error?.message || 'Unable to save estimate'); setEstimateDocuments((current) => [b.data.document, ...current.filter((document) => document.id !== b.data.document.id)]); hydrate(b.data.document); setPresenting(reviewAfterSave); setDetailsOpen(reviewAfterSave); setBuilding(false); setSelected([]); await onCurrentJobInvoiceChanged?.();
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Unable to save estimate'); } finally { setBusy(false); }
  };
  const reviewEstimate = () => {
    if (estimate && !draftDirty) {
      setPresenting(true); setDetailsOpen(false);
      return;
    }
    void saveDraft(true);
  };
  const createAnotherEstimate = () => {
    setCollapsedOptions([]);setBuilding(true); setDetailsOpen(true); setEstimate(null); setPresenting(false); setDraftDirty(false); setTitle(''); setSummary(''); setTerms(''); setMode('multi'); setOptions([blankOption()]); setSelected([]); setJobTypeId(''); setFutureJobLabel(''); setDeposit(null); setError('');
  };
  const instantiateTemplate = async (template: EstimateTemplateChoice) => {
    if (!sourceJobId) return;
    setBusy(true); setError('');
    try {
      const separator = fieldQuery ? '&' : '?';
      const response = await apiFetch(`/api/v1/workspace/field/jobs/${encodeURIComponent(sourceJobId)}/estimate-templates/${encodeURIComponent(template.id)}/instantiate${fieldQuery}${separator}revision=${encodeURIComponent(String(template.revision))}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ revision: template.revision }) });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error?.message || 'Unable to use template');
      setEstimateDocuments((current) => [payload.data.document, ...current.filter((document) => document.id !== payload.data.document.id)]);
      hydrate(payload.data.document); setDetailsOpen(true); setPresenting(false); setSelected([]); setTemplatePicker(false); await onCurrentJobInvoiceChanged?.();
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Unable to use template'); } finally { setBusy(false); }
  };
  const useTemplate = async (template: EstimateTemplateChoice) => {
    if (!sourceJobId) return;
    setBusy(true); setError('');
    try {
      const separator = fieldQuery ? '&' : '?';
      const response = await apiFetch(`/api/v1/workspace/field/jobs/${encodeURIComponent(sourceJobId)}/estimate-templates/${encodeURIComponent(template.id)}/preview${fieldQuery}${separator}revision=${encodeURIComponent(String(template.revision))}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ revision: template.revision }) });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error?.message || 'Unable to preview template');
      if (template.sourceKind === 'package') { setTemplatePreview({ template, preview: payload.data as TemplatePreview }); setTemplatePicker(false); }
      else await instantiateTemplate(template);
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Unable to preview template'); } finally { setBusy(false); }
  };
  const startEstimate = (proceed: () => void) => { if (draftDirty) setConfirmation({ message: 'Discard unsaved estimate edits and start a new estimate?', proceed }); else proceed(); };
  const confirmationDialog = <>{confirmation && <Dialog title="Confirm estimate change" onClose={() => setConfirmation(null)} width="max-w-[398px]"><p className="text-sm">{confirmation.message}</p><div className="mt-4 flex flex-wrap gap-2"><button type="button" className="min-h-11 rounded-full border px-4" onClick={() => setConfirmation(null)}>Cancel</button><button type="button" className="min-h-11 rounded-full bg-black px-4 text-white" onClick={() => { const proceed = confirmation.proceed; setConfirmation(null); proceed(); }}>Continue</button></div></Dialog>}{deletingEstimate && <Dialog title="Delete estimate?" onClose={() => { if (!busy) setDeletingEstimate(null); }} width="max-w-[398px]"><p className="text-sm">Are you sure you want to delete the estimate “{deletingEstimate.title}”?</p>{error && <p role="alert" className="mt-2 text-sm text-rose-700">{error}</p>}<div className="mt-4 flex gap-3"><button type="button" disabled={busy} onClick={() => setDeletingEstimate(null)} className="min-h-11 rounded-full border px-4">No, keep estimate</button><button type="button" disabled={busy} onClick={() => void deleteDraft(deletingEstimate)} className="min-h-11 rounded-full bg-rose-700 px-4 text-white">{busy ? 'Deleting…' : 'Yes, delete estimate'}</button></div></Dialog>}</>;
  const estimateCreation = sourceJobId ? <section className={`${card} mb-3`}>
    <p className="mb-3 text-base font-semibold">{estimate ? 'Create a separate estimate' : 'Build an estimate'}</p>
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <button type="button" aria-label="Build from templates" disabled={busy} onClick={() => startEstimate(() => setTemplatePicker(true))} className="flex min-h-24 items-center gap-3 rounded-2xl border border-slate-200 bg-sky-50 p-4 text-left text-sky-950 hover:bg-sky-100 active:bg-sky-200 disabled:opacity-50"><span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white text-sky-800"><LayoutTemplate size={26} aria-hidden="true"/></span><span><strong className="block text-base">Build from templates</strong><span className="mt-1 block text-sm text-slate-600">Start with a saved service package</span></span></button>
      <button type="button" aria-label="Build from scratch" disabled={busy} onClick={() => startEstimate(() => { createAnotherEstimate(); requestAnimationFrame(() => document.querySelector<HTMLInputElement>('[aria-label="Estimate title"]')?.focus()); })} className="flex min-h-24 items-center gap-3 rounded-2xl border border-slate-200 bg-sky-50 p-4 text-left text-sky-950 hover:bg-sky-100 active:bg-sky-200 disabled:opacity-50"><span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white text-sky-800"><FilePlus2 size={26} aria-hidden="true"/></span><span><strong className="block text-base">Build from scratch</strong><span className="mt-1 block text-sm text-slate-600">Create an estimate and choose your items</span></span></button>
    </div>
    {templatePicker && <EstimateTemplatePicker visual busy={busy} error={error} endpoint={`/api/v1/workspace/field/jobs/${encodeURIComponent(sourceJobId)}/estimate-templates${fieldQuery}`} onClose={() => setTemplatePicker(false)} onSelect={(template) => void useTemplate(template)} />}
    {templatePreview && <EstimatePackageReview preview={templatePreview.preview} busy={busy} onBack={() => setTemplatePreview(null)} onContinue={() => { const pending = templatePreview.template; setTemplatePreview(null); void instantiateTemplate(pending); }} />}
  </section> : null;
  const estimateSwitcher = estimateDocuments.length > 0 ? <section aria-label="Saved estimates" className="mb-3 rounded-2xl border border-[#e2e2e2] bg-white p-3">
    <p className="text-xs font-semibold uppercase tracking-wide text-[#6b6b6b]">Choose an estimate on this job</p>
    <div className="mt-2 space-y-2">{estimateDocuments.map(document => {
      const active = estimate?.id === document.id && (detailsOpen || presenting);
      const expanded = estimate?.id === document.id && detailsOpen;
      const open = () => {
        if (estimate?.id !== document.id) { hydrate(document); setSelected([]); setDeposit(null); }
        setBuilding(false); setDetailsOpen(!expanded); setPresenting(false); setError('');
      };
      return <div key={document.id} className={`flex items-center rounded-xl border ${active ? 'border-slate-300 bg-sky-50 text-slate-900' : 'border-[#d9d9d9] bg-white'}`}><button type="button" aria-expanded={expanded} aria-current={active ? "true" : undefined} disabled={busy} onClick={() => {
        if (draftDirty && estimate?.id !== document.id) setConfirmation({ message: 'Discard unsaved estimate edits and open this estimate?', proceed: open });
        else open();
      }} className="flex min-h-14 min-w-0 flex-1 items-center justify-between gap-3 rounded-xl px-3 py-3 text-left text-sm">
        <span className="min-w-0"><span id={`saved-estimate-title-${document.id}`} className="block break-words font-semibold">{document.title}</span>{active&&<span className="mt-2 inline-block rounded-full bg-sky-100 px-2 py-1 text-xs font-semibold text-sky-900">Current estimate</span>}<span className="mt-1 block text-xs opacity-75">{document.status === 'draft' ? 'Saved · Awaiting approval' : estimatePresentation({ status: document.status ?? 'draft', optionsManaged: true, approvalCount: document.approvals.length, pendingApprovalCount: document.approvals.filter(approval => !approval.jobId).length })}</span></span>
        <span className="shrink-0 text-right font-semibold">{fieldEstimatePrice(document)}<span aria-hidden="true" className="ml-2">{expanded ? '⌃' : '⌄'}</span></span>
      </button>{features.has('estimate_delete') && document.status === 'draft' && !document.approvals.length && <button type="button" aria-label="Delete estimate" aria-describedby={`saved-estimate-title-${document.id}`} title={`Delete ${document.title}`} disabled={busy} onClick={() => {setError('');setDeletingEstimate(document);}} className="mr-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-red-600 hover:bg-red-50 focus-visible:outline-2 focus-visible:outline-red-600"><X aria-hidden="true" size={20} /></button>}</div>;
    })}</div>
  </section> : null;
  const moveLine = (optionIndex: number, lineIndex: number, direction: number) => {
    setDraftDirty(true);
    setOptions(current => current.map((option,index) => {
      if(index!==optionIndex) return option;
      const lines=[...option.lines], target=lineIndex+direction;
      if(target<0 || target>=lines.length) return option;
      [lines[lineIndex],lines[target]]=[lines[target],lines[lineIndex]];
      return {...option,lines};
    }));
  };
  const removeLine = (optionIndex: number, lineIndex: number) => { setDraftDirty(true); setOptions((current) => current.map((option, index) => index !== optionIndex ? option : { ...option, lines: option.lines.filter((_, index) => index !== lineIndex) })); };
  const deleteDraft = async (document: FieldEstimate) => {
    setBusy(true);setError('');
    try {const response=await apiFetch(`/api/v1/workspace/field/estimates/${encodeURIComponent(document.id)}${fieldQuery}`,{method:'DELETE',headers:{'Content-Type':'application/json'},body:JSON.stringify({revision:document.revision})});const body=await response.json();if(!response.ok)throw new Error(body.error?.message || 'Could not delete estimate.');setEstimateDocuments(current=>current.filter(saved=>saved.id!==document.id));if(estimate?.id===document.id){createAnotherEstimate();setBuilding(false);}setDeletingEstimate(null);setNotice('Estimate deleted.');}catch(cause){setError(cause instanceof Error ? cause.message : 'Could not delete estimate.');}finally{setBusy(false);}
  };
  const duplicateOption = (optionIndex: number) => {
    setDraftDirty(true);
    setOptions(current => {
      const source = current[optionIndex];
      return [...current.map(option => current.length === 1 ? {...option, label: title.trim() || option.label} : option), { label: `${current.length === 1 ? title.trim() || source.label : source.label} (copy)`, description: source.description, isRecommended: false,
        lines: source.lines.map(({ id: _id, ...line }) => ({ ...line, clientKey:crypto.randomUUID() })) }];
    });
  };
  const removeOption = (optionIndex: number) => { setDraftDirty(true); setOptions((current) => current.filter((_, index) => index !== optionIndex)); };
  const clearOption = (optionIndex: number) => { setDraftDirty(true); setOptions((current) => current.map((option, index) => index === optionIndex ? blankOption(option.label || 'Recommended work') : option)); };
  const addPricebookItem = (optionIndex: number, item: FieldPricebookItem) => {
    const deductionBaseCents = options[optionIndex].lines.reduce((sum, line) => sum + Math.max(0, Math.round(line.quantity * line.unitPriceCents)), 0);
    const priceCents = item.isDeduction && item.modifierAmountType === "percentage" && item.modifierBasisPoints != null
      ? Math.round(deductionBaseCents * item.modifierBasisPoints / 10_000)
      : Math.abs(item.priceCents);
    const previous = replacementLine === null ? null : options[optionIndex].lines[replacementLine];
    const line: FieldLine = { clientKey:previous?.clientKey ?? crypto.randomUUID(), ...(previous?.id ? {id:previous.id} : {}), priceBookItemId:item.id, itemName:item.name, itemDescription:estimateDescription(item.description), costCents:item.costCents, imageUrl:item.imageUrl, imageContentUrl:item.imageContentUrl, description:estimateDescription(item.description) || item.name, quantity:previous?.quantity ?? 1, unitPriceCents:item.isDeduction ? -priceCents : item.priceCents };
    updateOption(optionIndex,{lines: replacementLine === null ? [...options[optionIndex].lines,line] : options[optionIndex].lines.map((entry,index)=>index === replacementLine ? line : entry)});
  };
  const pendingApprovals = estimate?.approvals.filter((approval) => !approval.jobId) ?? [];
  const approvedTotal = estimate?.options.filter((option) => selected.includes(option.id!)).reduce((sum, option) => sum + option.totalCents, 0) ?? 0;
  const sign = async () => {
    if (!estimate || !selected.length || !canvas.current || !customerName.trim()) return setError('Choose the approved work, customer name, and signature.');
    setBusy(true); setError(''); try { const r = await apiFetch(`/api/v1/workspace/field/estimates/${estimate.id}/approve${fieldQuery}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ revision: estimate.revision, optionIds: selected, method: 'onsite', customerName, signatureDataUrl: opaqueSignatureJpeg(canvas.current), idempotencyKey: approvalKey }) }); const b = await r.json(); if (!r.ok) throw new Error(b.error?.message || 'Approval failed'); const updated = await apiFetch(`/api/v1/workspace/field/estimates/${encodeURIComponent(estimate.id)}${fieldQuery}`); const payload = await updated.json(); if (!updated.ok || !payload.data?.document) throw new Error('Approval saved. Retry to reopen the approved job setup.'); hydrate(payload.data.document); setEstimateDocuments(current=>current.map(document=>document.id===estimate.id?payload.data.document:document)); setPresenting(false); setDetailsOpen(true); setSaleSetupOpen(true); setApprovalKey(crypto.randomUUID()); setNotice('Estimate approved. Set up the sold work.'); } catch (cause) { setError(cause instanceof Error ? cause.message : 'Approval failed'); } finally { setBusy(false); }
  };
  const allocate = async (fulfillment: 'now' | 'later', confirmed = false) => {
    if (!estimate || !selected.length) return setError('Choose signed options to allocate.'); if ((fulfillment === 'later' || appointment) && !jobTypeId) return setError('Choose a job type.');
    const approvalIds = pendingApprovals.filter((approval) => selected.includes(approval.optionId)).map((approval) => approval.id); if (!approvalIds.length) return setError('Those options are already allocated.');
    if (fulfillment === 'now' && !confirmed) { setConfirmation({ message: 'Add the selected signed work to this visit’s job invoice now? This changes the customer invoice.', proceed: () => void allocate('now', true) }); return; }
    setBusy(true); setError(''); setNotice(''); try { const body = { approvalIds, fulfillment, ...(fulfillment === 'now' ? (sourceJobId ? { jobId: sourceJobId } : { appointmentId: appointment!.appointmentId, jobTypeId }) : { jobTypeId, futureJobLabel: futureJobLabel.trim() || undefined }), idempotencyKey: crypto.randomUUID() }; const r = await apiFetch(`/api/v1/workspace/field/estimate-fulfillment${fieldQuery}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }); const b = await r.json(); if (!r.ok) throw new Error(b.error?.message || 'Allocation could not be saved'); if (fulfillment === 'later') setDeposit({ fulfillmentId: b.data.fulfillmentId, invoiceId: b.data.invoiceId, totalCents:b.data.totalCents ?? approvedTotal, balanceDueCents:b.data.balanceDueCents ?? approvedTotal }); if (fulfillment === 'now') { await onCurrentJobInvoiceChanged?.(); setNotice('Added to this visit’s invoice. The current job invoice is up to date.'); } setSelected([]); await load(); } catch (cause) { setError(cause instanceof Error ? cause.message : 'Allocation could not be saved'); } finally { setBusy(false); }
  };

  const draw = (event: PointerEvent<HTMLCanvasElement>) => { if (!drawing.current || !canvas.current) return; const c = canvas.current, ctx = c.getContext('2d')!, x = event.nativeEvent.offsetX * c.width / c.clientWidth, y = event.nativeEvent.offsetY * c.height / c.clientHeight; ctx.lineTo(x, y); ctx.stroke(); };
  if (initialEstimateId && !estimate) return <section className={card}>{error ? <><p role="alert" className="text-sm text-rose-700">{error}</p><p className="mt-2 text-sm">The office may need to assign this estimate to your work before sales actions are available.</p><button type="button" onClick={() => void load()} className="mt-2 min-h-11 underline">Retry estimate</button></> : <p role="status">Loading estimate…</p>}</section>;
  const closeEditor = (after?:()=>void) => {
    const close=()=>{if(estimate)hydrate(estimate);else{setTitle('');setSummary('');setOptions([blankOption()]);setDraftDirty(false);}setBuilding(false);setDetailsOpen(false);setPresenting(false);setPricebookTarget(null);after?.();};
    if(draftDirty)setConfirmation({message:'Close this estimate and discard unsaved changes?',proceed:close});else close();
  };
  const optionActions = <div role="group" aria-label="Estimate option actions" className="flex flex-wrap gap-2">
        <button type="button" disabled={busy} onClick={() => {setDetailsOpen(true);setBuilding(true);setPresenting(false);setCollapsedOptions([]); setDraftDirty(true); setOptions((current) => [...current, blankOption(`Option ${current.length + 1}`)]); }} className="inline-flex min-h-11 items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-4 text-sm font-semibold text-sky-900 hover:bg-sky-100"><Plus size={18} aria-hidden="true" />Create new option</button>
        <button type="button" disabled={busy || options.length === 0} onClick={() => {setDetailsOpen(true);setBuilding(true);setPresenting(false);setCollapsedOptions([]); if (options.length === 1) duplicateOption(0); else setDuplicatePickerOpen(true); }} className="inline-flex min-h-11 items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-4 text-sm font-semibold text-sky-900 hover:bg-sky-100 disabled:opacity-50"><Copy size={18} aria-hidden="true" />Duplicate</button>
      </div>;
  const activeEstimateHeader = <div aria-label="Current estimate" className="relative mb-4 rounded-xl border border-slate-200 bg-sky-50 p-4 pr-16"><button type="button" aria-label="Close estimate builder" disabled={busy} onClick={()=>closeEditor()} className="absolute right-2 top-2 flex h-11 w-11 items-center justify-center rounded-full bg-red-50 text-red-700 hover:bg-red-100"><X size={20} aria-hidden="true"/></button><p className="text-xs font-semibold uppercase tracking-wide text-sky-800">{estimate ? 'Current estimate' : 'New estimate · Not saved'}</p><h3 className="mt-1 break-words text-lg font-semibold text-slate-900">Working on: {title.trim() || 'Untitled estimate'}</h3><p className="mt-1 text-sm text-slate-600">{draftDirty ? 'Unsaved changes' : estimate ? 'Saved estimate' : 'Add a name and items, then save this estimate.'}</p></div>;
  const workspaceContent = () => {
  if ((estimate && !detailsOpen && !presenting) || (!estimate && !building)) return <>{confirmationDialog}{estimateCreation}{estimateSwitcher}{estimate?.status==='draft'&&<div className="p-3">{optionActions}</div>}{error && <p role="alert" className="p-4 text-sm text-rose-700">{error}</p>}</>;
  if (!estimate || (estimate.status === 'draft' && !presenting)) return <>{confirmationDialog}{estimateCreation}{estimateSwitcher}<section aria-label="Estimate editor" className={`${card} mt-4 border border-slate-200`}>
    {activeEstimateHeader}
    <div className="mt-3 space-y-2">
      <label className="block text-sm font-medium">Estimate title<input spellCheck autoCorrect="on" autoCapitalize="sentences" aria-label="Estimate title" value={title} onChange={(e) => { setDraftDirty(true); setTitle(e.target.value); }} placeholder="e.g. Improve heating reliability" className="mt-1 w-full rounded border p-2" /></label>
      <label className="block text-sm font-medium">Estimate summary<EstimateDescription aria-label="Estimate summary" spellCheck autoCorrect="on" autoCapitalize="sentences" maxLength={5000} value={summary} onChange={event => { setDraftDirty(true); setSummary(event.target.value); }} placeholder="Summarize the proposed work for the customer…" className="mt-1 w-full rounded border p-2" /></label>
      {options.map((option, index) => <div key={option.id ?? index} className={`${well} space-y-2`}>
        <div className="flex items-center justify-between gap-2"><button type="button" aria-label={`Toggle option ${index+1}`} aria-expanded={!collapsedOptions.includes(index)} onClick={()=>setCollapsedOptions(current=>current.includes(index)?current.filter(value=>value!==index):[...current,index])} className="min-h-11 flex-1 text-left font-semibold">Option {index+1} · {option.label || title || 'New option'}<ChevronDown size={18} aria-hidden="true" className="ml-2 inline"/></button><button type="button" aria-label={options.length>1?`Remove option ${index+1}`:'Clear option'} onClick={()=>{const proceed=()=>{options.length>1?removeOption(index):clearOption(index);setCollapsedOptions([]);};if(option.lines.length)setConfirmation({message:options.length>1?'Remove this option and its items?':'Clear the items in this option?',proceed});else proceed();}} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-700 hover:bg-red-100"><X size={20} aria-hidden="true"/></button></div>
        <div hidden={collapsedOptions.includes(index)} className="space-y-2">
        <div className="flex items-end justify-end gap-2">{options.length > 1 && <label className="min-w-0 flex-1 text-sm font-medium">Option name<input spellCheck autoCorrect="on" autoCapitalize="sentences" aria-label={`Option ${index + 1} name`} value={option.label} onChange={(e) => updateOption(index, { label: e.target.value })} placeholder="e.g. Replace inducer motor" className="mt-1 w-full rounded border p-2" /></label>}

        </div>
        {option.lines.map((line, lineIndex) => <FieldEstimateItem key={line.id ?? line.clientKey ?? lineIndex} line={line} card={lineCard(line)} descriptionLabel={`Option ${index + 1} line ${lineIndex + 1} item`} onDescriptionChange={description => updateLine(index, lineIndex, { description })} onRemove={() => removeLine(index, lineIndex)} onMoveUp={lineIndex > 0 ? () => moveLine(index,lineIndex,-1) : undefined} onMoveDown={lineIndex < option.lines.length-1 ? () => moveLine(index,lineIndex,1) : undefined}>
          <div className="grid grid-cols-[55px_minmax(0,1fr)_minmax(0,1fr)] gap-2"><label className="text-xs font-medium">Qty<input aria-label={`Option ${index + 1} line ${lineIndex + 1} quantity`} value={String(line.quantity)} onChange={(e) => updateLine(index, lineIndex, { quantity: Number(e.target.value) })} inputMode="decimal" className="mt-1 w-full rounded border p-2 text-sm" /></label>{features.has("estimate_costs") && <label className="text-xs font-medium">Cost<EstimatePriceInput aria-label={`Option ${index + 1} line ${lineIndex + 1} cost`} cents={line.costCents ?? catalogItems[line.priceBookItemId ?? ""]?.costCents ?? 0} onPriceChange={costCents => {if(costCents >= 0) {
              const item = catalogItems[line.priceBookItemId ?? ""];
              const unitPriceCents = item?.taxClass === "material" && item.materialMarkupBps != null ? Math.round(costCents * (10000 + item.materialMarkupBps) / 10000) : undefined;
              updateLine(index,lineIndex,{costCents,...(unitPriceCents === undefined ? {} : {unitPriceCents})});
            }}} className="mt-1 w-full rounded border p-2 text-sm" />{(line.costCents ?? catalogItems[line.priceBookItemId ?? ""]?.costCents) == null && <span className="text-[10px]">Enter cost</span>}</label>}<label className="text-xs font-medium">{catalogItems[line.priceBookItemId ?? ""]?.taxClass === "material" ? "Selling price" : "Price"}<EstimatePriceInput readOnly={features.has("estimate_costs") && catalogItems[line.priceBookItemId ?? ""]?.taxClass === "material" && catalogItems[line.priceBookItemId ?? ""]?.materialMarkupBps != null} aria-label={`Option ${index + 1} line ${lineIndex + 1} price`} cents={line.unitPriceCents} onPriceChange={unitPriceCents => updateLine(index, lineIndex, { unitPriceCents })} className="mt-1 w-full rounded border p-2 text-sm" /></label></div>
          {catalogItems[line.priceBookItemId ?? ""]?.taxClass === "material" && <p className="text-xs text-[#6b6b6b]">{catalogItems[line.priceBookItemId ?? ""]?.materialMarkupBps == null ? "Company material markup is not set." : `Selling price = cost + ${catalogItems[line.priceBookItemId ?? ""]!.materialMarkupBps! / 100}% company markup.`}</p>}
          {line.priceBookItemId && line.unitPriceCents === 0 && <p className="text-xs text-[#6b6b6b]">$0.00 — informational items do not change the total</p>}
        <button type="button" onClick={() => {setEstimatePickerKind(undefined);setReplacementLine(lineIndex);setPricebookTarget(index);}} className="min-h-11 text-sm font-semibold underline">Replace item</button></FieldEstimateItem>)}
        {sourceJobId && <><div aria-label={`Add items to option ${index+1}`} className="grid grid-cols-2 gap-2">{([["service","Add service / labor",Wrench],["equipment","Add equipment",Package],["material","Add materials",Boxes],["rebates","Add rebate",BadgePercent],["discounts","Add discount",BadgePercent],[undefined,"Browse all estimate items",Search]] as const).map(([kind,label,Icon])=><button type="button" key={label} onClick={()=>{setEstimatePickerKind(kind);setReplacementLine(null);setPricebookTarget(index);}} className="flex min-h-14 items-center gap-2 rounded-xl border border-slate-200 bg-sky-50 p-3 text-left text-sm font-semibold text-sky-950 hover:bg-sky-100"><Icon size={20} className="shrink-0" aria-hidden="true"/>{label}</button>)}</div>{pricebookTarget === index && <FieldPricebookPicker kind={estimatePickerKind} jobId={sourceJobId} actingQuery={fieldQuery} allowDeductions onClose={() => {setPricebookTarget(null);setReplacementLine(null);}} onAdd={item => addPricebookItem(index, item)} onAddMany={replacementLine === null ? items => {
          let baseCents = options[index].lines.reduce((sum,line)=>sum+Math.max(0,Math.round(line.quantity*line.unitPriceCents)),0);
          const lines = items.map(item=>{const price = item.isDeduction && item.modifierAmountType === 'percentage' ? Math.round(baseCents*(item.modifierBasisPoints ?? 0)/10000) : Math.abs(item.priceCents);if(!item.isDeduction)baseCents+=price;return {clientKey:crypto.randomUUID(),priceBookItemId:item.id,itemName:item.name,itemDescription:estimateDescription(item.description),description:estimateDescription(item.description) || item.name,quantity:1,unitPriceCents:item.isDeduction ? -price : item.priceCents,costCents:item.costCents,imageUrl:item.imageUrl,imageContentUrl:item.imageContentUrl};});
          updateOption(index,{lines:[...options[index].lines,...lines]});
        } : undefined} />}</>}
        <FieldEstimateTotals subtotal={option.lines.reduce((sum, line) => sum + Math.round(line.quantity * line.unitPriceCents), 0)} tax={draftDirty ? undefined : estimate?.options.find(saved => saved.id === option.id)?.taxCents} total={draftDirty ? undefined : estimate?.options.find(saved => saved.id === option.id)?.totalCents} />
        </div>
      </div>)}
      {(!estimate || draftDirty) && <div className="border-t pt-4"><button type="button" disabled={busy || (!sourceJobId && !estimate)} onClick={() => void saveDraft(false)} className="min-h-11 w-full rounded-full bg-black text-white disabled:opacity-60">{busy ? 'Saving…' : 'Save'}</button></div>}
      {optionActions}
      {duplicatePickerOpen && <Dialog title="Duplicate option" onClose={() => setDuplicatePickerOpen(false)} width="max-w-[398px]">
        <p className="mb-3 text-sm text-slate-600">Choose the option to copy. The original stays unchanged.</p>
        <div className="grid gap-2">{options.map((option,index) => <button key={option.id ?? index} type="button" onClick={() => { duplicateOption(index); setDuplicatePickerOpen(false); }} className="flex min-h-11 items-center gap-2 rounded-xl border px-3 py-2 text-left text-sm font-semibold"><Copy size={18} className="shrink-0" aria-hidden="true" />{option.label || `Option ${index + 1}`}</button>)}</div>
      </Dialog>}


      {!sourceJobId && !estimate && <p className="text-sm text-[#8a8a8a]">This assigned estimate visit is already linked to its estimate.</p>}
    </div>
    {notice && <p role="status" className="mt-2 text-sm text-emerald-700">{notice}</p>}{error && <p role="alert" className="mt-2 text-sm text-rose-700">{error}</p>}
  </section></> ;
  const signed = (estimate?.approvals.length ?? 0) > 0;
  const selectedApprovals = pendingApprovals.filter((approval) => selected.includes(approval.optionId));
  const canCompleteNow = selectedApprovals.length > 0 && selectedApprovals.every((approval) => approval.method === 'onsite');
  const reviewCard = <section aria-label="Estimate review" className={`${card} mt-4 border border-slate-200`}>
    {!presenting&&activeEstimateHeader}
    <div className="flex items-start justify-between gap-3"><div>{presenting ? <div className="mb-3 rounded-xl bg-sky-50 p-4"><h2 className="break-words text-xl font-semibold text-sky-950">{estimate.title}</h2>{estimate.options.length>1&&<p className="mt-1 text-sm text-slate-600">{estimate.options.length} options</p>}</div> : <p className="text-sm font-semibold">{estimate.title}</p>}{estimate.summary && <p className="mt-1 whitespace-pre-wrap text-sm">{estimateDescription(estimate.summary)}</p>}{signed&&<p className="mt-1 text-sm text-[#8a8a8a]">{pendingApprovals.length ? 'Sold work awaiting allocation' : 'All signed work has been allocated'}</p>}</div>{signed && <button type="button" onClick={createAnotherEstimate} className="min-h-10 shrink-0 text-sm font-semibold underline">Create another estimate</button>}</div>
    {notice && <p role="status" className="mt-2 text-sm text-emerald-700">{notice}</p>}
    {!signed ? <div className="mt-3 space-y-3">
      <div aria-label="Selected estimate total" role="status" className="sticky top-0 z-10 rounded-xl border bg-white p-3"><p className="flex justify-between gap-2 font-semibold"><span>Selected total · {selected.length} {selected.length === 1 ? 'option' : 'options'}</span><span>{money(approvedTotal)}</span></p><p className="mt-1 text-xs text-[#6b6b6b]">Includes sales tax for all selected options.</p></div>
      {estimate.options.filter((option) => option.status !== 'declined').map((option) => <label key={option.id} className="block rounded border p-3"><span className="flex gap-2"><input type={estimate.selectionMode === 'single' ? 'radio' : 'checkbox'} checked={selected.includes(option.id!)} onChange={() => setSelected((current) => estimate.selectionMode === 'single' ? [option.id!] : current.includes(option.id!) ? current.filter((id) => id !== option.id) : [...current, option.id!])} /><span className="font-semibold">{estimate.options.length === 1 || (estimate.options[0].id === option.id && /^recommended(?: work)?$/i.test(option.label.trim())) ? estimate.title : option.label}</span></span>{option.description && <span className="mt-1 block text-sm text-[#555]">{option.description}</span>}<span className="mt-2 block space-y-1 text-sm">{option.lines.map((line) => <span key={line.id ?? `${line.description}-${line.quantity}`} className="block border-b border-[#ececec] last:border-0">{lineCard(line)}</span>)}</span><FieldEstimateTotals subtotal={option.subtotalCents ?? option.lines.reduce((sum, line) => sum + Math.round(line.quantity * line.unitPriceCents), 0)} tax={option.taxCents} total={option.totalCents} /></label>)}
      <div className={well}>{estimate.terms && <p className="mt-1 text-sm">{estimate.terms}</p>}</div>
      <label className="block text-sm font-medium">Customer name<input value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Customer name" className="mt-1 w-full rounded border p-2" /></label>
      <label className="block text-sm font-medium">Customer signature<canvas aria-label="Customer signature" ref={canvas} width={600} height={130} onPointerDown={(event) => { event.preventDefault(); event.currentTarget.setPointerCapture(event.pointerId); drawing.current = true; const c = canvas.current!, ctx = c.getContext('2d')!; ctx.beginPath(); ctx.moveTo(event.nativeEvent.offsetX * c.width / c.clientWidth, event.nativeEvent.offsetY * c.height / c.clientHeight); }} onPointerMove={draw} onPointerUp={() => { drawing.current = false; }} onPointerCancel={() => {drawing.current=false;}} style={{touchAction:"none"}} className="mt-1 h-24 w-full rounded border" /></label>
      <button disabled={busy || !selected.length || !customerName.trim()} onClick={() => void sign()} className="min-h-11 w-full rounded-full bg-black text-white disabled:opacity-60">Sign approval</button>
    </div> : <div className="mt-3 space-y-3">
      <details className="rounded border p-3" open><summary className="cursor-pointer text-sm font-semibold">Signed estimate details</summary><div className="mt-3 space-y-3 text-sm">{estimate.approvals[0]?.customerName && <p>Approved by <span className="font-semibold">{estimate.approvals[0].customerName}</span></p>}{estimate.options.map((option) => <div key={option.id} className="border-t pt-2 first:border-t-0 first:pt-0"><p className="font-semibold">{option.label} · {money(option.totalCents)}</p><p className="text-xs">Sales tax: {option.taxCents === undefined ? "Unavailable" : money(option.taxCents)}</p>{option.description && <p className="mt-1 text-[#555]">{option.description}</p>}{option.lines.map((line) => <p key={line.id ?? `${line.description}-${line.quantity}`} className="mt-1">{lineCard(line)}</p>)}</div>)}{estimate.terms && <div className="border-t pt-2"><p className="font-semibold">Terms</p><p className="mt-1 whitespace-pre-wrap">{estimate.terms}</p></div>}</div></details>
      {pendingApprovals.length ? <><button type="button" disabled={busy} onClick={() => { setSelected(pendingApprovals.map(a => a.optionId)); setSaleSetupOpen(true); }} className="min-h-11 w-full rounded-full bg-black px-4 text-white">Set up approved job</button><p className="text-sm font-medium">Allocate approved options from this estimate</p><p className="-mt-2 text-sm text-[#6b6b6b]">Choose where each signed option belongs: today’s visit or a future job.</p>
        {pendingApprovals.map((approval) => <label key={approval.id} className="flex gap-2 rounded border p-3"><input type="checkbox" checked={selected.includes(approval.optionId)} onChange={() => setSelected((current) => current.includes(approval.optionId) ? current.filter((id) => id !== approval.optionId) : [...current, approval.optionId])} /><span>{estimate.options.find((option) => option.id === approval.optionId)?.label} · {money(approval.approvedAmountCents)}</span></label>)}
</> : <div className={`${well} text-sm`}><p className="font-semibold">This estimate is fully allocated</p><button type="button" onClick={()=>setSaleSetupOpen(true)} className="mt-2 min-h-11 rounded-full border px-4 font-semibold">Open sold job</button><p className="mt-1 text-[#6b6b6b]">Its signed options have already been placed on today’s job or a future job. There is nothing else to allocate here.</p></div>}
    </div>}
    {!saleSetupOpen && Array.from(new Map([...(estimate.approvals.filter(approval=>approval.canCollectDeposit && approval.fulfillmentId && approval.invoiceId && (approval.invoiceBalanceDueCents ?? 0) >= (approval.invoiceTotalCents ?? 0)).map(approval=>({fulfillmentId:approval.fulfillmentId!,invoiceId:approval.invoiceId!,totalCents:approval.invoiceTotalCents ?? 0,balanceDueCents:approval.invoiceBalanceDueCents ?? 0}))),...(deposit ? [deposit] : [])].map(item=>[item.invoiceId,item])).values()).map(deposit => <FieldEstimateDeposit key={deposit.fulfillmentId} fulfillmentId={deposit.fulfillmentId} invoiceId={deposit.invoiceId} initialTotalCents={deposit.totalCents} initialBalanceDueCents={deposit.balanceDueCents} actingQuery={fieldQuery} cardEnabled={features.has('card_deposits')} balanceGuard={features.has('deposit_balance_guard')} customerEmail={job?.customerEmail ?? appointment?.customerEmail} onSaved={async()=>{setDeposit(null);setSaleSetupOpen(true);await load(); await onCurrentJobInvoiceChanged?.();}} />)}
    {error && <p role="alert" className="mt-2 text-sm text-rose-700">{error}</p>}
  </section>;
  const reviewFrame = <>{confirmationDialog}{saleSetupOpen && <FieldApprovedJobDialog estimate={estimate} actingQuery={fieldQuery} onClose={() => setSaleSetupOpen(false)} onChanged={() => load(estimate.id)} onCompleteNow={canCompleteNow ? () => { setSaleSetupOpen(false); void allocate('now'); } : undefined} />}{estimateCreation}{estimateSwitcher}{presenting ? <Dialog title="Present estimate" width="max-w-3xl" onClose={() => {setPresenting(false);setDetailsOpen(false);}}>{reviewCard}</Dialog> : reviewCard}</>;
  return reviewFrame;
  };
  return <>{workspaceContent()}<FieldEstimateDelivery key={estimate?.id ?? 'unsaved'} estimate={estimate} dirty={draftDirty} actingQuery={fieldQuery} customerEmail={job?.customerEmail ?? appointment?.customerEmail} jobTypes={jobTypes} onPresent={reviewEstimate} onActionSelected={() => {setDetailsOpen(false);setPresenting(false);}} /></>;
}

export function FieldApprovedJobDialog({ estimate, actingQuery, onClose, onChanged, onCompleteNow }: {
 estimate: FieldEstimate; actingQuery: string; onClose: () => void; onChanged: () => Promise<void>; onCompleteNow?: () => void;
}) {
 const features=useFieldWorkflow();
 const [approvals] = useState(() => estimate.approvals.filter(a=>!a.jobId));
 const [savedJobs,setSavedJobs]=useState<Array<{jobId:string;invoiceId:string;fulfillmentId:string;jobNumber?:number;jobTypeId:string;balanceDueCents:number}>>([]);
 const [loadingSaved,setLoadingSaved]=useState(estimate.approvals.every(a=>Boolean(a.jobId)));
 const [key] = useState(()=>crypto.randomUUID());
 const [jobTypes,setJobTypes]=useState<Array<{id:string;name:string}>>([]);
 const [jobTypeId,setJobTypeId]=useState('');
 const [prepared,setPrepared]=useState<{jobId:string;invoiceId:string;fulfillmentId:string;jobNumber?:number} | null>(null);
 const saved=useRef<typeof prepared>(null);
 const running=useRef(false);
 const [step,setStep]=useState<'summary'|'job'|'deposit'|'purchase-orders'|'booking'>('summary');
 const [busy,setBusy]=useState(false); const [error,setError]=useState(''); const [notice,setNotice]=useState('');
 const [orders,setOrders]=useState<NonNullable<Detail['purchaseOrders']>>([]);
 const [balance,setBalance]=useState<number | null>(null);
 const soldApprovals=approvals.length?approvals:estimate.approvals.filter(a=>a.jobId===prepared?.jobId);
 const soldTotal=soldApprovals.reduce((sum,a)=>sum+a.approvedAmountCents,0);
 useEffect(()=>{void apiFetch('/api/v1/workspace/field/job-types').then(async r=>{const b=await r.json();if(!r.ok)throw new Error('Unable to load job types.');setJobTypes(b.data.items);}).catch(c=>setError(c.message));},[]);
 useEffect(()=>{
   if(approvals.length)return;
   let live=true;
   void apiFetch(`/api/v1/workspace/field/estimates/${encodeURIComponent(estimate.id)}/sold-jobs${actingQuery}`).then(async r=>{const b=await r.json();if(!r.ok)throw new Error(b.error?.message || 'Sold jobs could not be loaded.');if(!live)return;setSavedJobs(b.data.items);const first=b.data.items[0];if(first){saved.current=first;setPrepared(first);setJobTypeId(first.jobTypeId);setBalance(first.balanceDueCents);}else setError('This sold work is already on its assigned job. Open that job to continue.');}).catch(c=>{if(live)setError(c.message);}).finally(()=>{if(live)setLoadingSaved(false);});
   return ()=>{live=false;};
 },[]);
 const post=async(path:string,input:unknown)=>{const r=await apiFetch(path,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(input)});const b=await r.json();if(!r.ok)throw new Error(b.error?.message || 'The request could not be saved.');return b.data;};
 const prepareJob=async()=>{
   if(saved.current)return saved.current.jobId;
   if(!jobTypeId || !approvals.length)throw new Error('Choose a job type and sold work.');
   const result=await post(`/api/v1/workspace/field/estimate-fulfillment${actingQuery}`,{approvalIds:approvals.map(a=>a.id),fulfillment:'later',jobTypeId,idempotencyKey:key});
   if(!result.jobId || !result.invoiceId || !result.fulfillmentId)throw new Error('The sold job could not be confirmed. Retry to recover it.');
   saved.current=result;setPrepared(result);await onChanged();return result.jobId as string;
 };
 const loadOrders=async(jobId:string)=>{const r=await apiFetch(`/api/v1/workspace/field/jobs/${encodeURIComponent(jobId)}/purchase-orders${actingQuery}`);const b=await r.json();if(!r.ok)throw new Error(b.error?.message || 'Purchase orders could not be loaded.');setOrders(b.data.items ?? []);};
 const open=async(next:typeof step)=>{if(running.current)return;running.current=true;setBusy(true);setError('');try{const id=await prepareJob();if(next==='purchase-orders')await loadOrders(id);setStep(next);setNotice(next==='summary'?'Sold job created. It is ready for scheduling.':'');}catch(c){setError(c instanceof Error?c.message:'Unable to prepare job.');}finally{running.current=false;setBusy(false);}};
 return <Dialog title={step==='booking'?'Book appointment':'Approved job'} width="max-w-3xl" onClose={()=>{if(!busy)onClose();}}>
   <p className="mt-3 font-semibold text-emerald-700">Estimate approved</p>
   <h2 className="mt-2 text-xl font-semibold">{estimate.title}</h2>
   <p className="mt-2 text-lg font-semibold">{money((approvals.length?approvals:estimate.approvals.filter(a=>a.jobId===prepared?.jobId)).reduce((sum,a)=>sum+a.approvedAmountCents,0))}</p>
   <details className="my-3 rounded-xl border p-3"><summary className="cursor-pointer text-sm font-semibold">View estimate details</summary>{soldApprovals.map(a=>{const option=estimate.options.find(o=>o.id===a.optionId);return <div key={a.id} className="mt-3 text-sm"><p className="font-semibold">{estimate.options.length===1?estimate.title:option?.label} · {money(a.approvedAmountCents)}</p>{option?.lines.map((line,i)=><p key={line.id || i} className="mt-2 whitespace-pre-wrap">{line.itemName || line.description} · {line.quantity} × {money(line.unitPriceCents)}</p>)}</div>;})}</details>
   {savedJobs.length>1 && <label className="mt-3 block text-sm">Sold job<select aria-label="Saved sold job" disabled={busy} value={prepared?.jobId || ''} onChange={e=>{const value=savedJobs.find(j=>j.jobId===e.target.value);if(value){saved.current=value;setPrepared(value);setJobTypeId(value.jobTypeId);setBalance(value.balanceDueCents);setStep('summary');}}}>{savedJobs.map(j=><option key={j.jobId} value={j.jobId}>Job #{j.jobNumber}</option>)}</select></label>}
   <label className="mt-3 block text-sm">Job type<select aria-label="Sold job type" disabled={busy || Boolean(prepared)} value={jobTypeId} onChange={e=>setJobTypeId(e.target.value)} className="mt-1 min-h-11 w-full rounded-lg border p-2"><option value="">Choose job type</option>{jobTypes.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}</select></label>
   {prepared && <p className="mt-3 text-sm font-semibold">{prepared.jobNumber?`Job #${prepared.jobNumber}`:'Sold job'} · {step==='booking'?'Ready to book':'Saved'}</p>}
   {notice && <p role="status" className="mt-3 text-sm text-emerald-700">{notice}</p>}
   {step!=='summary' && <button type="button" disabled={busy} onClick={()=>setStep('summary')} className="mt-3 min-h-11 underline">{step==='deposit'?'Cancel deposit':'Job actions'}</button>}
   {loadingSaved && <p role="status">Loading sold job…</p>}
   {!prepared && approvals.length > 0 && <p className="mt-3 text-sm">Book into a future job to create a separate job and invoice. You can still view the estimate and collect its deposit from this visit. Choose Complete work now only when doing the work on today’s visit.</p>}
   {step==='summary' && !loadingSaved && (approvals.length>0 || prepared) && <div className="mt-4 grid gap-3 sm:grid-cols-2">
     <button type="button" disabled={busy} onClick={()=>void open('deposit')} className="min-h-16 rounded-xl border p-4 font-semibold">Collect deposit</button>
     <button type="button" disabled={busy} onClick={()=>void open('purchase-orders')} className="min-h-16 rounded-xl border p-4 font-semibold">Create purchase order</button>
     <button type="button" disabled={busy} onClick={()=>void open('job')} className="min-h-11 rounded-full bg-black px-4 text-white">Open job</button>
     <button type="button" disabled={busy} onClick={()=>void open('job')} className="min-h-11 rounded-full border px-4">Book into a future job</button>
     <button type="button" disabled={busy} onClick={()=>setStep('booking')} className="min-h-11 rounded-full border px-4">Book appointment</button>
     {onCompleteNow && !prepared && <button type="button" disabled={busy} onClick={onCompleteNow} className="min-h-11 rounded-full border px-4 sm:col-span-2">Complete work now</button>}
   </div>}
   {step==='job' && prepared && <section aria-label="Sold job summary" className="mt-4 space-y-3 rounded-xl border p-4"><h3 className="font-semibold">Job #{prepared.jobNumber || prepared.jobId}</h3><p className="text-sm">{estimate.title}</p>{(approvals.length?approvals:estimate.approvals.filter(a=>a.jobId===prepared.jobId)).map(a=>{const option=estimate.options.find(o=>o.id===a.optionId);return option && <div key={a.id}>{option.lines.map((line,i)=><p key={line.id || i} className="mt-2 text-sm">{line.itemName || line.description} · {line.quantity} × {money(line.unitPriceCents)}</p>)}<FieldEstimateTotals subtotal={option.subtotalCents ?? option.lines.reduce((sum,line)=>sum+Math.round(line.quantity*line.unitPriceCents),0)} tax={option.taxCents} total={option.totalCents} /></div>;})}{balance!==null && <p>Balance due: {money(balance)}</p>}<div className="flex flex-wrap gap-2"><button type="button" onClick={()=>setStep('deposit')} className="min-h-11 rounded-full border px-4">Collect deposit</button><button type="button" onClick={()=>void open('purchase-orders')} className="min-h-11 rounded-full border px-4">Create purchase order</button><button type="button" onClick={()=>setStep('booking')} className="min-h-11 rounded-full border px-4">Book appointment</button></div></section>}
   {step==='deposit' && prepared && <FieldEstimateDeposit fulfillmentId={prepared.fulfillmentId} invoiceId={prepared.invoiceId} initialTotalCents={soldTotal} initialBalanceDueCents={balance ?? soldTotal} actingQuery={actingQuery} cardEnabled={features.has('card_deposits')} balanceGuard={features.has('deposit_balance_guard')} onBusyChange={setBusy} onSaved={async result=>{setBalance(result.balanceDueCents);setStep('summary');setNotice(`Deposit recorded. Remaining balance: ${money(result.balanceDueCents)}. Choose the next step below.`);await onChanged();}} />}
   {step==='purchase-orders' && prepared && <FieldPurchaseOrders preparation jobId={prepared.jobId} actingQuery={actingQuery} purchaseOrders={orders} busy={busy?'saving':''} onCreated={()=>{void loadOrders(prepared.jobId).catch(c=>setError(c.message));}} />}
   {step==='booking' && <InstallScheduler jobTypeId={jobTypeId} availabilityBase={`/api/v1/workspace/field/estimates/${encodeURIComponent(estimate.id)}/scheduling`} actingQuery={actingQuery} prepareJob={prepareJob} saveVisit={visit=>post(`/api/v1/workspace/field/sold-jobs/${encodeURIComponent(visit.jobId)}/appointments${actingQuery}`,visit)} onBusyChange={setBusy} onDone={()=>{setStep('summary');setNotice('Appointment booked.');void onChanged();}} />}
   {error && <p role="alert" className="mt-3 text-sm text-rose-700">{error}</p>}
 </Dialog>;
}

function TruckMaterialUse({ jobId }: { jobId: string }) {
  const [items, setItems] = useState<Array<{ priceBookItemId: string; itemName: string; locationId: string; locationName: string; quantityOnHand: number }>>([]);
  const [selected, setSelected] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => {
    const response = await apiFetch("/api/v1/workspace/inventory/my-truck");
    const payload = await response.json().catch(() => null) as ApiPayload<{ items: Array<{ priceBookItemId: string; itemName: string; locationId: string; locationName: string; quantityOnHand: number }> }> | { error?: { message?: string } } | null;
    if (!response.ok) throw new Error(payload && "error" in payload ? payload.error?.message || "Unable to load truck stock" : "Unable to load truck stock");
    const loaded = (payload as ApiPayload<{ items: Array<{ priceBookItemId: string; itemName: string; locationId: string; locationName: string; quantityOnHand: number }> }>).data.items;
    setItems(loaded); setSelected((current) => current || loaded[0]?.priceBookItemId || "");
  };
  useEffect(() => { void load().catch((reason) => setError(reason instanceof Error ? reason.message : "Unable to load truck stock")); }, [jobId]);
  const submit = async (kind: "use" | "return") => {
    const item = items.find((value) => value.priceBookItemId === selected); const parsed = Number(quantity);
    if (!item || !Number.isFinite(parsed) || parsed <= 0) return setError("Choose stocked material and a positive quantity.");
    setBusy(true); setError("");
    try {
      const response = await apiFetch(`/api/v1/workspace/inventory/jobs/${encodeURIComponent(jobId)}/${kind}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ priceBookItemId: item.priceBookItemId, locationId: item.locationId, quantity: parsed, note: null }) });
      const payload = await response.json().catch(() => null) as { error?: { message?: string } } | null;
      if (!response.ok) throw new Error(payload?.error?.message || "That stock movement could not be saved");
      setQuantity("1"); await load();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "That stock movement could not be saved"); } finally { setBusy(false); }
  };
  return <section className={card}><p className="text-sm font-semibold">Truck material</p><p className="mt-1 text-sm text-[#8a8a8a]">Use a stocked material on this job or return unused stock to your truck.</p>{items.length ? <><select value={selected} onChange={(event) => setSelected(event.target.value)} className="mt-3 min-h-11 w-full rounded-xl border border-[#e2e2e2] px-3 text-base">{items.map((item) => <option key={`${item.locationId}-${item.priceBookItemId}`} value={item.priceBookItemId}>{item.itemName} · {item.quantityOnHand} on hand</option>)}</select><input value={quantity} onChange={(event) => setQuantity(event.target.value)} inputMode="decimal" placeholder="Quantity" className="mt-2 min-h-11 w-full rounded-xl border border-[#e2e2e2] px-3 text-base" /><div className="mt-2 grid grid-cols-2 gap-2"><button disabled={busy} onClick={() => void submit("use")} className="min-h-11 rounded-full bg-[#ececec] text-sm font-semibold disabled:opacity-60">Use on job</button><button disabled={busy} onClick={() => void submit("return")} className="min-h-11 rounded-full border border-[#e2e2e2] text-sm font-semibold disabled:opacity-60">Return to truck</button></div></> : <p className="mt-3 text-sm text-[#8a8a8a]">No stocked materials are assigned to your truck.</p>}{error && <p role="alert" className="mt-3 text-sm text-rose-700">{error}</p>}</section>;
}

export function InvoiceDraft({
  jobId, actingQuery,
  invoice,
  disabled,
  onAddLine, onSaved, jobTypeName, jobNumber, actionsTarget, customerEmail, onOpenMaintenancePlan,
}: {
  jobId: string; actingQuery: string;
  actionsTarget?: HTMLElement | null;
  onOpenMaintenancePlan?: () => void;
  customerEmail?: string | null;
  onSaved: () => Promise<void>; jobTypeName?: string | null; jobNumber?: string | number | null;
  invoice: Invoice | undefined;
  disabled: boolean;
  onAddLine: (
    description: string,
    quantity: number,
    unitPriceCents: number,
    priceBookItemId?: string,
  ) => Promise<{ ok: boolean; message: string }>;
}) {
  const features=useFieldWorkflow();
  const [ordering,setOrdering]=useState(false), [orderError,setOrderError]=useState('');
  const orderLock=useRef(false);
  const moveInvoiceLine=async (lineId:string,direction:number)=>{
    if(!invoice || disabled || orderLock.current) return;
    const ids=invoice.items.map(item=>item.id), visibleIds=invoice.items.filter(item=>showHiddenItems || isCustomerVisibleInvoiceLine(item)).map(item=>item.id), index=ids.indexOf(lineId), target=ids.indexOf(visibleIds[visibleIds.indexOf(lineId)+direction]);
    if(index<0 || target<0 || target>=ids.length) return;
    [ids[index],ids[target]]=[ids[target],ids[index]];
    orderLock.current=true;setOrdering(true);setOrderError('');
    try {
      await savePendingEdits();
      const response=await apiFetch(`/api/v1/workspace/field/jobs/${encodeURIComponent(jobId)}/invoices/${encodeURIComponent(invoice.id)}/line-order${actingQuery}`,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify({lineIds:ids})});
      const body=await response.json();if(!response.ok) throw new Error(body.error?.message || 'Item order could not be saved.');
      await onSaved();
    } catch(cause){setOrderError(cause instanceof Error ? cause.message : 'Item order could not be saved.');}
    finally{orderLock.current=false;setOrdering(false);}
  };
  const [pickerOpen, setPickerOpen] = useState(false);
  const [showHiddenItems, setShowHiddenItems] = useState(false);
  const [pickerKind, setPickerKind] = useState<"service" | "equipment" | "material" | "fees" | "deductions" | undefined>();
  const pendingEdits = useRef(new Map<string, PendingInvoiceEdit>());
  const [hasPendingEdits, setHasPendingEdits] = useState(false);
  const registerPendingEdit = useCallback((id: string, edit: PendingInvoiceEdit | null) => {
    if (edit) pendingEdits.current.set(id, edit); else pendingEdits.current.delete(id);
    setHasPendingEdits(pendingEdits.current.size > 0);
  }, []);
  const savePendingEdits = async () => {
    const edits = [...pendingEdits.current.values()];
    for (const edit of edits) { const error = edit.validate(); if (error) throw new Error(error); }
    for (const edit of edits) await edit.save();
  };

  const presentation = invoice ? invoicePresentation(invoice) : null;
  return (
    <section className={card}>
      <div className="flex items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-sm font-semibold">
          {invoice ? `Invoice #${invoice.displayNumber?.trim() || invoice.invoiceNumber}` : "Invoice"}
        </p>
        <p className="text-sm font-semibold">
          {money(invoice?.totalCents ?? 0)}
        </p>
      </div>
      <div className="mt-2"><FieldInvoiceStatus invoice={invoice} /></div>
      {invoice?.payments?.length ? <ul aria-label="Recorded invoice payments" className="mt-2 space-y-1 text-sm">{invoice.payments.map(payment=><li key={payment.id}>{money(payment.amountCents)} · {payment.method.replaceAll('_',' ')}{payment.reference ? ` · ${payment.reference}` : ''} · {new Date(payment.paidAt).toLocaleString()}</li>)}</ul> : null}
      <FieldInvoiceNotes summaryRequired={serviceCallSummaryRequired(invoice)} customerEmail={customerEmail} actionsTarget={actionsTarget} jobId={jobId} actingQuery={actingQuery} invoice={invoice} disabled={disabled} onSaved={onSaved} beforeSave={savePendingEdits} hasPendingEdits={hasPendingEdits}>
      <div className={`${well} mt-3`}>
        {!invoice && (
          <p className="text-sm text-[#8a8a8a]">No invoice is ready</p>
        )}
        {invoice && presentation?.estimateDisplayNumber && (
          <p className="mt-1 text-sm text-[#8a8a8a]">Includes approved work from Estimate #{presentation.estimateDisplayNumber}</p>
        )}
        {invoice && presentation?.soldLater && (
          <p className="mt-1 text-sm text-[#8a8a8a]">Not scheduled yet.</p>
        )}
        {(() => {
          const allItems = invoice?.items ?? [];
          const hiddenCount = allItems.filter((item) => !isCustomerVisibleInvoiceLine(item)).length;
          const visibleItems = allItems.filter((item) => showHiddenItems || isCustomerVisibleInvoiceLine(item));
          return <>
            <div className="mb-3 flex flex-wrap gap-2">{hiddenCount > 0 && <label className="flex items-center gap-2 text-xs font-semibold text-[#6b6b6b]"><input type="checkbox" checked={showHiddenItems} onChange={(event) => setShowHiddenItems(event.target.checked)} /> Show hidden items ({hiddenCount})</label>}</div>
            {orderError && <p role="alert" className="text-sm text-rose-700">{orderError}</p>}
            {groupFieldInvoiceLines(visibleItems).map(([label, items],groupIndex) => <section key={`${label}-${groupIndex}`} aria-label={label}>{!items.every(item=>commercialLinePresentation(item.itemName,item.description,item.itemDescription).name===label) && <h4 className="mt-3 font-semibold">{label}</h4>}{items.map(item => <FieldInvoiceLine key={`${invoice!.id}:${item.id}`} line={item} onMoveUp={features.has('invoice_line_order') && visibleItems[0]?.id !== item.id ? () => void moveInvoiceLine(item.id,-1) : undefined} onMoveDown={features.has('invoice_line_order') && visibleItems.at(-1)?.id !== item.id ? () => void moveInvoiceLine(item.id,1) : undefined} invoiceId={invoice!.id} jobId={jobId} actingQuery={actingQuery} disabled={disabled || ordering || ["paid", "void"].includes(invoice!.status)} onSaved={onSaved} registerPendingEdit={registerPendingEdit} autoSave />)}</section>)}
          </>;
        })()}
      </div>
      <div aria-label="Add invoice items" className="mt-4 grid grid-cols-2 gap-2">{([["service", "Add service / labor", Wrench], ["equipment", "Add equipment", Package], ["material", "Add materials", Boxes], ["fees", "Add fee", BadgePercent], ["deductions", "Add discount", BadgePercent], [undefined, "Browse all items", Search]] as const).map(([kind, label, Icon]) => <button key={label} type="button" disabled={disabled || Boolean(invoice && ["paid", "void"].includes(invoice.status))} onClick={() => { setPickerKind(kind); setPickerOpen(true); }} className="flex min-h-14 items-center gap-2 rounded-2xl border border-sky-200 bg-sky-50 px-3 py-3 text-left text-sm font-semibold text-sky-900 shadow-sm hover:bg-sky-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 disabled:opacity-50"><Icon size={20} className="shrink-0" aria-hidden="true" />{label}</button>)}</div>
      {pickerOpen && <FieldPricebookPicker onOpenMaintenancePlan={onOpenMaintenancePlan} kind={pickerKind} jobId={jobId} actingQuery={actingQuery} allowDeductions={pickerKind === "deductions"} onClose={() => setPickerOpen(false)} onAddMany={async (items,onAdded) => { for(const item of items) { const result=await onAddLine(item.description?.trim() || item.name,1,item.isDeduction ? 0 : item.priceCents,item.id); if(!result.ok) throw new Error(result.message); onAdded(item.id); } }} onAdd={async item => { const result = await onAddLine(item.description?.trim() || item.name, 1, item.isDeduction ? 0 : item.priceCents, item.id); if (!result.ok) throw new Error(result.message); }} />}

      {invoice && <div aria-label="Invoice totals" className="mt-4 space-y-2 border-t border-[#e2e2e2] pt-3 text-sm">
        {invoice.subtotalCents !== undefined && <p className="flex justify-between"><span>Subtotal</span><span>{money(invoice.subtotalCents)}</span></p>}
        <p className="flex justify-between"><span>Sales tax</span><span>{invoice.taxCents === undefined ? "Unavailable" : money(invoice.taxCents)}</span></p>
        <p className="flex justify-between font-semibold"><span>Invoice total</span><span>{money(invoice.totalCents)}</span></p>
        <p className="flex justify-between text-emerald-800"><span>Amount paid</span><span>{money(presentation?.paidCents ?? 0)}</span></p>
        <p className="flex justify-between font-semibold"><span>Balance due</span><span>{money(invoice.balanceDueCents)}</span></p>
      </div>}
      </FieldInvoiceNotes>
    </section>
  );
}

function JobPhotos({
  jobId,
  actingTechnicianId,
  refreshToken,
  onCountChange,
}: {
  jobId: string;
  actingTechnicianId: string;
  refreshToken: number;
  onCountChange: (count: number | null | "error") => void;
}) {
  const MAX_PARALLEL_UPLOADS = 2;
  const [photos, setPhotos] = useState<JobPhoto[]>([]);
  const features=useFieldWorkflow();
  const [selectedPhoto,setSelectedPhoto] = useState<JobPhoto | null>(null);
  const [caption,setCaption] = useState("");
  const [confirmDelete,setConfirmDelete] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [failedUploads, setFailedUploads] = useState<Array<{ file: File; message: string }>>([]);
  const onCountChangeRef = useRef(onCountChange);
  const cameraRef = useRef<HTMLInputElement>(null);
  const libraryRef = useRef<HTMLInputElement>(null);
  const actingQuery = actingTechnicianId
    ? `?technicianId=${encodeURIComponent(actingTechnicianId)}`
    : "";

  useEffect(() => {
    onCountChangeRef.current = onCountChange;
  }, [onCountChange]);

  useEffect(() => {
    onCountChangeRef.current(photos.length);
  }, [photos]);

  const load = async () => {
    const response = await apiFetch(
      `/api/v1/workspace/field/jobs/${encodeURIComponent(jobId)}/photos${actingQuery}`,
    );
    const payload = (await response.json().catch(() => null)) as
      ApiPayload<{ items: JobPhoto[] }> | { error?: { message?: string } };
    if (!response.ok)
      throw new Error(
        "error" in payload
          ? payload.error?.message || "Unable to load photos"
          : "Unable to load photos",
      );
    const items = (payload as ApiPayload<{ items: JobPhoto[] }>).data.items;
    setPhotos(items);
    onCountChangeRef.current(items.length);
    return items;
  };

  useEffect(() => {
    let cancelled = false;
    void load()
      .then(() => {
        if (!cancelled) setError("");
      })
      .catch((cause) => {
        if (!cancelled) {
          onCountChangeRef.current("error");
          setError(cause instanceof Error ? cause.message : "Unable to load photos");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [jobId, actingTechnicianId]);

  useOnFieldRefresh(refreshToken, () => {
    void load().catch((cause) => {
      setError(cause instanceof Error ? cause.message : "Unable to load photos");
    });
  });

  const changePhoto = async (remove=false) => {
    if(!selectedPhoto)return;setBusy(true);setError("");
    try{const response=await apiFetch(`/api/v1/workspace/field/jobs/${encodeURIComponent(jobId)}/photos/${encodeURIComponent(selectedPhoto.id)}${actingQuery}`,{method:remove ? "DELETE" : "PATCH",headers:{"Content-Type":"application/json"},...(remove ? {} : {body:JSON.stringify({caption:caption.trim() || null})})});const body=await response.json();if(!response.ok)throw new Error(body.error?.message || "Photo could not be changed.");await load();setSelectedPhoto(null);setConfirmDelete(false);}catch(cause){setError(cause instanceof Error ? cause.message : "Photo could not be changed.");}finally{setBusy(false);}
  };

  const uploadOne = async (file: File) => {
    try {
      const prepared = await prepareFieldPhoto(file);
      const body = new FormData();
      body.append("photo", prepared, prepared.name);
      const response = await apiFetch(
        `/api/v1/workspace/field/jobs/${encodeURIComponent(jobId)}/photos${actingQuery}`,
        { method: "POST", body },
      );
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as {
          error?: { message?: string };
        } | null;
        throw new Error(
          payload?.error?.message || "That photo could not be saved",
        );
      }
      const payload = await response.json().catch(() => null) as ApiPayload<JobPhoto> | null;
      const uploaded = payload?.data;
      if (!uploaded?.id) {
        throw new Error("The photo was saved, but WorkView couldn't confirm it. Refresh the job to check it.");
      }
      return uploaded;
    } catch (cause) {
      throw cause instanceof Error ? cause : new Error("That photo could not be saved");
    }
  };

  const showUploadedPhoto = (uploaded: JobPhoto) => {
    // The POST has already committed the attachment. Show its confirmed
    // response immediately instead of making the gallery depend on a second
    // request, which can be interrupted by an unreliable field connection.
    setPhotos(current => {
      const next = [uploaded, ...current.filter(photo => photo.id !== uploaded.id)];
      return next;
    });
  };

  const uploadFiles = async (files: File[]) => {
    if (!files.length) return;
    setError("");
    setUploadingCount(current => current + files.length);
    const uploaded: Array<JobPhoto | undefined> = Array(files.length);
    const failures: Array<{ file: File; message: string }> = [];
    let nextIndex = 0;

    const worker = async () => {
      while (nextIndex < files.length) {
        const index = nextIndex++;
        try {
          const photo = await uploadOne(files[index]);
          uploaded[index] = photo;
          showUploadedPhoto(photo);
        } catch (cause) {
          failures.push({
            file: files[index],
            message: cause instanceof Error ? cause.message : "That photo could not be saved",
          });
        } finally {
          setUploadingCount(current => Math.max(0, current - 1));
        }
      }
    };

    await Promise.all(Array.from({ length: Math.min(MAX_PARALLEL_UPLOADS, files.length) }, worker));
    const confirmed = uploaded.filter((photo): photo is JobPhoto => Boolean(photo));
    setFailedUploads(current => [
      ...current.filter(({ file }) => !files.includes(file)),
      ...failures,
    ]);
    if (failures.length) {
      setError(failures.length === 1 ? failures[0].message : `${failures.length} photos could not be saved. Please retry those photos.`);
    }
    if (confirmed.length === 1 && files.length === 1 && features.has("photo_details")) {
      setSelectedPhoto(confirmed[0]);
      setCaption(confirmed[0].caption || "");
      setConfirmDelete(false);
    }
    // Reconcile any concurrent changes without turning a successful capture
    // into a visible failure when this read request is transiently unavailable.
    if (confirmed.length) void load().catch(() => undefined);
  };

  return (
    <section className={card}>
      {selectedPhoto && <Dialog title="Edit job photo" onClose={()=>{if(!busy)setSelectedPhoto(null);}} width="max-w-[398px]"><FieldPhotoThumb photo={selectedPhoto} actingQuery={actingQuery}/><label className="mt-3 block text-sm font-semibold">Photo label<input aria-label="Photo label" maxLength={200} value={caption} onChange={event=>setCaption(event.target.value)} className="mt-1 min-h-11 w-full rounded border p-2" /></label>{error && <p role="alert" className="text-sm text-rose-700">{error}</p>}<button type="button" disabled={busy} className="mt-3 min-h-11 rounded border px-4" onClick={()=>void changePhoto()}>Save label</button>{confirmDelete ? <div className="mt-3"><p className="text-sm">Remove this photo from the job?</p><button type="button" disabled={busy} onClick={()=>void changePhoto(true)} className="min-h-11 px-4 text-rose-700">Delete photo</button><button type="button" disabled={busy} onClick={()=>setConfirmDelete(false)} className="min-h-11 px-4">Keep photo</button></div> : <button type="button" disabled={busy} onClick={()=>setConfirmDelete(true)} className="ml-3 min-h-11 text-rose-700">Remove photo</button>}</Dialog>}
      <p className="flex items-center gap-2 text-sm font-semibold">
        <PhotoIcon /> Photos
      </p>
      <div className={`${well} mt-3`}>
        {photos.length ? (
          <div className="grid grid-cols-3 gap-2">
            {photos.map((photo) => (
              <div key={photo.id}><FieldPhotoThumb
                photo={photo}
                actingQuery={actingQuery}
              /><p className="mt-1 break-words text-xs">{photo.caption || "Unlabelled photo"}</p>{features.has("photo_details") && <button type="button" disabled={busy} className="min-h-11 w-full text-xs font-semibold underline" onClick={()=>{setSelectedPhoto(photo);setCaption(photo.caption || "");setConfirmDelete(false);setError("");}}>Edit</button>}</div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[#8a8a8a]">No photos captured yet.</p>
        )}
      </div>
      {error && (
        <p role="alert" className="mt-2 text-sm text-rose-700">
          {error}
        </p>
      )}
      {failedUploads.length > 0 && (
        <div className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 p-2 text-sm text-rose-900">
          <span>{failedUploads.length} failed photo{failedUploads.length === 1 ? "" : "s"} ready to retry.</span>
          <button
            type="button"
            disabled={busy || uploadingCount > 0}
            onClick={() => void uploadFiles(failedUploads.map(({ file }) => file))}
            className="min-h-11 shrink-0 rounded-full border border-rose-300 bg-white px-3 font-semibold disabled:opacity-60"
          >
            Retry failed {failedUploads.length === 1 ? "photo" : "photos"}
          </button>
        </div>
      )}
      {uploadingCount > 0 && (
        <p className="mt-2 text-sm text-[#4d4d4d]" aria-live="polite">
          Uploading {uploadingCount} photo{uploadingCount === 1 ? "" : "s"}…
        </p>
      )}
      <div className="mt-3 flex gap-2">
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(event) => {
            const files = Array.from(event.target.files ?? []);
            event.currentTarget.value = "";
            void uploadFiles(files);
          }}
        />
        <input
          ref={libraryRef}
          type="file"
          accept="image/*"
          multiple
          aria-label="Choose photos from library"
          className="hidden"
          onChange={(event) => {
            const files = Array.from(event.target.files ?? []);
            event.currentTarget.value = "";
            void uploadFiles(files);
          }}
        />
        <button
          type="button"
          disabled={busy}
          onClick={() => cameraRef.current?.click()}
          className="min-h-11 flex-1 rounded-full bg-[#ececec] text-sm font-semibold disabled:opacity-60"
        >
          Capture
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => libraryRef.current?.click()}
          className="min-h-11 flex-1 rounded-full bg-[#ececec] text-sm font-semibold disabled:opacity-60"
        >
          Add photos
        </button>
      </div>
    </section>
  );
}

function FieldPhotoThumb({
  photo,
  actingQuery,
}: {
  photo: JobPhoto;
  actingQuery: string;
}) {
  const { imageUrl, error: photoError, retry, onImageError } = usePhotoImage(photo.contentUrl ? `${photo.contentUrl}${actingQuery}` : null);
  const uploadedAt = photo.createdAt && Number.isFinite(Date.parse(photo.createdAt)) ? new Date(photo.createdAt) : null;
  return (
    <figure className="overflow-hidden rounded-xl bg-[#e8e8e8]">
      {imageUrl ? (
        <img
          src={imageUrl}
          onError={onImageError}
          alt={photo.caption || photo.fileName || "Job photo"}
          className="aspect-square w-full object-cover"
        />
      ) : (
        <span className="grid aspect-square place-items-center text-center text-[10px] text-[#8a8a8a]">
          {photoError || (photo.contentUrl ? "Loading" : <>Not copied<br />to preview</>)}
        </span>
      )}
      {photoError && <button type="button" onClick={retry} className="p-2 text-xs font-semibold">Retry photo</button>}
      <figcaption className="bg-white px-1 py-2 text-[11px] text-[#6b6b6b]">{uploadedAt ? <>Uploaded <time dateTime={uploadedAt.toISOString()}>{uploadedAt.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" })}</time></> : "Upload date unavailable"}</figcaption>
    </figure>
  );
}

type FieldPOLine = VendorCosts & { key: string; priceBookItemId: string; name: string; description: string; quantity: string; unitCost: string };
const purchaseOrderNeedsDelivery = (type: string) => !['supply house run', 'permit', 'permits'].includes(type.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' '));
const blankPOLine = (): FieldPOLine => ({ key: crypto.randomUUID(), priceBookItemId: "", name: "", description: "", quantity: "1", unitCost: "" });
export function FieldPurchaseOrders({ jobId, actingQuery, purchaseOrders, busy, onCreated, invoiceId, preparation = false }: {
  jobId: string; actingQuery: string; purchaseOrders: NonNullable<Detail["purchaseOrders"]>; busy: string; onCreated: () => void; invoiceId?: string; preparation?: boolean;
}) {
  const [purchaseItems, setPurchaseItems] = useState<PurchaseMaterial[]>([]);
  const [pickerLine, setPickerLine] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(purchaseOrders.length === 0);
  const [savedOrders, setSavedOrders] = useState<typeof purchaseOrders>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const [notice, setNotice] = useState("");
  const formRef = useRef<HTMLFormElement>(null);
  const lock = useRef(false);
  const [lines, setLines] = useState<FieldPOLine[]>(() => [blankPOLine()]);
  const [requestType, setRequestType] = useState("supply_house_run");
  const [deliveryDate, setDeliveryDate] = useState("");
  const needsDeliveryDate = purchaseOrderNeedsDelivery(requestType);
  const [vendors, setVendors] = useState<Array<{ id: string; name: string }>>([]);
  const [orderTypes, setOrderTypes] = useState<string[]>([]);
  const [vendorId, setVendorId] = useState("");
  const [memo, setMemo] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const actingTechnicianId = new URLSearchParams(actingQuery).get("technicianId");
  const orders = Array.from(new Map([...purchaseOrders, ...savedOrders].map(order => [order.id, order])).values());
  useEffect(() => { setSavedOrders(current => current.filter(order => !purchaseOrders.some(item => item.id === order.id))); }, [purchaseOrders]);
  const changeLine = (key: string, patch: Partial<FieldPOLine>) => setLines(current => current.map(line => line.key === key ? { ...line, ...patch } : line));
  const changeVendor = (next: string) => {
    setVendorId(next);
    setLines(current => current.map(line => {
      const costs = line.vendorCosts ?? purchaseItems.find(item => item.id === line.priceBookItemId)?.vendorCosts;
      const cost = costs?.find(cost => cost.vendorId === next && cost.active !== false);
      return costs?.length ? { ...line, unitCost: cost == null ? "" : (cost.costCents / 100).toFixed(2) } : line;
    }));
  };
  useEffect(() => {
    const controller = new AbortController();
    void apiFetch(`/api/v1/workspace/field/jobs/${encodeURIComponent(jobId)}/purchase-orders${actingQuery}`, { signal: controller.signal }).then(async response => {
      const body = await response.json();
      if (!response.ok) throw new Error(body.error?.message || "Vendors could not be loaded.");
      if (controller.signal.aborted) return;
      const availableTypes = body.data.orderTypes ?? ["Supply house run", "Order"];
      setPurchaseItems(body.data.materials ?? []);
      setVendors(body.data.vendors ?? []); setOrderTypes(availableTypes); setRequestType(current => availableTypes.includes(current) ? current : availableTypes[0] ?? "");
      setSavedOrders(body.data.items ?? []);
      if (body.data.items?.length) setCreating(false);
    }).catch(cause => { if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : "Vendors could not be loaded."); });
    return () => controller.abort();
  }, [jobId, actingQuery]);
  function newOrder() {
    setEditingId(null); setLines([blankPOLine()]); setVendorId(""); setDeliveryDate(""); setMemo(""); setCreating(true); setNotice(""); setError("");
    requestAnimationFrame(() => { formRef.current?.scrollIntoView?.({ block: "start" }); formRef.current?.querySelector<HTMLInputElement>('[role="combobox"]')?.focus(); });
  }
  async function editOrder(id: string) {
    if (lock.current) return;
    lock.current = true; setOpeningId(id); setError("");
    try {
      const response = await apiFetch(`/api/v1/workspace/field/jobs/${encodeURIComponent(jobId)}/purchase-orders/${encodeURIComponent(id)}${actingQuery}`);
      const body = await response.json();
      if (!response.ok) throw new Error(body.error?.message || "Unable to load purchase order.");
      const item = body.data.item;
      setVendorId(item.vendorId); setRequestType(item.orderType || ""); setDeliveryDate(item.requiredByDate || ""); setMemo(item.vendorMemo || "");
      setLines(item.lines.map((line: {id:string;priceBookItemId:string;description:string;quantity:number;unitCostCents:number}) => {
        const [name, ...details] = line.description.split("\n");
        return { key: line.id, priceBookItemId: line.priceBookItemId || "", name, description: details.join("\n"), quantity: String(line.quantity), unitCost: (line.unitCostCents / 100).toFixed(2) };
      }));
      setEditingId(id); setCreating(true); setExpandedId(null); setNotice("");
      requestAnimationFrame(() => formRef.current?.scrollIntoView?.({ block: "start" }));
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Unable to load purchase order."); }
    finally { lock.current = false; setOpeningId(null); }
  }
  async function cancelOrder(id: string) {
    if (lock.current || !window.confirm("Cancel this purchase order request?")) return;
    lock.current = true; setSaving(true); setError("");
    try {
      const response = await apiFetch(`/api/v1/workspace/field/jobs/${encodeURIComponent(jobId)}/purchase-orders/${encodeURIComponent(id)}/cancel${actingQuery}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error?.message || "Unable to cancel purchase order.");
      setSavedOrders(current => [...current.filter(order => order.id !== id), {...orders.find(order => order.id === id)!, status: 'cancelled', canEdit: false}]);
      setNotice("Purchase order cancelled."); onCreated();
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Unable to cancel purchase order."); }
    finally { lock.current = false; setSaving(false); }
  }
  const submit = async (event: FormEvent) => {
    event.preventDefault(); if (lock.current || busy) return;
    const parsed = lines.map(line => ({ priceBookItemId: line.priceBookItemId, description: [line.name, line.description.trim()].filter(Boolean).join("\n"), quantity: Number(line.quantity), unitCostCents: Math.round(Number(line.unitCost) * 100) }));
    if (!vendorId) { setError("Search for and select a vendor."); return; }
    if (parsed.some((line, index) => !line.priceBookItemId || !lines[index].name || line.description.length > 1000 || !lines[index].quantity.trim() || !Number.isFinite(line.quantity) || line.quantity <= 0 || line.quantity > 100000 || !lines[index].unitCost.trim() || !Number.isFinite(line.unitCostCents) || line.unitCostCents < 0 || line.unitCostCents > 100000000)) { setError("Choose an item and enter a quantity and unit cost for each line."); return; }
    lock.current = true; setSaving(true); setError("");
    try {
      const suffix = editingId ? `/${encodeURIComponent(editingId)}` : "";
      const response = await apiFetch(`/api/v1/workspace/field/jobs/${encodeURIComponent(jobId)}/purchase-orders${suffix}${actingQuery}`, { method: editingId ? "PATCH" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ vendorId, orderType: requestType || undefined, vendorMemo: memo.trim() || null, requiredByDate: needsDeliveryDate ? deliveryDate || null : null, lines: parsed }) });
      const body = await response.json(); if (!response.ok) throw new Error(body.error?.message || "Purchase order could not be saved.");
      const id = body.data.id;
      if (!id) throw new Error("Could not confirm the saved purchase order. Refresh before trying again.");
      const order = { id, displayNumber: body.data.displayNumber ?? orders.find(order => order.id === id)?.displayNumber ?? null, status: body.data.status ?? 'submitted', amountCents: parsed.reduce((sum,line) => sum + Math.round(line.quantity * line.unitCostCents), 0), vendorName: vendors.find(vendor => vendor.id === vendorId)?.name ?? '', vendorMemo: memo.trim() || null, requiredByDate: needsDeliveryDate ? deliveryDate || null : null, orderType: requestType, createdBy: actingTechnicianId, canEdit: true };
      setSavedOrders(current => [order, ...current.filter(item => item.id !== id)]);
      setRevision(current => current + 1); setExpandedId(null); setLines([blankPOLine()]); setDeliveryDate(""); setMemo(""); setEditingId(null); setCreating(false);
      setNotice(editingId ? "Changes saved. Open the purchase order card to review or edit." : "Purchase order saved for office review. Open its card to review or edit."); onCreated();
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Purchase order could not be saved."); }
    finally { lock.current = false; setSaving(false); }
  };
  return <section className={card}>
    <p className="flex items-center gap-2 text-sm font-semibold"><CartIcon /> Purchase orders / materials</p>
    <div className="mt-3 space-y-3">
      {orders.map(order => <article key={order.id} aria-label={`Purchase order ${order.displayNumber || order.vendorName}`} className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 text-sm shadow-sm">
        <button type="button" aria-expanded={expandedId === order.id} disabled={saving || Boolean(busy) || Boolean(openingId)} onClick={() => setExpandedId(current => current === order.id ? null : order.id)} className="flex min-h-20 w-full items-center justify-between gap-4 p-4 text-left hover:bg-sky-50 focus-visible:outline-2 focus-visible:outline-sky-600">
          <span className="min-w-0"><span className="block font-semibold text-slate-950">{order.displayNumber || "Purchase order"} · {order.vendorName}</span><span className="mt-1 block text-xs text-slate-600">{order.orderType ? `${order.orderType} · ` : ""}{order.status === 'submitted' ? 'Awaiting office review' : order.status.replaceAll('_',' ')} · {money(order.amountCents)}</span><span className="mt-2 block text-xs font-semibold text-sky-800">{expandedId === order.id ? 'Hide details' : 'View purchase order'}</span></span>
          <ChevronDown size={20} aria-hidden="true" className={`shrink-0 text-sky-800 ${expandedId === order.id ? 'rotate-180' : ''}`} />
        </button>
        {expandedId === order.id && <div className="border-t border-slate-200 bg-white p-4">
          {order.requiredByDate && purchaseOrderNeedsDelivery(order.orderType ?? '') && <p className="text-xs text-slate-600">Delivery date: {order.requiredByDate}</p>}
          {order.vendorMemo && <div className="mt-3 rounded-lg bg-slate-50 p-3"><p className="text-xs font-semibold text-slate-600">Purchase order note for supplier</p><p className="mt-1 whitespace-pre-wrap">{order.vendorMemo}</p></div>}
          {order.status === "submitted" && (order.canEdit === true || (order.canEdit === undefined && actingTechnicianId && order.createdBy === actingTechnicianId)) && <div className="mt-3 flex flex-wrap gap-2"><button type="button" disabled={saving || Boolean(busy) || Boolean(openingId)} onClick={() => void editOrder(order.id)} className="min-h-11 rounded-full bg-sky-700 px-4 font-semibold text-white hover:bg-sky-800">{openingId === order.id ? 'Opening…' : 'Edit purchase order'}</button><button type="button" disabled={saving || Boolean(busy) || Boolean(openingId)} onClick={() => void cancelOrder(order.id)} className="min-h-11 rounded-full px-4 text-rose-700 hover:bg-rose-50">Cancel request</button></div>}
          {order.status !== "cancelled" && <FieldPurchaseOrderContents key={`${order.id}:${revision}`} jobId={jobId} orderId={order.id} actingQuery={actingQuery} />}
          {!preparation && order.status !== "cancelled" && <PurchaseOrderInvoiceItems jobId={jobId} orderId={order.id} fieldQuery={actingQuery} currentInvoiceId={invoiceId} onSaved={onCreated} />}
        </div>}
       </article>)}
    </div>
    {notice && <p role="status" className="mt-3 rounded-xl bg-emerald-50 p-3 text-sm text-emerald-900">{notice}</p>}
    {!creating && <button type="button" disabled={saving || Boolean(busy) || Boolean(openingId)} onClick={newOrder} className="mt-4 flex min-h-12 w-full items-center justify-center gap-2 rounded-xl border border-sky-200 bg-sky-50 px-4 text-sm font-semibold text-sky-900 hover:bg-sky-100"><Plus size={18} aria-hidden="true" />New purchase order</button>}
    {!!vendors.length && creating && <form ref={formRef} aria-label="New purchase order" onSubmit={event => void submit(event)} className="mt-4 space-y-5">
      <fieldset disabled={saving || Boolean(busy) || Boolean(openingId)} className="space-y-5">
        <div className="flex items-center justify-between gap-3"><h3 className="text-base font-semibold">{editingId ? 'Edit purchase order' : 'New purchase order'}</h3>{(orders.length > 0 || editingId) && <button type="button" onClick={() => {setCreating(false);setEditingId(null);setError('');}} className="min-h-11 px-3 text-sm font-semibold text-slate-600">{editingId ? 'Cancel editing' : 'Cancel'}</button>}</div>
        <label className="block text-sm font-semibold">Purchase order type<select aria-label="Purchase order request type" required value={requestType} onChange={event => {const type=event.target.value;setRequestType(type);if(!purchaseOrderNeedsDelivery(type))setDeliveryDate('');}} className="mt-1 min-h-12 w-full rounded-xl border border-slate-300 bg-white p-3"><option value="">Choose type</option>{orderTypes.map(type => <option key={type} value={type}>{type}</option>)}</select></label>
        <FieldVendorSearch vendors={vendors} value={vendorId} onChange={changeVendor} />
        {needsDeliveryDate && <label className="block text-sm font-semibold">Delivery date<input type="date" value={deliveryDate} onChange={event => setDeliveryDate(event.target.value)} className="mt-1 min-h-12 w-full rounded-xl border border-slate-300 bg-white p-3" /></label>}
        <div className="space-y-4">{lines.map((line, index) => <div key={line.key} className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center justify-between gap-3"><p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Item {index + 1}</p>{lines.length > 1 && <button type="button" aria-label={`Remove PO item ${index + 1}`} onClick={() => setLines(current => current.filter(item => item.key !== line.key))} className="min-h-11 px-2 text-sm text-rose-700">Remove</button>}</div>
          {line.name && <p className="break-words text-lg font-semibold leading-snug text-slate-950">{line.name}</p>}
          <button type="button" onClick={() => setPickerLine(line.key)} className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-sky-100 px-4 text-sm font-semibold text-sky-950 hover:bg-sky-200"><Search size={16} aria-hidden="true" />{line.name ? "Change item" : "Choose pricebook item"}</button>
          {line.priceBookItemId && <>
          <label className="block text-sm font-medium">Description<textarea aria-label={`PO item ${index + 1} description`} maxLength={Math.max(0, 999-line.name.length)} value={line.description} onChange={event => changeLine(line.key, {description:event.target.value})} rows={3} placeholder="Pricebook description or additional item details" className="mt-1 w-full rounded-xl border border-slate-300 bg-white p-3 text-base" /></label>
          <div className="grid grid-cols-2 gap-3"><label className="text-sm font-medium">Quantity<input required aria-label={`PO item ${index + 1} quantity`} type="number" min="0.01" max="100000" step="any" value={line.quantity} onChange={event => changeLine(line.key, {quantity:event.target.value})} className="mt-1 min-h-12 w-full rounded-xl border border-slate-300 bg-white p-3" /></label><label className="text-sm font-medium">Unit cost<input required aria-label={`PO item ${index + 1} unit cost`} type="number" min="0" max="1000000" step="0.01" value={line.unitCost} onChange={event => changeLine(line.key, {unitCost:event.target.value})} className="mt-1 min-h-12 w-full rounded-xl border border-slate-300 bg-white p-3" /></label></div>
          </>}
        </div>)}</div>
        <button type="button" disabled={lines.length >= 200} onClick={() => setLines(current => [...current, blankPOLine()])} className="flex min-h-12 w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-sky-300 bg-sky-50 px-4 text-sm font-semibold text-sky-900 hover:bg-sky-100"><Plus size={18} aria-hidden="true" />Add another item</button>
        <label className="block text-sm font-semibold">Purchase order note for supplier<textarea aria-label="Purchase order note for supplier" value={memo} maxLength={5000} onChange={event => setMemo(event.target.value)} rows={3} className="mt-1 w-full rounded-xl border border-slate-300 bg-white p-3 text-base" /></label>
        <div className="border-t border-slate-200 pt-4"><button type="submit" className="min-h-12 w-full rounded-xl bg-sky-700 px-4 text-sm font-semibold text-white shadow-sm hover:bg-sky-800">{saving ? "Saving…" : editingId ? "Save changes" : "Save purchase order"}</button></div>
      </fieldset>
    </form>}
     {pickerLine && <PurchaseOrderItemPicker categoryFirst vendorId={vendorId} endpoint={`/api/v1/workspace/field/jobs/${encodeURIComponent(jobId)}/purchase-orders${actingQuery}`} onClose={() => setPickerLine(null)} onAdd={item => changeLine(pickerLine, {vendorCosts:item.vendorCosts,priceBookItemId:item.id,name:item.name,description:estimateDescription(item.description).slice(0, Math.max(0,999-item.name.length)),unitCost:item.costCents == null ? "" : (item.costCents/100).toFixed(2)})} />}
    {error && <p role="alert" className="mt-3 text-sm text-rose-700">{error}</p>}
  </section>;
}

function EquipmentScan({
  compact = false,
  onComplete,
  jobId,
  appointmentId,
  actingTechnicianId,
  equipment,
  addRequest = 0,
  planConnections,
  linkedEquipmentIds = [],
	  equipmentTypes,
  disabled,
  onScan,
  onSaved,
}: {
  compact?: boolean;
  onComplete?:()=>void;
  jobId: string;
  appointmentId: string;
  actingTechnicianId: string;
  equipment: JobEquipment[];
  addRequest?: number;
  planConnections?: FieldEquipmentPlans | null;
  linkedEquipmentIds?: string[];
	  equipmentTypes: Array<{ id: string; name: string }>;
  disabled: boolean;
  onScan: (equipment: EquipmentEntry) => Promise<{ ok: boolean; message: string }>;
  onSaved: () => Promise<void>;
}) {
  const features=useFieldWorkflow();
  const cameraRef = useRef<HTMLInputElement>(null);
  const libraryRef = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<"start" | "manual" | "review">("start");
  const [nameplateFile, setNameplateFile] = useState<File | null>(null);
  const [equipmentId,setEquipmentId] = useState<string | undefined>();
  const [equipmentName,setEquipmentName] = useState("");
  const [installDate,setInstallDate] = useState("");
  const [saving,setSaving] = useState(false);
  const [serialNumber, setSerialNumber] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [modelNumber, setModelNumber] = useState("");
  const [manufactureYear, setManufactureYear] = useState("");
	  const [equipmentTypeId, setEquipmentTypeId] = useState("");
	  const [otherEquipmentType, setOtherEquipmentType] = useState("");
  const [condition, setCondition] = useState("");
  const [warranty, setWarranty] = useState("");
	const [scanNotes, setScanNotes] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState("");
  const actingQuery = `?appointmentId=${encodeURIComponent(appointmentId)}${actingTechnicianId ? `&technicianId=${encodeURIComponent(actingTechnicianId)}` : ""}`;

  const reset = () => {
		setMode("start"); setEquipmentId(undefined);setEquipmentName("");setInstallDate(""); setNameplateFile(null); setSerialNumber(""); setManufacturer(""); setModelNumber(""); setManufactureYear(""); setEquipmentTypeId(""); setOtherEquipmentType(""); setCondition(""); setWarranty(""); setScanNotes(""); setError("");
  };

  useEffect(()=>{if(addRequest>0){reset();setMode('manual');}},[addRequest]);
  const editEquipment = (item:JobEquipment) => {
    const matches = equipmentTypes.filter(type=>type.name===item.equipmentTypeName);
    setEquipmentId(item.id);setEquipmentName(item.name || '');setInstallDate(item.installDate?.slice(0,10) || '');
    setSerialNumber(item.serialNumber || '');setModelNumber(item.modelNumber || '');setManufacturer(item.manufacturer || '');
    setManufactureYear(item.manufactureYear ? String(item.manufactureYear) : '');
    setEquipmentTypeId(item.equipmentTypeId || (item.otherEquipmentType ? 'other' : matches.length===1 ? matches[0].id : ''));
    setOtherEquipmentType(item.otherEquipmentType || '');setCondition(item.condition || '');setNameplateFile(null);setMode('manual');setError('');
  };

  const analyze = async (file: File | undefined) => {
    if (!file) return;
    setAnalyzing(true); setError("");
    try {
      const prepared = await prepareFieldPhoto(file);
			console.info("nameplate_analysis_started", { contentType: prepared.type, bytes: prepared.size });
      const form = new FormData(); form.append("photo", prepared, prepared.name);
      const response = await apiFetch(`/api/v1/workspace/field/jobs/${encodeURIComponent(jobId)}/equipment/nameplate/analyze${actingQuery}`, { method: "POST", body: form });
      const payload = await response.json().catch(() => null);
			if (!response.ok) {
				console.warn("nameplate_analysis_rejected", { status: response.status, code: payload?.error?.code || "UNKNOWN" });
				throw new Error(payload?.error?.message || "We could not read that nameplate.");
			}
      const draft = payload.data.draft;
			console.info("nameplate_analysis_succeeded", { readability: draft.readability, hasManufacturer: Boolean(draft.manufacturer), hasModelNumber: Boolean(draft.modelNumber), hasSerialNumber: Boolean(draft.serialNumber), hasManufactureYear: Boolean(draft.manufactureYear) });
			setNameplateFile(prepared); setManufacturer(draft.manufacturer || ""); setModelNumber(draft.modelNumber || ""); setSerialNumber(draft.serialNumber || ""); setManufactureYear(draft.manufactureYear ? String(draft.manufactureYear) : ""); setWarranty(draft.warrantyStatus || "Verify manufacture date with manufacturer"); setScanNotes(draft.notes || ""); setMode("review");
		} catch (cause) { console.error("nameplate_analysis_failed", { errorName: cause instanceof Error ? cause.name : "UnknownError", errorMessage: cause instanceof Error ? cause.message : "Unknown error" }); setError(cause instanceof Error ? cause.message : "Nameplate analysis is unavailable. Enter it manually instead."); setMode("manual"); }
    finally { setAnalyzing(false); }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const widget=event.currentTarget.closest<HTMLDetailsElement>('details[data-job-tool]');
    if (!serialNumber.trim()) return setError("Enter a serial number.");
    if (!modelNumber.trim()) return setError("Enter a model number.");
		if (!equipmentTypeId) return setError("Choose an equipment type.");
		if (equipmentTypeId === "other" && !otherEquipmentType.trim()) return setError("Describe the equipment type.");
    if (!condition.trim()) return setError("Enter the equipment condition.");
    setError("");
		const entry = { ...(features.has("equipment_details") ? {equipmentId,name:equipmentName.trim() || null,installDate:installDate || null} : {}), serialNumber: serialNumber.trim(), modelNumber: modelNumber.trim(), manufacturer: manufacturer.trim(), manufactureYear: manufactureYear.trim() ? Number(manufactureYear) : null, equipmentTypeId: equipmentTypeId === "other" ? null : equipmentTypeId, otherEquipmentType: equipmentTypeId === "other" ? otherEquipmentType.trim() : null, condition: condition.trim() };
    if (manufactureYear.trim() && (!Number.isInteger(entry.manufactureYear) || entry.manufactureYear! < 1980 || entry.manufactureYear! > new Date().getFullYear())) return setError("Enter a valid manufacture year.");
    setSaving(true);
    try {
    let result: { ok: boolean; message: string };
    if (nameplateFile) {
      const form = new FormData(); form.append("photo", nameplateFile, nameplateFile.name); form.append("equipment", JSON.stringify(entry));
      const response = await apiFetch(`/api/v1/workspace/field/jobs/${encodeURIComponent(jobId)}/equipment/nameplate/confirm${actingQuery}`, { method: "POST", body: form });
      const payload = await response.json().catch(() => null);
      result = response.ok ? { ok: true, message: "" } : { ok: false, message: payload?.error?.message || "That equipment could not be saved." };
      if (result.ok) await onSaved();
    } else result = await onScan(entry);
    if (!result.ok) return setError(result.message);
    reset();
    if(widget){widget.open=false;widget.querySelector('summary')?.focus();}
    onComplete?.();
    } catch(cause) {setError(cause instanceof Error ? cause.message : "Equipment could not be saved.");} finally {setSaving(false);}
  };

  const recognizedManufacturer = ["navien", "carrier", "trane", "lennox", "york", "rheem", "ruud", "goodman", "amana", "bryant", "heil", "comfortmaker", "american standard", "bosch", "mitsubishi", "daikin", "payne"].some((brand) => manufacturer.toLowerCase().includes(brand));
  const quickLinks = recognizedManufacturer && modelNumber.trim() ? { warranty: `https://www.google.com/search?q=${encodeURIComponent(`${manufacturer} warranty lookup`)}`, manual: `https://www.manualslib.com/manuals.html?q=${encodeURIComponent(`${manufacturer} ${modelNumber}`)}` } : null;

  return (
    <section className={compact?'':card}>
      {!compact&&<><p className="flex items-center gap-2 text-sm font-semibold">
        <EquipmentIcon /> Equipment
      </p>
      <div className={`${well} mt-3`}>
        {equipment.length ? (
          equipment.map((item) => (
            <div key={item.id} className="py-1 text-sm">
              <button type="button" disabled={disabled || saving || !features.has('equipment_details')} onClick={()=>editEquipment(item)} className="min-h-11 text-left font-semibold underline decoration-slate-300 underline-offset-4 disabled:no-underline">{item.name || item.modelNumber || "Saved equipment"}</button>
              {!linkedEquipmentIds.includes(item.id)&&<p className="text-xs text-slate-600">Saved at this location</p>}
              {planConnections?.available ? <div className="my-1 flex flex-wrap gap-1">{planConnections.plans.filter(plan=>plan.equipmentIds.includes(item.id)).map(plan=><span key={plan.id} className={`rounded-full px-2 py-1 text-xs font-semibold ${plan.status==='pending_payment'?'bg-sky-50 text-sky-800':plan.status==='renewal'?'bg-amber-50 text-amber-800':'bg-emerald-50 text-emerald-800'}`}>Membership · {plan.name} · {plan.status==='pending_payment'?'Pending payment':plan.status==='renewal'?'Renewal due':'Active'}</span>)}{!planConnections.plans.some(plan=>plan.equipmentIds.includes(item.id))&&<span className="text-xs text-slate-600">Not linked to an active plan</span>}</div> : <p className="text-xs text-slate-500">Membership connections are unavailable right now.</p>}
              <p className="text-[#6b6b6b]">
                {item.modelNumber ? `${item.modelNumber} · ` : ""}
                {item.serialNumber}
                {item.manufactureYear ? ` · ${item.manufactureYear}` : ""}
				{item.equipmentTypeName || item.otherEquipmentType ? ` · ${item.equipmentTypeName || item.otherEquipmentType}` : ""}
                {item.condition ? ` · ${item.condition}` : ""}
              </p>
              <p className="text-xs">Installed {equipmentInstallDateLabel(item.installDate)}</p>
              {item.lastServicedAt && <p className="text-xs text-emerald-700">Serviced {new Date(item.lastServicedAt).toLocaleDateString()}</p>}
              {features.has("equipment_details") && <div className="flex flex-wrap gap-3"><button type="button" disabled={disabled || saving} className="min-h-11 text-sm font-semibold underline" onClick={() => editEquipment(item)}>Edit equipment</button>
              <button type="button" disabled={disabled || saving} className="min-h-11 text-sm font-semibold underline" onClick={async () => {setSaving(true);setError("");try{const response=await apiFetch(`/api/v1/workspace/field/jobs/${encodeURIComponent(jobId)}/equipment/${encodeURIComponent(item.id)}/serviced${actingQuery}`,{method:"POST"});const body=await response.json();if(!response.ok)throw new Error(body.error?.message || "Could not record service.");await onSaved();}catch(cause){setError(cause instanceof Error ? cause.message : "Could not record service.");}finally{setSaving(false);}}}>Mark serviced today</button></div>}
            </div>
          ))
        ) : (
          <p className="text-sm text-[#8a8a8a]">No equipment on file yet.</p>
        )}
      </div></>}
      {mode === "start" && <div className="mt-3 grid grid-cols-2 gap-2">
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(event) => { void analyze(event.target.files?.[0]); event.currentTarget.value = ""; }} />
        <input ref={libraryRef} type="file" accept="image/*" className="hidden" onChange={(event) => { void analyze(event.target.files?.[0]); event.currentTarget.value = ""; }} />
        <button type="button" disabled={disabled || analyzing} onClick={() => cameraRef.current?.click()} className="min-h-11 rounded-full bg-black text-sm font-semibold text-white disabled:opacity-60">{analyzing ? "Reading nameplate…" : compact ? "Scan equipment label" : "Scan nameplate"}</button>
        <button type="button" disabled={disabled || analyzing} onClick={() => setMode("manual")} className="min-h-11 rounded-full bg-[#ececec] text-sm font-semibold disabled:opacity-60">Enter manually</button>
        <button type="button" disabled={disabled || analyzing} onClick={() => libraryRef.current?.click()} className="col-span-2 text-sm font-semibold text-[#666] disabled:opacity-60">Choose nameplate from library</button>
      </div>}
      {mode !== "start" && <form aria-label="Equipment details" onSubmit={(event) => void submit(event)} className="mt-4 space-y-3">
        {mode==='review'&&<p className="text-sm font-semibold">Review nameplate details</p>}
        {features.has("equipment_details")&&<section aria-label="Equipment identification" className="space-y-4 rounded-[28px] bg-slate-50 p-3">
         <label className="block text-sm font-semibold text-slate-700">Equipment name<input aria-label="Equipment name" value={equipmentName} onChange={event=>setEquipmentName(event.target.value)} placeholder="e.g. Downstairs furnace" disabled={disabled||saving} className="mt-2 min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-base shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100 disabled:opacity-60"/></label>
         <EquipmentInstallDateField key={`${addRequest}:${equipmentId??'new'}`} value={installDate} onChange={setInstallDate} disabled={disabled||saving} plain/>
        </section>}
        <section aria-label="Equipment numbers" className="grid gap-3 rounded-[28px] bg-slate-50 p-3 sm:grid-cols-2">
         <label className="block text-sm font-semibold text-slate-700">Serial number<input value={serialNumber} onChange={event=>setSerialNumber(event.target.value)} placeholder="Serial number" disabled={disabled||saving} className="mt-2 min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-base shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100 disabled:opacity-60"/></label>
         <label className="block text-sm font-semibold text-slate-700">Model number<input value={modelNumber} onChange={event=>setModelNumber(event.target.value)} placeholder="Model number" disabled={disabled||saving} className="mt-2 min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-base shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100 disabled:opacity-60"/></label>
        </section>
        <section aria-label="Equipment classification" className="space-y-4 rounded-[28px] bg-slate-50 p-3">
         <EquipmentTypePicker key={`${addRequest}:${equipmentId??'new'}`} types={equipmentTypes} value={equipmentTypeId} onChange={id=>{setEquipmentTypeId(id);if(id!=='other')setOtherEquipmentType('');}} disabled={disabled||saving}/>
         {equipmentTypeId==='other'&&<label className="block text-sm font-semibold text-slate-700">Equipment type details<input value={otherEquipmentType} onChange={event=>setOtherEquipmentType(event.target.value)} placeholder="Describe equipment type" disabled={disabled||saving} className="mt-2 min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-base shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100 disabled:opacity-60"/></label>}
         <div className="space-y-2">
          <label className="block text-sm font-semibold text-slate-700">Condition<input value={condition} onChange={event=>setCondition(event.target.value)} placeholder="Condition" disabled={disabled||saving} className="mt-2 min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-base shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100 disabled:opacity-60"/></label>
          <div role="group" aria-label="Quick equipment condition" className="flex flex-wrap gap-2">{['Good','Fair','Needs attention'].map(value=><button key={value} type="button" aria-pressed={condition===value} disabled={disabled||saving} onClick={()=>setCondition(value)} className={`min-h-11 rounded-full border px-4 text-sm font-medium focus-visible:outline-2 focus-visible:outline-sky-700 disabled:opacity-50 ${condition===value?'border-sky-800 bg-sky-800 text-white':'border-slate-200 bg-white text-slate-700 hover:border-sky-300'}`}>{value}</button>)}</div>
         </div>
        </section>
        {error && (
          <p role="alert" className="text-sm text-rose-700">
            {error}
          </p>
        )}
				{scanNotes && <p className="rounded-xl bg-[#f4f4f4] px-3 py-2 text-sm text-[#444]">Scan note: {scanNotes}</p>}
        {warranty && <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900">{warranty}</p>}
        {quickLinks && <div className="flex gap-3 text-sm font-semibold"><a href={quickLinks.warranty} target="_blank" rel="noreferrer" className="underline">Warranty lookup</a><a href={quickLinks.manual} target="_blank" rel="noreferrer" className="underline">Find manual</a></div>}
        <button
          type="submit"
          disabled={disabled || saving}
          className="min-h-11 w-full rounded-full bg-[#ececec] text-sm font-semibold disabled:opacity-60"
        >
          Save equipment
        </button>
        <button type="button" onClick={reset} className="w-full text-sm font-semibold text-[#666]">Cancel</button>
      </form>}
      {error && mode === "start" && <p role="alert" className="mt-2 text-sm text-rose-700">{error}</p>}
    </section>
  );
}

export function CloseoutCard({
  invoiceActionsRef,
  completionAction,
  invoice, customerName,
  elapsedMs,
  canComplete,
  busy,
  completed,
  showPayment,
  amount,
  method,
  reference,
  setShowPayment,
  setAmount,
  setMethod,
  setReference,
  onSignature,
  onComplete,
  onPayment,
  onCardPayment,
}: {
  invoiceActionsRef?: (node: HTMLDivElement | null) => void;
  completionAction: { current: (() => Promise<void>) | null };
  customerName: string;
  invoice: Invoice | undefined;
  elapsedMs: number;
  canComplete: boolean;
  busy: string;
  completed: boolean;
  showPayment: boolean;
  amount: string;
  method: string;
  reference: string;
  setShowPayment: (value: boolean) => void;
  setAmount: (value: string) => void;
  setMethod: (value: string) => void;
  setReference: (value: string) => void;
  onSignature: (name: string, signature: string) => Promise<boolean>;
  onComplete: (
    value: "paid" | "due_later",
    finish: FinishPayload,
  ) => Promise<boolean>;
  onPayment: () => Promise<void>;
  onCardPayment: (amountCents: number) => Promise<void>;
}) {
  const laborHours = elapsedMs / 3_600_000;
  const [finishError, setFinishError] = useState("");
  const [pendingSignature, setPendingSignature] = useState<{
    name: string;
    dataUrl: string;
  } | null>(null);
	const [showCardPayment, setShowCardPayment] = useState(false);

  const signed = Boolean(invoice?.signedAt) || Boolean(pendingSignature);
  const paid = (invoice?.balanceDueCents ?? 0) <= 0;

  const submitComplete = async (paymentDisposition: "paid" | "due_later") => {
    if (!invoice) return;
    if(invoiceNeedsSummary(invoice)) {
      setFinishError('Add an Invoice summary that describes the work performed before completing this job.');
      document.querySelector<HTMLTextAreaElement>('[aria-label="Invoice summary"]')?.focus();
      return;
    }
    const parsedLaborHours = Number(laborHours);
    if (!Number.isFinite(parsedLaborHours) || parsedLaborHours < 0) {
      setFinishError("Job time is unavailable. Reopen the job and try again.");
      return;
    }
    setFinishError("");
    const ok = await onComplete(paymentDisposition, {
      laborHours: parsedLaborHours,
      notes: null,
      extraLineItems: [],
    });
    if (ok) {
      setPendingSignature(null);
    }
  };

  useEffect(() => {
    completionAction.current = async () => {
      if (busy || completed || !canComplete) return;
      await submitComplete(paid ? "paid" : "due_later");
    };
    return () => { completionAction.current = null; };
  });
  if (!invoice) return null;

  return (
    <section className={card}>
      <p className="mt-2 flex justify-between text-sm"><span>{paid && invoice.totalCents > 0 ? "Paid" : "Balance due"}</span><strong className={paid ? "text-emerald-800" : "text-amber-900"}>{money(paid && invoice.totalCents > 0 ? invoicePresentation(invoice).paidCents : invoice.balanceDueCents)}</strong></p>
      {!paid && invoice.status !== "void" && showPayment && (
        <div className="mt-4 space-y-2">
			{!signed && <SignaturePad customerName={customerName} disabled={Boolean(busy) || completed} onSave={async (name, signature) => { const saved = await onSignature(name, signature); if (saved) { setPendingSignature({ name, dataUrl: signature }); setFinishError(""); } return saved; }} />}
			{signed && <p className="rounded-2xl bg-[#e7f4ec] px-4 py-3 text-sm text-[#17683c]"><strong>Payment signature captured</strong></p>}
          <input
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            className="min-h-12 w-full rounded-xl border border-[#e2e2e2] px-3 text-base"
            aria-label="Payment amount"
          />
          <select
            aria-label="Payment type"
            value={method}
            onChange={(event) => setMethod(event.target.value)}
            className="min-h-12 w-full rounded-xl border border-[#e2e2e2] bg-white px-3 text-base"
          >
            <option value="cash">Cash</option>
            <option value="check">Check</option>
            <option value="ach">ACH / bank transfer</option>
          </select>
          <input
            value={reference}
            onChange={(event) => setReference(event.target.value)}
            placeholder={method === "check" ? "Check number (required)" : "Reference (optional)"}
            className="min-h-12 w-full rounded-xl border border-[#e2e2e2] px-3 text-base"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowPayment(false)}
              className="min-h-12 flex-1 rounded-full border border-[#e2e2e2] text-sm font-semibold"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={Boolean(busy) || !signed}
              onClick={() => void onPayment()}
              className="min-h-12 flex-1 rounded-full bg-[#ececec] text-sm font-semibold"
            >
              Record payment
            </button>
          </div>
        </div>
      )}
		{!paid && invoice.status !== "void" && showCardPayment && <div className="mt-4 space-y-3">
			{!signed && <SignaturePad customerName={customerName} disabled={Boolean(busy) || completed} onSave={async (name, signature) => { const saved = await onSignature(name, signature); if (saved) { setPendingSignature({ name, dataUrl: signature }); setFinishError(""); } return saved; }} />}
			{signed && <p className="rounded-2xl bg-[#e7f4ec] px-4 py-3 text-sm text-[#17683c]"><strong>Payment signature captured</strong></p>}
			<div className="flex gap-2"><button type="button" onClick={() => setShowCardPayment(false)} className="min-h-12 flex-1 rounded-full border border-[#e2e2e2] text-sm font-semibold">Cancel</button><button type="button" disabled={Boolean(busy) || !signed} onClick={() => void onCardPayment(invoice.balanceDueCents)} className="min-h-12 flex-1 rounded-full bg-black text-sm font-semibold text-white disabled:opacity-50">Continue to card payment</button></div>
		</div>}
      {finishError && (
        <p role="alert" className="mt-3 text-sm text-rose-700">
          {finishError}
        </p>
      )}
      {!paid && invoice.status !== "void" && !showPayment && !showCardPayment && <div className="mt-4 grid gap-2">
        {!paid && (
          <button
            type="button"
            disabled={Boolean(busy)}
            onClick={() => { setShowPayment(false); setShowCardPayment(true); }}
            className="min-h-12 rounded-2xl bg-black text-sm font-semibold text-white disabled:opacity-50"
          >
            Pay with Credit Card
          </button>
        )}
        <button
          type="button"
          disabled={Boolean(busy) || paid}
          onClick={() => {
            setAmount((invoice.balanceDueCents / 100).toFixed(2));
            setShowPayment(true);
          }}
          className="min-h-12 rounded-2xl border border-[#e2e2e2] text-sm font-semibold disabled:opacity-50"
        >
          Pay Other
        </button>
      </div>}
      <div ref={invoiceActionsRef} className="mt-3" />
    </section>
  );
}

function opaqueSignatureJpeg(canvas: HTMLCanvasElement) {
  const copy = document.createElement("canvas");
  copy.width = canvas.width; copy.height = canvas.height;
  const context = copy.getContext("2d");
  if (!context) return canvas.toDataURL("image/jpeg", 0.92);
  context.fillStyle = "#fff"; context.fillRect(0, 0, copy.width, copy.height);
  context.drawImage(canvas, 0, 0);
  return copy.toDataURL("image/jpeg", 0.92);
}

export function SignaturePad({
  disabled,
  customerName,
  onSave,
}: {
  disabled: boolean;
  customerName: string;
  onSave: (name: string, signature: string) => Promise<boolean>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawn, setDrawn] = useState(false);
  const drawing = useRef(false);
  const coordinates = (event: PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) * canvas.width) / rect.width,
      y: ((event.clientY - rect.top) * canvas.height) / rect.height,
    };
  };
  const begin = (event: PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    event.preventDefault();
    drawing.current = true;
    canvas.setPointerCapture(event.pointerId);
    const point = coordinates(event);
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  };
  const draw = (event: PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current || disabled) return;
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    const point = coordinates(event);
    ctx.lineTo(point.x, point.y);
    ctx.strokeStyle = "#111";
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.stroke();
    setDrawn(true);
  };
  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    setDrawn(false);
  };
  return (
    <div>
      <p className="text-sm font-semibold">Customer signature</p>
      <canvas
        ref={canvasRef}
        width={600}
        height={180}
        onPointerDown={begin}
        onPointerMove={draw}
        onPointerUp={() => {
          drawing.current = false;
        }}
        onPointerCancel={() => {
          drawing.current = false;
        }}
        className="mt-3 h-36 w-full rounded-2xl border border-dashed border-[#d0d0d0] bg-[#f8f8f8]"
        style={{ touchAction: "none" }}
        aria-label="Customer signature pad"
      />
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={clear}
          className="min-h-11 flex-1 rounded-full border border-[#e2e2e2] text-sm font-semibold"
        >
          Clear
        </button>
        <button
          type="button"
          disabled={disabled || !customerName.trim() || !drawn}
          onClick={() => {
			const image = canvasRef.current ? opaqueSignatureJpeg(canvasRef.current) : undefined;
            if (image) void onSave(customerName.trim(), image);
          }}
          className="min-h-11 flex-1 rounded-full bg-[#ececec] text-sm font-semibold disabled:opacity-50"
        >
          Save signature
        </button>
      </div>
    </div>
  );
}

const FIELD_SUPPORT_MAILTO = "mailto:info@workviewpro.com";

function FieldAccountMenu({
  signedIn,
  onSignOut,
  onSignIn,
  viewAs,
}: {
  signedIn: boolean;
  onSignOut: () => void;
  onSignIn: () => void;
  viewAs?: {
    technicians: Technician[];
    viewedTech: string;
    onOpen: () => Promise<Technician[]>;
    onSelect: (technicianId: string) => void;
  };
}) {
  const [open, setOpen] = useState(false);
  const [viewAsOpen, setViewAsOpen] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);
  const installation = useFieldAppInstall();

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className="relative">
      <button
        type="button"
        className="grid h-11 w-11 place-items-center text-[#6b6b6b]"
        aria-label="Menu"
        aria-expanded={open}
        aria-controls="field-account-menu"
        onClick={() => setOpen((value) => !value)}
      >
        <MenuIcon />
      </button>
      {open ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-20 bg-black/20"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
          />
          <nav
            id="field-account-menu"
            aria-label="Account"
            className="absolute right-0 top-12 z-30 w-52 rounded-[22px] bg-white p-2 shadow-[0_8px_24px_rgba(0,0,0,0.12)]"
          >
            {viewAs ? (
              <button
                type="button"
                className="flex min-h-11 w-full items-center rounded-2xl px-3 text-left text-sm font-semibold"
                onClick={() => {
                  setOpen(false);
                  void viewAs.onOpen().catch(() => []).finally(() => setViewAsOpen(true));
                }}
              >
                View as...
              </button>
            ) : null}
            {!installation.installed ? <button
              type="button"
              className="flex min-h-11 w-full items-center rounded-2xl px-3 text-left text-sm font-semibold"
              onClick={() => { setOpen(false); setInstallOpen(true); }}
            >Add to Home Screen</button> : null}
            <a
              href={FIELD_SUPPORT_MAILTO}
              className="flex min-h-11 w-full items-center rounded-2xl px-3 text-left text-sm font-semibold"
              onClick={() => setOpen(false)}
            >
              Help & Support
            </a>
            {signedIn ? (
              <button
                type="button"
                className="flex min-h-11 w-full items-center rounded-2xl px-3 text-left text-sm font-semibold"
                onClick={() => {
                  setOpen(false);
                  onSignOut();
                }}
              >
                Sign out
              </button>
            ) : (
              <button
                type="button"
                className="flex min-h-11 w-full items-center rounded-2xl px-3 text-left text-sm font-semibold"
                onClick={() => {
                  setOpen(false);
                  onSignIn();
                }}
              >
                Sign in
              </button>
            )}
          </nav>
        </>
      ) : null}
      {installOpen && !installation.installed ? <FieldAppInstallDialog installation={installation} onClose={() => setInstallOpen(false)} /> : null}
      {viewAs && viewAsOpen ? (
        <ViewAsTechnicianModal
          technicians={viewAs.technicians}
          viewedTech={viewAs.viewedTech}
          onSelect={viewAs.onSelect}
          onClose={() => setViewAsOpen(false)}
        />
      ) : null}
    </div>
  );
}

function ViewAsTechnicianModal({
  technicians,
  viewedTech,
  onSelect,
  onClose,
}: {
  technicians: Technician[];
  viewedTech: string;
  onSelect: (technicianId: string) => void;
  onClose: () => void;
}) {
  const options = [
    { id: "", name: "My assignments" },
    ...technicians.map((tech) => ({ id: tech.id, name: tech.name })),
  ];

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close"
        onClick={onClose}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="view-as-title"
        className="relative z-10 w-full max-w-[360px] rounded-[22px] bg-white p-4 shadow-[0_8px_24px_rgba(0,0,0,0.12)]"
      >
        <div className="flex items-start justify-between gap-3">
          <h2 id="view-as-title" className="text-lg font-semibold">
            View as...
          </h2>
          <button
            type="button"
            className={iconBtn}
            aria-label="Close view as dialog"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </div>
        <p className="mt-2 text-xs font-normal text-[#6b6b6b]">
          You can run the selected technician’s assigned field workflow.
          Signature and closeout retain your administrator identity.
        </p>
        <div className="mt-3 max-h-[50vh] space-y-1 overflow-y-auto">
          {options.map((option) => {
            const selected = option.id === viewedTech;
            return (
              <button
                key={option.id || "own"}
                type="button"
                aria-pressed={selected}
                onClick={() => {
                  onSelect(option.id);
                  onClose();
                }}
                className={`flex min-h-12 w-full items-center justify-between rounded-2xl px-3 text-left text-sm font-semibold ${
                  selected ? "bg-[#f5c400]" : "bg-[#f3f3f3]"
                }`}
              >
                <span>{option.name}</span>
                {selected ? <CheckIcon /> : null}
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function MenuIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M6 6l12 12" />
      <path d="M18 6l-12 12" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12l5 5L20 7" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-2.6-6.3" />
      <path d="M21 4v6h-6" />
    </svg>
  );
}
function PinIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 22s7-7.2 7-12a7 7 0 1 0-14 0c0 4.8 7 12 7 12z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}
function ClockIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v6l4 2" />
    </svg>
  );
}
function HashIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M9 4 7 20M17 4l-2 16M5 9h15M4 15h15" />
    </svg>
  );
}
function PhotoIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
function CartIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="9" cy="20" r="1.5" />
      <circle cx="18" cy="20" r="1.5" />
      <path d="M3 4h2l2.2 11.2a2 2 0 0 0 2 1.6h8.6a2 2 0 0 0 2-1.5L21 8H7" />
    </svg>
  );
}
function EquipmentIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="8" width="18" height="12" rx="2" />
      <path d="M7 8V6a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2" />
    </svg>
  );
}
function NotesIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M8 4h10a2 2 0 0 1 2 2v14l-4-2-4 2-4-2-4 2V6a2 2 0 0 1 2-2h2" />
      <path d="M9 9h8M9 13h6" />
    </svg>
  );
}
